/**
 * Strings of the `/fleet` module (OneFleet, D-16): French first, English; Lingala falls back to French through the
 * i18next setup until the glossary review covers the fleet terms (`ln.ts`). Section labels live with the other
 * navigation labels (`nav.fleet.*` in `../i18n.ts`); everything the module's own screens say is here.
 */
export const fleetEn = {
  title: "Fleet",
  subtitle:
    "Vehicles, drivers, dispatch, maintenance, fuel and cost — OneFleet, plugged into the ERP",
  overview: {
    sections: "Fleet sections",
    register: "Fleet register",
    registerHint:
      "OneFleet's own: what the fleet did over the last thirty days, and the vehicles it did it with. The ERP holds the asset a vehicle is linked to; the vehicle itself lives here.",
    kpi: {
      completed: "Deliveries completed",
      since: "since {{day}}",
      onTime: "On time",
      l100: "L / 100 km",
      litres: "{{litres}} litres",
      costPerKm: "Cost per km",
      workOrders: "Open work orders",
      backlog: "{{count}} older than 30 days",
    },
    table: {
      vehicle: "Vehicle",
      type: "Type",
      status: "Status",
      odometer: "Odometer",
      erpAsset: "ERP asset",
      none: "No vehicle in this company yet",
      all: "All {{count}} vehicles",
    },
    open: "Open",
    comingSoon: "{{count}} screens planned",
  },
  screens: {
    dispatch: {
      board: "Board",
      orders: "Orders",
      runs: "Runs",
      trips: "Trips",
      map: "Live map",
      rates: "Service rates",
      serviceAreas: "Service areas and zones",
      quotes: "Quotes",
    },
    vehicles: {
      list: "Vehicles",
      fleets: "Fleets",
      assets: "Assets and equipment",
      warranties: "Warranties",
      telematics: "Telematics devices",
    },
    drivers: {
      list: "Drivers",
      schedule: "Schedule",
      templates: "Shift templates",
      availability: "Availability",
      timeOff: "Time off",
      scorecards: "Scorecards",
    },
    maintenance: {
      workOrders: "Work orders",
      schedules: "Schedules",
      inspections: "Inspections",
      issues: "Issues",
      parts: "Parts inventory",
    },
    fuel: {
      reports: "Fuel reports",
      transactions: "Transactions",
      matching: "Matching",
      anomalies: "Anomalies",
      allocation: "Cost allocation",
    },
    partners: {
      customers: "Customers",
      contacts: "Contacts",
      vendors: "Vendors",
      places: "Places",
    },
    analytics: { dashboards: "Dashboards", reports: "Reports" },
    erp: {
      status: "Status",
      connections: "Connections",
      capabilities: "Capabilities",
      sync: "Sync",
      reconcile: "Reconcile",
      mappings: "Mappings",
      deadLetter: "Dead letter",
    },
    copilot: {
      proposals: "Proposals",
      sessions: "Sessions",
      tasks: "Tasks",
      policies: "Policies",
      alerts: "Alerts",
    },
    settings: {
      general: "General",
      orderTypes: "Order types and workflows",
      customFields: "Custom fields",
      driverApp: "Driver app",
      notifications: "Notifications",
      documents: "Printed documents",
      branding: "Branding",
      ai: "AI",
      integrations: "Integrations",
      security: "Security",
      developer: "Developer",
    },
  },
  placeholder: {
    title: "Planned screen",
    body: "This screen is delivered by OneFleet {{wp}}. Its wireframe and acceptance steps are in the OneFleet UI design and UAT scripts.",
    section: "{{count}} screens in this section",
  },
  status: {
    title: "OneFleet services",
    hint: "Reached through OneSphere's edge as /fleet-api and /fleet-rt; the AI API answers on /fleet-ai from WP-A1",
    api: "API",
    realtime: "Realtime",
    postgres: "Postgres",
    valkey: "Valkey",
    checking: "Checking…",
    up: "Up",
    ready: "Ready",
    degraded: "Degraded",
    down: "Not reachable",
    version: "Version {{version}} · {{sha}}",
    socket: {
      idle: "Idle",
      connecting: "Connecting…",
      connected: "Connected",
      auth_required: "Connected — authentication arrives with WP-B2",
      disconnected: "Disconnected",
      error: "Not reachable",
    },
    mock: "Mock mode: the answers come from the browser, not from the OneFleet stack",
    identity: "Identity",
    identityError: "OneFleet did not accept the session token",
    identityNoCompany:
      "Signed in, but no company scope: ask an administrator for a /Scope/Company group",
    permissions: "permissions",
    worker: "Worker",
    workerSeen: "Heartbeat {{age}} s ago",
    workerStale: "Last heartbeat {{age}} s ago — stale",
    workerNone: "No heartbeat",
    workerRelay: "outbox relay running",
    logs: "Logs",
    restart: "Restart",
    logsOf: "Last lines of {{service}}",
    logsNone:
      "Nothing shipped yet — the service writes its tail every couple of seconds.",
    restartConfirm:
      "Restart the {{service}} service? It exits and the container policy brings it back within seconds.",
    restartRequested:
      "Restart of {{service}} requested — the light turns red, then green again.",
    ai: "AI API",
    aiGateway: "Gateway up · {{on}}/{{total}} tracks keyed",
    aiGatewayDown: "AI API up, gateway not reachable",
  },
  vehicles: {
    search: "Search",
    searchHint: "plate, VIN, make, model",
    filterStatus: "Status",
    allStatuses: "All statuses",
    total: "{{count}} vehicles",
    plate: "Plate",
    vehicle: "Vehicle",
    type: "Type",
    statusLabel: "Status",
    odometer: "Odometer",
    costCenter: "Cost centre",
    empty:
      "No vehicle matches — the register fills from OneFleet (just seed) or from ERPNext Assets (WP-B7)",
    next: "Next page",
    first: "First page",
    status: {
      active: "Active",
      maintenance: "In maintenance",
      inactive: "Inactive",
      retired: "Retired",
      needs_review: "Needs review",
    },
    types: {
      truck: "Truck",
      van: "Van",
      car: "Car",
      pickup: "Pickup",
      bus: "Bus",
      motorcycle: "Motorcycle",
      trailer: "Trailer",
      forklift: "Forklift",
      other: "Other",
    },
    fuel: {
      diesel: "Diesel",
      petrol: "Petrol",
      electric: "Electric",
      hybrid: "Hybrid",
      lpg: "LPG",
    },
    erpAsset: "ERP asset",
    new: "New vehicle",
    pull: "Pull ERP assets",
    pulling: "Pulling…",
    pullHint:
      "Reads the Assets and Vehicles of {{name}} and links them by OneFleet id, VIN, then plate; unknown ones arrive as 'needs review'. Nothing is written to the ERP.",
    form: {
      newTitle: "New vehicle",
      editTitle: "Edit {{plate}}",
      back: "Back",
      locked:
        "Creating and editing vehicles takes the fleet manager or administrator role.",
      identity: "Identity",
      vin: "VIN",
      internalId: "Internal id",
      make: "Make",
      model: "Model",
      year: "Year",
      displayName: "Display name",
      fuel: "Fuel",
      usage: "Usage and accounting",
      capacityWeight: "Capacity (kg)",
      capacityVolume: "Capacity (l)",
      project: "Project",
      odometerHint:
        "The odometer only moves forward: a lower value is kept as it is (telematics and fuel logs feed it too).",
      erpTitle: "ERP",
      erpOn:
        "Connected to {{name}}: saving files a proposal to mirror the vehicle as an ERPNext Vehicle. A person approves it on Vehicle 360 or the ERP Hub — nothing is written before that.",
      erpOff:
        "No active ERP connection: the vehicle stays OneFleet-only until one is provisioned from Administration › Add-ons.",
      saving: "Saving…",
      save: "Save",
      create: "Create vehicle",
      cancel: "Cancel",
    },
  },
  erp: {
    title: "ERP Hub",
    hint: "Two ERP tracks per company — ERPNext and Oracle Fusion — behind one port; the modules switch on per company from here",
    tracks: "ERP tracks",
    erpnext: "ERPNext — OneSphere's site (erp.localhost)",
    fusion: "Oracle Fusion — WireMock double until a project pod is named",
    provisioned:
      "Connections are provisioned from OneSphere Administration › Add-ons and listed under Connections",
    openAdmin: "Open Administration › Add-ons",
    conn: {
      title: "ERP connections",
      hint: "One connection per ERP; reads are free, every write to the ERP is a proposal a person approves",
      none: "No connection yet — provision one from Administration › Add-ons",
      provisionedFrom: "provisioned from {{from}}",
      healthy: "Healthy",
      unhealthy: "Unhealthy",
      webhooks: "{{count}} webhooks",
      noWebhooks: "No webhooks",
      poll: "polls every {{every}} min · last {{at}}",
      pollOff: "poller off",
      pollHint:
        "No webhooks on this system: the worker pulls what changed since the last run (LastUpdateDate).",
      pollEnable: "Switch the poller on",
      pollDisable: "Switch the poller off",
      lastHealth: "Last check {{at}}",
      test: "Test",
      links: "{{count}} links",
      inbound: "{{count}} inbound events",
      pending: "{{count}} proposals pending",
    },
    pull: {
      run: "Pull now",
      running: "Pulling…",
      assets: "Assets (vehicles)",
      vehicles: "Vehicles (HRMS)",
      drivers: "Employees → drivers",
      customers: "Customers",
      suppliers: "Suppliers",
      dimensions: "Cost centres, projects, sites",
    },
    proposals: {
      title: "Proposed ERP writes",
      count: "{{count}} pending",
      hint: "Nothing is written to the ERP without a decision here (or from the Proposals inbox)",
      empty: "No proposal waiting",
      by: "proposed by {{who}}",
      dualControl: "dual control",
      approve: "Approve and write",
      reject: "Reject",
    },
    form: {
      name: "Connection name",
      erpCompany: "ERP company (the /Scope/Company group)",
      baseUrl: "ERPNext URL (as OneFleet's services reach it)",
      site: "Site / Host header",
      apiKey: "Integration user API key",
      apiSecret: "Integration user API secret",
      registerWebhooks:
        "Register the OneFleet webhooks on the ERP site now (an ERP write — the bridge creates the Webhook documents)",
      submit: "Provision the connection",
      done: "Connection {{id}} provisioned — see the ERP Hub",
      hint: "Credentials are sealed on arrival and never returned; the connection test, capabilities and dimension pull run immediately (reads)",
      system: "ERP track",
      systems: { erpnext: "ERPNext (this OneSphere)", fusion: "Oracle Fusion" },
      fusion: {
        podUrl: "Fusion pod URL (https://<pod>.fa.<region>.oraclecloud.com)",
        username: "Integration user",
        password: "Password",
        businessUnit: "Business unit",
        faBook: "Fixed-assets book",
        costCenterSet: "Cost-centre value set code",
        vehicleItem: "Vehicle item",
        transactionSource: "Transaction source",
        siteDefault: "Default site / location",
        driverJobs: "Driver job names (comma-separated)",
        hint: "The pod's REST API is read at connect (company, business units, books — reads only); every write to a Fusion pod stays a proposal a person approves on the named pod (D-07). Production pods are entered here, never in an .env file.",
      },
    },
  },
  orders: {
    search: "Search",
    searchHint: "internal id, tracking number, notes",
    filterStatus: "Status",
    allStatuses: "All statuses",
    summary: "{{total}} orders · {{active}} active · {{unassigned}} unassigned",
    new: "New order",
    order: "Order",
    route: "Route",
    statusLabel: "Status",
    driver: "Driver",
    scheduled: "Scheduled",
    created: "created",
    distance: "Distance",
    billingLabel: "Billing",
    empty:
      "No order matches — create one, or run just seed for the DLG sample orders",
    unassigned: "Unassigned",
    priority: "priority {{n}}",
    stops: "{{count}} stops",
    tracking: "Tracking",
    actions: "Actions",
    assignDriver: "Assign a driver",
    assignVehicle: "Vehicle",
    driversVehicle: "The driver's own — {{plate}}",
    noDriversVehicle: "— the driver has no vehicle —",
    suggestDriver: "Suggest a driver",
    directions: "Directions",
    directionsCount: "{{count}} turns · {{distance}} · {{time}}",
    directionsNone:
      "No turn-by-turn steps for this route (straight-line provider) — switch the map provider to OSRM or Google.",
    openGoogleMaps: "Open in Google Maps",
    directionsHint:
      "Computed by {{provider}} when the order was routed; the driver app reads the same steps from /v1/orders/:id/directions.",
    turns: {
      depart: "Head out on {{road}}",
      arrive: "Arrive at the stop",
      roundabout: "At the roundabout take exit {{exit}} onto {{road}}",
      merge: "Merge onto {{road}}",
      fork: "Keep on {{road}}",
      continue: "Continue on {{road}}",
      turn: {
        left: "Turn left onto {{road}}",
        right: "Turn right onto {{road}}",
        "slight left": "Bear left onto {{road}}",
        "slight right": "Bear right onto {{road}}",
        "sharp left": "Turn sharp left onto {{road}}",
        "sharp right": "Turn sharp right onto {{road}}",
        uturn: "Make a U-turn onto {{road}}",
      },
    },
    suggesting: "Ranking…",
    suggestSource: "rule",
    suggestHint:
      "ranked by availability, distance to the pickup, workload and vehicle — assigning stays your click",
    noCandidates: "No driver available.",
    assignAndDispatch: "Assign and dispatch",
    reassign: "Reassign",
    quickAssign: "Assign",
    noEvents: "No event can be fired from this state",
    eventNote: "Note for the next event (optional)",
    waypoints: "Waypoints",
    eta: "ETA",
    payload: "Payload",
    noEntities: "No entity on the payload",
    entity: "Item",
    qty: "Qty",
    weight: "kg",
    item: "ERP item",
    price: "Price",
    orderPrice: "Order price",
    costCenter: "Cost centre",
    project: "Project",
    site: "Site",
    pod: "Proof of delivery",
    podNone: "Not required",
    cod: "Cash on delivery",
    activity: "Activity",
    internal: "internal",
    erp: "ERP documents",
    noErpDocs:
      "No ERP document yet — completing the order proposes the Delivery Note and the Sales Invoice draft",
    erpHint:
      "ERP writes are proposals a person approves in Copilot › Proposals",
    openProposals: "Open the proposals",
    openErp: "Open in ERPNext",
    open: "Open the order",
    notes: "Notes",
    straightLine: "straight line",
    status: {
      created: "Created",
      dispatched: "Dispatched",
      started: "Started",
      picked_up: "Picked up",
      en_route: "En route",
      completed: "Completed",
      canceled: "Canceled",
    },
    billing: {
      unbilled: "Unbilled",
      draft: "Draft",
      invoiced: "Invoiced",
      paid: "Paid",
    },
    events: {
      dispatch: "Dispatch",
      unassign: "Unassign",
      start: "Start",
      pickup: "Picked up",
      next: "Next stop",
      complete: "Complete",
      cancel: "Cancel",
    },
    kind: {
      pickup: "Pickup",
      dropoff: "Drop-off",
      stop: "Stop",
      return: "Return",
    },
    wpStatus: {
      pending: "Pending",
      current: "Current",
      arrived: "Arrived",
      completed: "Completed",
      skipped: "Skipped",
    },
    proof: {
      signature: "Signature",
      photo: "Photo",
      qr: "QR scan",
      note: "Note",
      any: "Any",
    },
    proofs: "Proofs",
    proofUnavailable: "unavailable",
    placeType: {
      depot: "Depot",
      customer_site: "Customer site",
      warehouse: "Warehouse",
      fuel_station: "Fuel station",
      workshop: "Workshop",
      port: "Port",
      other: "Place",
    },
    form: {
      type: "Order type",
      internalId: "Internal id",
      customer: "Customer",
      customerHint: "search the customers (ERP-linked contacts)",
      placeHint: "search a place or type an address",
      searching: "Searching…",
      noPlace: "No place or address found",
      address: "address",
      geocoded: "geocoded",
      change: "change",
      priority: "Priority (0–9)",
      driver: "Driver",
      assignLater: "Assign later",
      addEntity: "add an item",
      entityHint: "what is carried",
      remove: "remove",
      preview: "Route preview",
      previewHint:
        "Pick a pickup and a drop-off to preview the route through the tenant's map provider",
      time: "Time",
      provider: "Provider",
      checkDropoff: "a drop-off is set",
      checkCustomer: "an ERP-linked customer (needed for the Delivery Note)",
      checkItems: "entities carry an ERP item (or the profile's service item)",
      checkDriver: "a driver is assigned now (optional)",
      create: "Create the order",
      creating: "Creating…",
      quote: {
        title: "Quote",
        hint: "Price the stops and the entities against the tenant's service rates; creating with a rate gives the order its purchase rate — the ERP invoice lines come from it.",
        ask: "Get a quote",
        asking: "Pricing…",
        none: "No rate applies to these stops — the order can still be created without a price.",
        notApplicable: "not applicable",
        accept: "Create with this rate",
      },
      parseBadge: "rules + model draft · you confirm",
      parsedRule: "rule draft · you confirm",
      parsedAi: "ai + rule draft · you confirm",
      describe: "Describe the order",
      describeHint:
        "one sentence — or paste the customer's e-mail or WhatsApp message — FR / EN / LN; the copilot fills the form, you check and create",
      fromMessage: {
        email: "Read from an e-mail",
        whatsapp: "Read from a WhatsApp message",
        dropped:
          "{{count}} line(s) put aside (headers, quoted thread, signature)",
        sender: "the sender became the customer",
      },
      describePlaceholder:
        "Livrer 12 cartons de médicaments du Dépôt Limete à l'Hôpital Kintambo demain à 8h, urgent, signature",
      parse: "Parse",
      parsing: "Reading…",
      read: "Read",
    },
  },
  board: {
    counts:
      "{{active}} active · {{unassigned}} unassigned · {{vehicles}} vehicles on the map",
    selectHint:
      "Select an order in a lane or on the map to see its summary, assign a driver and fire the next event",
  },
  lanes: {
    view: { board: "Board", lanes: "Lanes" },
    day: "Day",
    prev: "Previous day",
    next: "Next day",
    today: "Today",
    counts:
      "{{orders}} orders on the day · {{drivers}} drivers · the latest 200 orders",
    empty: "No driver and no order on this day",
  },
  map: {
    onlineOnly: "Online only",
    zones: "Zones ({{count}})",
    counts: "{{vehicles}} vehicles · {{drivers}} drivers · {{online}} online",
    online: "Online",
    offline: "Offline",
    empty:
      "No position yet — the driver app, a GPS device or a manual entry places a vehicle",
    track: "Track: {{count}} points in the last 6 h",
    source: {
      device: "device",
      app: "driver app",
      manual: "manual",
      provider: "provider",
    },
  },
  erpCard: {
    title: "ERP",
    sync: {
      linked: "Linked",
      pending: "Pending",
      proposed: "Proposed",
      conflict: "Conflict",
      error: "Error",
      not_linked: "Not linked",
    },
    pending: "{{count}} to decide",
    lastSynced: "Last synced {{when}}",
    openHub: "ERP Hub",
    showRecord: "The record",
    hideRecord: "Hide the record",
    readAt: "Read from the ERP at {{when}}",
    noConnection:
      "No active ERP connection — provision one from Administration › Add-ons.",
    capabilityOff: "Switched off on this connection: {{capability}}",
    notLinkedHint:
      "Not linked to an ERP document yet — 'Pull ERP assets' links by OneFleet id, VIN or plate; a mirror is proposed when the record is created or changed.",
    proposedHint:
      "A proposal to mirror this record waits for a decision below.",
    money: {
      title: "Books (read back from the ERP)",
      cost: "Gross cost",
      nbv: "Net book value",
      accumulated: "Accumulated depreciation",
      method: "Method",
      usefulLife: "Useful life",
      months: "{{count}} months",
      purchased: "Purchased",
      costCenter: "Cost centre",
      assetStatus: "Asset status",
      insurer: "Insurer",
      policy: "Policy",
      insuredValue: "Insured value",
      insuranceWindow: "Cover",
      owner: "Owned by",
      leaseEnds: "Lease ends",
    },
    noMoney:
      "No asset books yet — they arrive with the Asset link (pull or webhook) and the nightly refresh.",
    asOf: "as of {{when}}",
    proposals: "Proposals",
    proposalsHint: "every ERP write is a proposal a person decides",
    noProposals: "No proposal about this record.",
    waitingDecision: "Waiting for finance / an administrator",
    approval: {
      pending: "Pending",
      approved: "Approved",
      executed: "Executed",
      rejected: "Rejected",
      failed: "Failed",
      expired: "Expired",
    },
    capabilities: {
      "assets.vehicle.write": "Mirror as ERP Vehicle",
      "links.write": "Set the OneFleet id on the ERP document",
      "assets.lifecycle.write": "Lifecycle action",
      "hr.driver.write": "Mirror as ERP Driver",
      "sales.delivery.write": "Delivery Note",
      "sales.invoice_draft.write": "Sales Invoice draft",
      "sales.trip.write": "Delivery Trip",
    },
    sandbox: "sandbox",
    healthy: "healthy",
    unhealthy: "unreachable",
  },
  asset360: {
    tabs: {
      label: "Asset sections",
      overview: "Overview",
      lifecycle: "Lifecycle & ERP",
      activity: "Activity",
    },
    assignment: "Assignment",
    assignedTo: "Mounted on",
    unassigned: "Not assigned",
    lastSeen: "Last position",
    noWarranty: "No warranty on this asset",
  },
  vehicle360: {
    back: "Vehicles",
    edit: "Edit",
    updated: "updated {{when}}",
    online: "Online",
    offline: "Offline",
    tabs: {
      label: "Vehicle",
      overview: "Overview",
      telematics: "Telematics",
      maintenance: "Maintenance",
      fuel: "Fuel",
      documents: "Documents",
      lifecycle: "Lifecycle & ERP",
      activity: "Activity",
    },
    documents: "Papers of the vehicle",
    documentsHint:
      "Insurance, technical inspection, emissions certificate, road tax, transport licence — what the vehicle must carry and renew.",
    documentsOwner: "OneFleet's own record",
    documentsOwnerBody:
      "No ERP models these papers (ERPNext's vehicle has a single «last carbon check» date), so OneFleet keeps them and the Compliance Monitor watches them.",
    spec: "Specification",
    engineHours: "Engine hours",
    usage: "Odometer and usage",
    lastPosition: "Last position",
    near: "Near",
    speed: "Speed",
    source: {
      device: "device",
      app: "driver app",
      manual: "manual",
      provider: "provider",
    },
    orders: "Orders",
    ordersActive: "{{count}} active",
    noOrders: "No order assigned to this vehicle yet.",
    noPosition:
      "No position yet — a tracker (D-21), the driver app or a manual entry on the live map puts it on the map.",
    openLifecycle: "Books, links and proposals",
    devices: "Trackers",
    deviceActive: "active",
    deviceInactive: "inactive",
    obd: "OBD-II",
    lastFix: "last fix",
    noDevices:
      "No tracker installed — positions come from the driver app or manual entries.",
    devicesHint:
      "Independent GPS / OBD-II devices report through the Traccar gateway (D-21, D-24); a device fix is the vehicle's position of record.",
    track24h: "Track (24 h)",
    trackPoints: "{{count}} recorded points",
    obdPanel: "Engine (OBD-II)",
    obdFields: {
      rpm: "RPM",
      coolant: "Coolant",
      coolantTemp: "Coolant",
      fuelLevel: "Fuel level",
      engineLoad: "Engine load",
      battery: "Battery",
      batteryLevel: "Battery",
      power: "Power",
      engineHours: "Engine hours",
      obdOdometer: "OBD odometer",
      vin: "VIN",
    },
    sensorHistory: "OBD history",
    sensorWindow: "last {{hours}} h",
    sensorPoints: "{{count}} readings",
    dtcs: "Fault codes:",
    dtcsIssue: "the maintenance issue",
    vinMismatch:
      "The device reports VIN {{reported}}, the vehicle's is {{vehicle}} — is the tracker in the right truck?",
    deviceEvents: "Device events",
    eventKinds: {
      no_signal: "No signal",
      signal_restored: "Signal back",
      dtc: "Fault codes",
      vin_mismatch: "VIN mismatch",
      provisioned: "Provisioned",
      provider: "Gateway",
    },
    eventOpen: "open",
    silentSince: "silent since {{when}}",
    noDeviceEvents: "No event on this tracker yet.",
    fuelUsual: "Usual consumption",
    fuelLitres: "Litres (last reports)",
    fuelSpend: "Spend",
    fuelReports: "Reports",
    fuelConsumption: "L/100 km",
    noFuel: "No fuel report for this vehicle yet.",
    fuelHint:
      "Consumption is computed between two full-tank reports; the usual is the rolling mean of the last six. Record fuel from the Fuel screen or the driver app.",
    lifecycle: "Lifecycle",
    lifecycleStates: {
      planned: "Planned",
      acquired: "Acquired",
      in_service: "In service",
      under_maintenance: "Under maintenance",
      out_of_service: "Out of service",
      for_disposal: "For disposal",
      disposed: "Disposed / retired",
    },
    lifecycleHint:
      "OneFleet keeps the operational lifecycle and its evidence; the ERP keeps the books — transfer, revalue and dispose are proposals it writes once approved.",
    ownership: "Who edits what",
    ownsOneFleet:
      "specification, odometer, status, telematics, documents, work",
    ownsErp:
      "cost, net book value, depreciation, transfers, disposal, stock, procurement",
    activity: "Activity",
    noActivity: "No event recorded for this vehicle yet.",
    events: {
      "vehicle.created": "Created",
      "vehicle.updated": "Updated",
      "vehicle.deleted": "Deleted",
      "vehicle.restored": "Restored",
      "vehicle.linked": "Linked to the ERP",
      "vehicle.online_changed": "Online state changed",
      "vehicle.location_changed": "Position",
      "geofence.entered": "Entered a zone",
      "geofence.exited": "Left a zone",
      "geofence.dwell": "Dwelling in a zone",
      "geofence.speeding": "Speeding in a zone",
    },
  },
  drivers: {
    search: "Search",
    searchHint: "name, phone, licence, internal id",
    filterStatus: "Status",
    filterLive: "Live state",
    allLive: "Any live state",
    total: "{{count}} drivers",
    expiring: "{{count}} licence(s) expiring or expired",
    new: "New driver",
    empty: "No driver matches.",
    driver: "Driver",
    liveState: "Live state",
    vehicle: "Vehicle",
    lastSeen: "Last seen",
    erpEmployee: "ERP Employee / Driver",
    status: { active: "Active", inactive: "Inactive", suspended: "Suspended" },
    live: {
      available: "Available",
      busy: "Busy",
      off_duty: "Off duty",
      unknown: "Unknown",
    },
    licence: {
      title: "Licence",
      expired: "expired {{days}} d ago",
      expiresIn: "expires in {{days}} d",
    },
    form: {
      newTitle: "New driver",
      editTitle: "Edit {{name}}",
      hint: "The phone number is the driver's app login (one-time code). With an ERP connection whose hr.driver.write rule is on, saving files a proposal to mirror the driver on the ERP — a person approves it (D-06).",
      create: "Create the driver",
      identity: "Identity",
      name: "Name",
      phone: "Phone (E.164)",
      email: "E-mail",
      internalId: "Internal id",
      city: "City",
      country: "Country (ISO-2)",
      skills: "Skills",
      skillsHint: "comma-separated: hazmat, reefer, long-haul …",
      licenceNumber: "Licence number",
      licenceClass: "Class",
      licenceExpires: "Expires on",
      state: "State",
      accounting: "Accounting",
    },
  },
  driver360: {
    back: "Drivers",
    back360: "Driver",
    tabs: {
      overview: "Overview",
      documents: "Documents",
      scorecard: "Scorecard",
      schedule: "Schedule",
      activity: "Activity",
    },
    profile: "Profile",
    hos: "Hours-of-service profile",
    hosDefault: "company default",
    orders: "Orders",
    noOrders: "No order assigned yet.",
    documents: "Documents and expiries",
    documentsHint:
      "The licence is kept on the driver record; the other papers (medical, hazmat permit, identity) get their store with WP-U16 — the alert thresholds already apply.",
    licence: "Driving licence",
    noExpiry: "no expiry recorded",
    expiry: {
      none: "No expiry date recorded — add one on the driver form.",
      expired:
        "Expired {{days}} day(s) ago — the driver should not be dispatched.",
      d7: "Expires within 7 days — renew now.",
      d14: "Expires within 14 days.",
      d30: "Expires within 30 days.",
      ok: "Valid.",
    },
    papers: {
      medical: "Medical certificate",
      hazmat: "Hazmat permit",
      identity: "Identity document",
    },
    papersLater: "not tracked yet",
    thresholds: "Alert thresholds",
    thresholdsBody:
      "Expiries are flagged at 30, 14 and 7 days and when past; the Compliance Monitor (WP-A9) turns them into alerts and the ERP Employee's termination deactivates the driver (erp_wins).",
    events: {
      "driver.created": "Driver created",
      "driver.updated": "Driver updated",
      "driver.linked": "Linked to the ERP",
      "driver.location_changed": "Position",
      "driver.online_changed": "Online state",
    },
  },
  fleets: {
    title: "Fleets",
    hint: "The company's fleets — groups of vehicles and drivers used for dispatch, service areas and reporting. Open one to see its members.",
    vehicles: "Vehicles ({{count}})",
    drivers: "Drivers ({{count}})",
    empty: "No fleet yet.",
    remove: "Remove from the fleet",
    add: { vehicles: "Add a vehicle…", drivers: "Add a driver…" },
    addButton: "Add",
  },
  assets: {
    title: "Assets and equipment",
    hint: "The non-vehicle assets (trailers, containers, generators …) with the ERP asset they are linked to, and the equipment mounted on vehicles or assets.",
    searchHint: "code, name, VIN, plate, serial",
    code: "Code",
    asset: "Asset",
    identification: "Identification",
    class: "Class",
    serial: "Serial number",
    warranties: "Warranties",
    empty: "No asset matches.",
    equipment: "Equipment",
    equipmentHint:
      "Mounted equipment (tail lifts, reefers, tanks …) with its serial number and the vehicle it is on.",
    equipmentEmpty: "No equipment recorded.",
    form: {
      new: "New equipment",
      newAsset: "New asset",
      edit: "Edit the asset",
      name: "Name",
      type: "Type",
      typeHint: "tail lift, reefer, tank…",
      serial: "Serial number",
      save: "Save the equipment",
      cancel: "Cancel",
    },
    types: {
      trailer: "Trailer",
      container: "Container",
      reefer: "Reefer",
      generator: "Generator",
      forklift: "Forklift",
      tank: "Tank",
      equipment: "Equipment",
      other: "Other",
    },
  },
  partners: {
    search: "Search",
    searchHint: "name, e-mail, phone, tax id",
    total: "{{count}} record(s)",
    allTypes: "All types",
    edit: "Edit",
    remove: "Remove",
    confirmRemove:
      "Remove {{name}}? The record is kept for the history; orders and documents that reference it keep working.",
    pullHint:
      "Backfill from {{name}} (reads only; existing records are linked by their ERP id)",
    pulling: "Pulling…",
    pull: { customers: "Pull ERP customers", suppliers: "Pull ERP suppliers" },
    erpOwnedHint:
      "The ERP masters this field once the record is linked; edit it there.",
    status: { active: "active", inactive: "inactive" },
    col: {
      name: "Name",
      customer: "Customer",
      reach: "Phone / e-mail",
      taxId: "Tax id",
      groupTerritory: "Group · territory",
      place: "Place",
      status: "Status",
      erpCustomer: "ERP customer",
      erpSupplier: "ERP supplier",
      usedBy: "Used by",
      type: "Type",
      address: "Address",
      owner: "Owner",
      coordinates: "Lat, lng",
    },
    customers: {
      title: "Customers",
      one: "Customer",
      hint: "The organisations OneFleet delivers for. Linked to the ERP Customer: name, tax id, group and territory come from the ERP (marked ERP); phone, places and notes stay here. A customer created here reaches the ERP only through a proposal a person approves.",
      new: "New customer",
      empty: "No customer yet — add one, or pull the ERP customers.",
      footer:
        "Badges: linked = the ERP Customer is known; proposed = a push waits for approval in the Proposals inbox; not linked = OneFleet-only by design.",
    },
    contacts: {
      title: "Contacts",
      hint: "The people at the customers — who to call at a site, who signs for a delivery. Contacts are OneFleet's; the ERP keeps the organisation.",
      new: "New contact",
      empty: "No contact person yet.",
    },
    usage: {
      vehicles: "vehicles",
      assets: "assets",
      warranties: "warranties",
      drivers: "drivers",
      fleets: "fleets",
      personnel: "people",
      fuel_providers: "fuel providers",
      work_orders: "work orders",
    },
    personnel: {
      title: "People at this vendor",
      hint: "Who to call — the vendor's contacts, kept in OneFleet (not pushed to the ERP).",
      name: "Name",
      role: "Role",
      add: "Add",
      remove: "Remove",
      empty: "Nobody yet.",
    },
    vendors: {
      title: "Vendors",
      one: "Vendor",
      hint: "Carriers, mechanics, parts and fuel suppliers, insurers — with their ERP Supplier and where each one is used (vehicles, work orders, fuel providers, warranties…). A new vendor proposes an ERP Supplier — an existing one with the same tax id, e-mail or name is linked, not duplicated.",
      new: "New vendor",
      empty: "No vendor yet — add one, or pull the ERP suppliers.",
      footer:
        "The legal and tax identity is the ERP Supplier's once linked; the type and how to reach them are OneFleet's.",
    },
    vendorTypes: {
      carrier: "carrier",
      mechanic: "mechanic",
      supplier: "supplier",
      fuel: "fuel",
      insurer: "insurer",
      other: "other",
    },
    places: {
      title: "Places",
      hint: "The depots, customer sites, warehouses, fuel stations, workshops and ports OneFleet routes to — geocoded here on the tenant's map provider; an ERP address is linked only when a document needs it.",
      searchHint: "name, address, city",
      new: "New place",
      empty: "No place yet — add the depot first.",
      mapHint: "{{count}} place(s) on the map — click a row or a marker.",
      ownCompany: "Own company",
      draft: "new place",
      addressHint: "street and number, or a landmark",
      city: "City",
      province: "Province",
      location: "Location",
      lookup: "Look up the address",
      looking: "Looking up…",
      noHits:
        "The geocoder found nothing for that address — type the coordinates.",
      lat: "Latitude",
      lng: "Longitude",
      needLocation:
        "A place needs coordinates: look up the address or type latitude / longitude.",
    },
    placeTypes: {
      depot: "depot",
      customer_site: "customer site",
      warehouse: "warehouse",
      fuel_station: "fuel station",
      workshop: "workshop",
      port: "port",
      other: "other",
    },
    form: {
      editTitle: "Edit {{name}}",
      create: "Create",
      identity: "Identity",
      title: "Title",
      titleHint: "site manager, receptionist…",
      customerHint:
        "A customer is an organisation OneFleet delivers for. With an ERP connection whose master.customer.write rule is on, creating one files a proposal to mirror it as an ERP Customer.",
      contactHint:
        "A contact person at a customer; the phone is what the driver app shows on the stop.",
      vendorHint:
        "A vendor OneFleet buys from or hands work to; with master.supplier.write on, creating one proposes an ERP Supplier.",
      placeHint:
        "A geocoded point on the tenant's map; the address is looked up on the configured provider or the coordinates typed.",
      erpFields: "ERP-mastered fields",
      erpFieldsHint:
        "Editable until the record is linked to an ERP Customer; the ERP owns them afterwards.",
      erpSupplierHint:
        "Editable until the record is linked to an ERP Supplier; the ERP owns it afterwards.",
      erpOwnedNote:
        "This record is linked to the ERP: its name and its tax fields are the ERP's — change them there; the phone, the place and the notes are yours.",
      group: "Customer group",
      territory: "Territory",
      website: "Website",
      notes: "Notes",
    },
  },
  warranties: {
    claims: {
      title: "Claims",
      hint: "What was claimed from the vendors under these warranties and what came back — filed and moved along on the work order's panel; an accepted amount nets the job's cost in the allocations.",
      status: "Status",
      all: "every status",
      none: "No claim yet.",
      totals: "Totals",
      col: {
        claim: "Claim",
        status: "Status",
        workOrder: "Work order",
        claimed: "Claimed",
        recovered: "Recovered",
        reference: "Reference",
      },
    },
    title: "Warranties",
    hint: "Every warranty on a vehicle, asset or equipment with its coverage window; expiring ones are flagged 30 days ahead.",
    name: "Warranty",
    subject: "Covers",
    window: "Window",
    state: "State",
    coverage: "Coverage",
    empty: "No warranty recorded.",
    subjects: { vehicle: "vehicle", asset: "asset", equipment: "equipment" },
    states: {
      expired: "expired",
      expiring: "expiring",
      running: "running",
      future: "not started",
      open: "open-ended",
    },
    form: {
      new: "New warranty",
      subject: "On",
      vendor: "Vendor",
      starts: "Starts on",
      ends: "Ends on",
      terms: "Terms",
      coverageHint: "km: 100000\nmonths: 24",
      save: "Save the warranty",
    },
  },
  telematics: {
    protocols: {
      title: "Where a tracker points",
      hint: "The protocols the gateway listens for. Give an installer the address of the row their device speaks; tick the ones this fleet uses and name the models you buy.",
      askAdmin: "ask the administrator",
      notPublished: "this deployment does not open that port yet",
      col: {
        protocol: "Protocol",
        endpoint: "Address for the device",
        models: "Models",
        used: "In use",
      },
    },
    title: "Telematics devices",
    hint: "The trackers installed on the vehicles — the gateway and the provider adapters feed their fixes into positions; a device's fix is the vehicle's position of record (D-21 / D-24).",
    device: "Device",
    provider: "Provider",
    installed: "Installed",
    lastFix: "Last fix",
    noFix: "no fix yet",
    empty: "No device installed.",
    newDevice: "New device",
    cancel: "Cancel",
    add: "Add the device",
    deviceId: "Device identity",
    deviceIdHint: "IMEI / the provider's unique id",
    name: "Name",
    model: "Model",
    obd: "OBD-II (engine data, fuel level, odometer, DTCs)",
    providers: {
      traccar: "Traccar (the gateway)",
      wialon: "Wialon feed",
      geotab: "Geotab feed",
      samsara: "Samsara feed",
    },
    events: {
      title: "Open device events",
      hint: "What the gateway and the health sweep raised across the fleet — a silent tracker, a fault code, a VIN mismatch; acknowledge what a person has seen.",
      empty: "No open device event.",
      acknowledge: "Acknowledge",
      acknowledged: "acknowledged {{when}}",
    },
  },
  maintenance: {
    title: "Work orders",
    hint: "Draft → Approved → In progress → Done → Costed (ERP). Completing a work order files the ERP repair document as a proposal; requesting parts files a Material Request — a person approves both (D-06).",
    newFromIssue: "Open a work order from an issue",
    status: {
      draft: "Draft",
      approved: "Approved",
      in_progress: "In progress",
      done: "Done",
      costed: "Costed (ERP)",
      cancelled: "Cancelled",
    },
    priority: { low: "low", normal: "normal", high: "high", urgent: "urgent" },
    kind: {
      preventive: "Preventive",
      corrective: "Corrective",
      inspection: "Inspection",
      recall: "Recall",
      other: "Other",
    },
    readiness: {
      ready: "parts ready",
      short: "parts short",
      requested: "parts requested",
      no_parts: "no parts",
    },
    due: {
      overdue: "Overdue",
      due_soon: "Due soon",
      ok: "OK",
      unknown: "Unknown",
    },
    summary: {
      open: "Open work orders",
      inProgress: "In progress",
      due: "Overdue / due soon",
      issues: "Open issues",
      partsBelow: "Parts below level",
    },
    findings: {
      title: "Advisor findings",
      hint: "Schedules due soon or overdue from the odometer and the calendar — draft the work order, the planned parts and checklist come along.",
      draft: "Draft work order",
      openWo: "open work order",
    },
    lines: {
      kind: "Kind",
      description: "Description",
      qty: "Qty",
      unit: "Unit",
      total: "Total",
      source: "Source",
      kinds: {
        part: "Part",
        labour: "Labour",
        service: "Service",
        external: "External",
      },
      sources: {
        stock: "from stock",
        material_request: "material request",
        internal: "internal",
        external: "external",
      },
    },
    claims: {
      title: "Warranty claims",
      summary: "claimed {{claimed}} · recovered {{recovered}}",
      file: "File a claim",
      warranty: "Warranty",
      amount: "Amount claimed ({{currency}})",
      create: "Create the claim",
      submit: "submit to the vendor",
      accept: "accepted",
      reject: "rejected",
      rejectedByVendor: "rejected by the vendor",
      acceptedAmount: "Amount accepted",
      reference: "vendor's reference",
      confirm: "Confirm",
      pay: "paid",
      withdraw: "withdraw",
      none: "No warranty covers this vehicle — nothing to claim.",
      status: {
        draft: "draft",
        submitted: "submitted",
        accepted: "accepted",
        rejected: "rejected",
        paid: "paid",
        cancelled: "withdrawn",
      },
    },
    detail: {
      lines: "Lines",
      coverage: "Warranty coverage",
      coverageAll: "All warranties",
      noCoverage: "No running warranty on this vehicle or its equipment.",
      coverageHint:
        "A repair under warranty is claimed from the vendor — say so on the lines and the ERP document.",
      short: "Short of stock",
      asOf: "stock as of {{when}}",
      requestParts: "Request parts (ERP) — a proposal",
      checklist: "Checklist",
      findings: "Findings",
      budget: "Budget",
      estimate: "Estimate",
      approved: "Approved",
      actual: "Actual (ERP)",
      scheduled: "scheduled {{when}}",
      completed: "completed {{when}}",
      doneHint:
        "Done — the ERP repair document is proposed in the inbox; once approved the ERP's cost lands here and the work order becomes Costed.",
    },
    actions: {
      approve: "Approve",
      start: "Start",
      complete: "Complete",
      cancel: "Cancel",
      odometer: "odometer at completion",
    },
    woForm: {
      new: "New work order",
      title: "New work order",
      name: "Title",
      kind: "Kind",
      priority: "Priority",
      scheduledAt: "Scheduled",
      description: "Description",
      part: "Part code",
      removeLine: "Remove",
      save: "Create the work order",
      cancel: "Cancel",
    },
    completeDialog: {
      title: "Complete {{number}}",
      hint: "What was actually done: the lines as used (the estimate's rows, adjusted), the findings, the odometer. The ERP repair document is proposed from these — a person approves it.",
      notes: "Notes",
      linesUsed: "Lines as used",
      addFinding: "Finding",
      findingText: "What was found",
      findingKind: "Finding kind",
      findingSeverity: "Finding severity",
      findingKinds: {
        observation: "Observation",
        defect: "Defect",
        wear: "Wear",
        recommendation: "Recommendation",
      },
    },
    schedules: {
      title: "Schedules",
      hint: "Preventive services every N km and / or N days from the last one; the state comes from the vehicle's odometer, the calendar and the distance trend.",
      name: "Schedule",
      interval: "Interval",
      lastDone: "Last done",
      nextDue: "Next due",
      state: "State",
      days: "days",
      parts: "{{count}} planned parts",
      empty: "No schedule yet.",
    },
    issues: {
      title: "Issues",
      hint: "What drivers, inspections and the console reported on the vehicles. An issue opens a work order and resolves when it completes.",
      report: "Report an issue",
      openWo: "Open a work order",
      resolve: "Resolve",
      close: "Close",
      empty: "No issue in this state.",
      reportedBy: "reported by {{who}}",
      filters: { active: "Active" },
      state: {
        open: "Open",
        in_review: "In review",
        resolved: "Resolved",
        closed: "Closed",
      },
      severity: {
        low: "Low",
        medium: "Medium",
        high: "High",
        critical: "Critical",
      },
      categories: {
        engine: "Engine",
        brakes: "Brakes",
        tyres: "Tyres",
        body: "Body",
        electrical: "Electrical",
        other: "Other",
      },
      form: {
        title: "What is wrong",
        vehicle: "Vehicle…",
        description: "Details (optional)",
        save: "Report",
      },
    },
    parts: {
      title: "Parts inventory",
      hint: "The catalogue comes from the ERP's Items; on hand is the ERP's per warehouse (as of the last read), reserved is what started work orders hold, projected = on hand − reserved. Stock is never edited here.",
      searchHint: "code, name",
      state: "State",
      refresh: "Refresh from ERP",
      new: "New part",
      refreshed: "{{n}} bins refreshed",
      alerts: "{{count}} part(s) below the reorder level:",
      alertsHint:
        "request them from a work order (a Material Request proposal).",
      part: "Part",
      warehouse: "Warehouse",
      onHand: "On hand (ERP)",
      reserved: "Reserved",
      projected: "Projected",
      ordered: "On order",
      reorder: "Reorder level",
      neededBy: "Needed by",
      erpReserved: "+ {{qty}} held by the ERP ({{count}} reservations)",
      states: {
        ok: "OK",
        at_reorder: "At reorder",
        below: "Below level",
        out: "Out of stock",
        ordered: "On order",
        unknown: "No reading",
      },
      asOf: "ERP stock as of {{when}}",
      empty:
        "No part yet — pull the ERP's Items from the ERP Hub (Reconcile › pulls: parts, stock).",
    },
  },
  inspections: {
    title: "Inspections",
    hint: "Checklists the fleet fills on a vehicle — from the console or the driver app — with typed items; a failed item opens one maintenance issue the Advisor drafts the work order from. A submitted inspection is a record.",
    fillNew: "Fill an inspection",
    newForm: "New form",
    cancel: "Cancel",
    forms: "Forms",
    noForms: "No form yet — build the first one.",
    formMeta: "{{items}} items · every {{freq}} days",
    driverVisibleShort: "in the driver app",
    fillThis: "Fill",
    activate: "Activate",
    deactivate: "Deactivate",
    due: "Due on the fleet (7 days)",
    noneDue: "Nothing due.",
    never: "never filled",
    overdueBy: "{{days}} d overdue",
    dueIn: "due in {{days}} d",
    all: "All",
    empty: "No inspection in this state.",
    draft: "draft",
    issue: "issue",
    issueOpened: "an issue was opened",
    lastSubmitted:
      "{{form}} on {{label}} submitted — {{failed}} failed item(s).",
    result: { pass: "Pass", fail: "Fail" },
    kinds: {
      check: "OK / fail",
      number: "Reading",
      text: "Text",
      photo: "Photo",
    },
    col: {
      when: "When",
      form: "Form",
      subject: "Vehicle",
      result: "Result",
      failed: "Failed items",
      by: "By",
    },
    builder: {
      name: "Form name",
      description: "Description (optional)",
      frequency: "Every N days",
      driverVisible: "Drivers can fill it",
      item: "Item",
      kind: "Kind",
      bounds: "Bounds",
      category: "Category",
      severity: "Severity",
      required: "Req.",
      unit: "unit",
      addItem: "Add an item",
      save: "Save the form",
      remove: "Remove",
    },
    fill: {
      form: "Form",
      vehicle: "Vehicle…",
      odometer: "Odometer (km)",
      ok: "OK",
      fail: "Fail",
      note: "What is wrong",
      notes: "Notes (optional)",
      photoUrl: "Photo URL",
      submit: "Submit the inspection",
      submitting: "Submitting…",
    },
  },
  lifecycle: {
    since: "since {{when}}",
    location: "location",
    custodian: "custodian",
    disposed: "Disposed by {{kind}} {{when}}.",
    disposalKinds: { scrap: "scrap", sale: "sale" },
    actions: "Lifecycle actions",
    actionsHint:
      "Operational steps apply at once and move the vehicle status with them; transfer, revalue and dispose change the books and are proposals — a second person approves them in the inbox.",
    actionLabels: {
      acquire: "Acquire",
      place_in_service: "Place in service",
      start_maintenance: "Start maintenance",
      end_maintenance: "End maintenance",
      take_out_of_service: "Take out of service",
      mark_for_disposal: "Mark for disposal",
      dispose: "Dispose",
      transfer: "Transfer",
      revalue: "Revalue",
    },
    proposal: "proposal",
    noActions: "No action from this state.",
    readOnly: "Your role reads the lifecycle; finance proposes the steps.",
    reason: "Reason (optional)",
    propose: "File the proposal",
    forms: {
      transfer: {
        title: "Transfer",
        hint: "→ Asset Movement in the ERP: a new location and / or custodian.",
        location: "Target location (ERP Location)",
        driver: "New custodian — a driver…",
        employee: "…or the ERP Employee id",
      },
      revalue: {
        title: "Revalue",
        hint: "→ Asset Value Adjustment on the profile's difference account.",
        value: "New value ({{currency}})",
        current: "Current net book value: {{value}}",
      },
      dispose: {
        title: "Dispose",
        hint: "→ scrap through the ERP, or a sale invoice on the asset item.",
        amount: "Sale amount ({{currency}})",
        customer: "ERP Customer",
      },
    },
    inInbox: "in the inbox",
    withdraw: "Withdraw",
    eventStates: {
      applied: "applied",
      proposed: "proposed",
      approved: "approved",
      rejected: "rejected",
      failed: "failed",
    },
    eventKinds: {
      depreciation_posted: "Depreciation posted",
      repair: "Repair recorded in the ERP",
    },
    timeline: "Timeline",
    noEvents:
      "No lifecycle event yet — the state is derived from the vehicle's status.",
    depreciation: "Books and depreciation (ERP)",
    refresh: "Refresh from ERP",
    books: {
      cost: "Cost",
      nbv: "Net book value",
      method: "Method",
      erpStatus: "ERP status",
    },
    months: "months",
    noBooks:
      "No ERP books yet — link the vehicle to its Asset (ERP Hub › Reconcile).",
    posted: "posted",
    scheduled: "scheduled",
    nextPosting: "next",
    endOfLife: "end of life",
    asOf: "read {{when}}",
    date: "Date",
    amount: "Amount",
    accumulated: "Accumulated",
    journal: "Journal entry",
    scheduledRow: "scheduled",
    showAll: "Show all {{count}} rows",
    showNext: "Show the next postings only",
    noSchedule: "No depreciation schedule read yet — Refresh from ERP.",
    tco: "TCO (12 months)",
    tcoTotal: "cost of ownership",
    tcoPerKm: "per km on {{km}} km",
    tcoFleet: "fleet {{value}}",
    buckets: {
      fuel: "Fuel",
      maintenance: "Maintenance",
      depreciation: "Depreciation",
      other: "Insurance / other",
    },
  },
  fuel: {
    summary: {
      waiting: "Waiting for a match",
      matched: "Matched / posted",
      litres: "Litres this month",
      spend: "Spend this month",
      anomalies: "Open anomalies",
      unmatched: "Unclaimed provider lines",
    },
    status: {
      draft: "Draft",
      submitted: "Submitted",
      matched: "Matched",
      posted: "Posted (ERP)",
      rejected: "Rejected",
    },
    payment: {
      card: "Fuel card",
      cash: "Cash",
      driver_paid: "Driver paid",
      account: "On account",
      other: "Other",
    },
    source: {
      driver_app: "driver app",
      console: "console",
      provider: "provider",
      import: "import",
    },
    txState: { unmatched: "Unclaimed", matched: "Matched", ignored: "Ignored" },
    batchState: {
      draft: "Draft",
      proposed: "Proposed",
      posted: "Posted (ERP)",
      cancelled: "Cancelled",
    },
    anomalyState: {
      open: "Open",
      dismissed: "Dismissed",
      confirmed: "Confirmed",
    },
    runState: {
      draft: "Draft",
      previewed: "Previewed",
      proposed: "Proposed",
      posted: "Posted (ERP)",
      rejected: "Rejected",
      superseded: "Superseded",
    },
    reportsObd: {
      obdOk: "gauge +{{rise}} %",
      obdHint:
        "OBD fuel level {{before}} % → {{after}} % around the fill (≈ {{expected}} % expected from the litres)",
    },
    rules: {
      over_capacity: "Over tank capacity",
      station_distance: "Vehicle elsewhere",
      consumption_outlier: "Consumption outlier",
      duplicate: "Duplicate",
      odometer_regression: "Odometer went back",
      off_hours: "Off hours",
      no_vehicle_movement: "No movement",
      obd_level_flat: "Gauge did not rise",
    },
    pools: {
      label: "Pool",
      fuel_matched: "Matched fuel",
      fuel_all: "All fuel",
      maintenance_actual: "Maintenance actuals (ERP)",
    },
    bases: {
      km_share_orders: "km share of the orders",
      hours_share_orders: "engine-hours share of the orders",
      days_share_orders: "days in service per target",
      order_cost_center: "the report's order",
      vehicle_cost_center: "the vehicle's cost centre",
      fixed_pct: "fixed percentages",
    },
    reports: {
      title: "Fuel reports",
      hint: "Every litre lands here — the driver's report, the console's, or the provider's statement line. Card-paid reports wait for the statement; cash and driver-paid ones carry their receipt. Posting to the ERP (Vehicle Log, Expense Claim) is a proposal in the inbox.",
      record: "Record fuel",
      state: "State",
      number: "Report",
      vehicle: "Vehicle",
      when: "When",
      litres: "Litres",
      amount: "Amount",
      payment: "Payment",
      anomalies: "Anomalies",
      erp: "ERP",
      score: "match {{score}} %",
      proposalPending: "proposal in the inbox",
      empty: "No fuel report in this state.",
    },
    form: {
      vehicle: "Vehicle…",
      driver: "Driver (optional)",
      odometer: "Odometer (km)",
      litres: "Litres",
      unit: "Price / L",
      amount: "Amount",
      card: "Card (optional)",
      station: "Station",
      receipt: "Receipt no.",
      fullTank: "Full tank",
      save: "Save",
    },
    actions: {
      submit: "Submit",
      reject: "Reject",
      rejectReason: "Why is this report rejected?",
    },
    forms: {
      provider: {
        new: "New provider",
        title: "New fuel provider",
        name: "Name",
        kind: "Kind",
        currency: "Currency",
        supplier: "ERP supplier",
        supplierHint: "the Supplier name in the ERP (optional)",
        delimiter: "CSV delimiter",
        datetime: "Date format",
        columns: "Statement columns",
        decimalComma: "Decimal comma in the amounts",
        save: "Create the provider",
        kinds: {
          card: "Fuel card issuer",
          station: "Station network",
          api: "Provider API",
          csv: "Statement file",
        },
        column: {
          external_id: "Reference",
          card: "Card",
          occurred_at: "Date",
          litres: "Litres",
          amount: "Amount",
          station: "Station",
        },
      },
      card: {
        new: "New card",
        title: "New fuel card",
        ref: "Card reference",
        label: "Label",
        save: "Create the card",
      },
      report: {
        edit: "Edit",
        erp: "ERP",
        title: "Edit {{number}}",
        receiptFile: "Receipt (photo / PDF)",
        receiptLinked: "A receipt file is linked to this report.",
        notes: "Notes",
        save: "Save the report",
      },
    },
    providers: {
      title: "Providers and cards",
      provider: "Provider",
      noSupplier: "no ERP supplier",
      lastImport: "last import {{when}}",
      neverImported: "no statement yet",
      unassigned: "no vehicle",
      empty:
        "No fuel provider yet — the seed adds a demo card provider; the Admin adds the real ones.",
      hint: "A provider's CSV columns are mapped in its config; a card assigned to a vehicle turns every statement line into a fuel report of that vehicle.",
    },
    feed: {
      none: "no feed — statements by hand",
      pullEvery: "feed pulled every {{every}} min",
      pushOnly: "the provider pushes (token set)",
      lastOk: "last {{when}} · {{created}} new, {{duplicates}} known",
      lastFailed: "last pull failed: {{error}}",
      syncNow: "Sync now",
      url: "Feed URL (the provider's export — CSV or JSON in its own columns, mapped in the config)",
      format: "Format",
      jsonPath: "JSON path to the rows",
      auth: "Authentication",
      authKind: {
        none: "none",
        bearer: "Bearer token",
        basic: "Basic (user:password)",
        header: "API-key header",
      },
      credential: "Credential (sealed, never shown again)",
      sinceParam: "Query parameter for “since the last sync”",
      everyMin: "Every (minutes)",
      save: "Save the feed",
      rotateToken: "Rotate the push token",
      tokenOnce: "Shown once —",
      hint: "The provider may push instead: POST /webhooks/fuel/{{id}} with X-OneFleet-Token; rows are deduplicated by their external id either way.",
    },
    import: {
      title: "Import a statement",
      placeholder:
        "ref;carte;date;qte;montant;station\nTX-1001;7041-****-4521;14/09/2026 08:37;117,50;470 000,00;TOTAL LIMETE",
      run: "Import",
      stats:
        "{{created}} new lines · {{matched}} matched a waiting report · {{reports}} reports created · {{duplicates}} already known · {{invalid}} invalid · {{unassigned}} without a vehicle",
      hint: "The same statement imported twice counts duplicates, never doubles. Lines without a vehicle wait in the matching queue until the card is assigned.",
    },
    transactions: {
      title: "Provider transactions",
      hint: "What the provider says happened, one row per statement line; matched lines point at their fuel report, the rest wait in the queue.",
      ref: "Reference",
      card: "Card · vehicle",
      station: "Station",
      report: "Fuel report",
      inBatch: "in a batch",
      empty: "No transaction in this state.",
    },
    batches: {
      title: "AP batches",
      hint: "The card transactions of a provider for a month, summed per vehicle → a Purchase Invoice draft proposed to the ERP (W3: a second person approves in the inbox).",
      billNo: "Supplier bill no.",
      build: "Build the month's batch",
      period: "Period",
      transactions: "Lines",
      lines: "Per vehicle",
      propose: "Propose the invoice",
      empty: "No batch yet.",
    },
    matching: {
      title: "Matching queue",
      hint: "Card-paid reports still waiting for the provider's line, with the candidates the rule scored (vehicle or card, ± 2 h, litres or amount ± 5 %); a person links the doubtful ones.",
      waiting: "{{count}} reports waiting",
      noneWaiting: "Every report is matched.",
      noCandidate: "no provider line within the window yet",
      link: "Link",
      unmatched: "{{count}} provider lines unclaimed",
      noneUnmatched: "Every provider line is claimed.",
      cardUnassigned: "card without a vehicle",
      unmatchedHint:
        "Assign the card to a vehicle (Transactions) or record the report the line belongs to.",
    },
    anomalies: {
      title: "Anomalies",
      hint: "Findings of the deterministic rules on the fuel reports, with their evidence. Dismiss what has an explanation, confirm what needs a follow-up — the Fuel Analyst explains, it never decides.",
      note: "A note for the decision (optional)",
      dismiss: "Dismiss",
      confirm: "Confirm",
      decided: "decided by {{who}} {{when}}",
      source: { rule: "rule", ai: "AI" },
      empty: "No anomaly in this state.",
    },
    allocation: {
      title: "Cost allocation",
      hint: "Rules split the period's fuel (and the ERP's maintenance actuals) over cost centres or projects — by the km share of the orders each vehicle ran, its own cost centre or fixed percentages. The journal is a re-class the ERP posts once a person approves it (W3, dual control).",
      lastRun: "last run {{period}} · {{state}}",
      newRun: "New run",
      rules: "Rules",
      newRule: "New rule",
      ruleName: "Rule name",
      priority: "Priority",
      sourceCc: "Credit cost centre (optional — the vehicle's when empty)",
      splitsHint:
        "One split per line: cost centre = percent, e.g. Depot Nord - DLG = 60",
      targetCostCenter: "cost centre",
      targetProject: "project",
      inactive: "inactive",
      noRules:
        "No rule yet — the seed adds one (km share with the vehicle fallback).",
      runs: "Runs",
      noRuns: "No run yet — pick a month and preview.",
      run: "Run {{period}}",
      reversal: "reversal",
      items: "{{count}} source documents",
      lines: "{{count}} lines",
      previewed: "previewed {{when}}",
      proposalPending: "the journal proposal waits in the inbox (dual control)",
      total: "Allocated",
      unallocated: "{{amount}} not allocated",
      preview: "Preview again",
      propose: "Propose the journal",
      reject: "Reject",
      supersede: "Supersede (reversing run)",
      byTarget: "By target",
      byPool: "By pool",
      nothing: "Nothing allocated in this run.",
      journal: "Journal preview",
      journalHint:
        "Debit per target, credit per source cost centre, on the pool's expense account from the connection profile — the total expense never changes.",
      dimension: "Dimension",
      debit: "Debit",
      credit: "Credit",
      accountFromProfile: "(account from the profile)",
      source: "Source",
      rule: "Basis",
      target: "Target",
      share: "Share",
      evidence: "Evidence",
      from: "from {{cc}}",
      unallocatedList: "{{count}} not allocated:",
    },
  },
  copilot: {
    storeDown:
      "The AI store is not reachable — the rows below may be incomplete.",
    decisions: {
      allow: "allowed",
      auto: "auto-approved",
      proposal: "proposal",
      deny: "denied",
      stub: "stub",
    },
    kinds: {
      chat: "Ask OneFleet",
      parse: "New-order parse",
      candidates: "Driver ranking",
      assign: "Assignment",
      plain: "Plain chat",
    },
    sessions: {
      title: "Sessions",
      hint: "Every conversation the copilot panel opened on this tenant — who, which agent, on which page, how many tasks.",
      hintOwn:
        "Your own copilot conversations — an administrator sees the tenant's.",
      who: "Who",
      agent: "Agent",
      page: "Page",
      tasks: "Tasks",
      lastAt: "Last used",
      empty: "No session yet — ask the copilot something from the panel.",
    },
    tasks: {
      title: "Tasks",
      hint: "One row per agent run: the source (rule / ai / ai+rule), the model, the tools called, the proposals filed, the warnings. The question's text is never stored — only its size.",
      hintOwn: "Your own runs — an administrator sees the tenant's.",
      session: "session",
      cloud: "cloud",
      sizes: "question {{q}} chars · answer {{a}} chars",
      empty: "No task yet.",
      audit: "Tool-call audit",
      auditHint:
        "Every tool call the policy allowed, denied, turned into a proposal or auto-approved — with the rule and the reason (administrators).",
      auditEmpty: "No tool call recorded.",
      when: "When",
      tool: "Tool",
      decision: "Decision",
      reason: "Reason",
    },
    policies: {
      title: "Policies",
      hint: "What the agents may do on this tenant: the tools each agent and role may reach, the write classes, the auto-approve rules, the cloud gate and the thresholds. Everything else is a proposal a person decides (D-06).",
      agents: "Agents × tools",
      roles: "Roles × tools",
      writes: "Write classes",
      rules: "Auto-approve rules",
      enabled: "on",
      disabled: "off",
      cap: "max {{n}} / h",
      cloud: "Cloud",
      on: "on",
      off: "off",
      highValue: "High-value order from",
      dualControl: "Dual control from",
      ttl: "Proposal lifetimes",
      override: "Tenant override (YAML)",
      defaultOnly: "default policy, no override",
      version: "override v{{n}}",
      editorHint:
        "Keys you set here replace the default's (dicts merge, lists replace). Saving validates on the AI API; an empty override returns to the default. Every save is a new version.",
      commentHint: "What changed (optional)",
      save: "Save as a new version",
      clear: "Clear",
      saved: "Saved as v{{n}} — in force within a minute.",
      history: "Versions",
      active: "active",
      load: "load",
      noHistory: "No override saved yet.",
    },
  },
  views: {
    label: "Saved views",
    pick: "Saved views…",
    none: "No saved view yet",
    saveCurrent: "Save this view",
    nameHint: "Name of the view",
    save: "Save",
    remove: "Remove the view “{{name}}”",
  },
  hub: {
    all: "All",
    connection: "ERP connection",
    direction: {
      push: "push",
      pull: "pull",
      bidir: "both ways",
      link_only: "link only",
    },
    submit: { draft: "Draft", submit: "Submit" },
    states: {
      pending: "Pending",
      processing: "Processing",
      done: "Done",
      ok: "OK",
      running: "Running",
      failed: "Failed",
      dead: "Dead",
      error: "Error",
      ignored: "Ignored",
      executed: "Executed",
      rejected: "Rejected",
      approved: "Approved",
      expired: "Expired",
      superseded: "Superseded",
    },
    modules: {
      assets: "Vehicles · Asset Lifecycle & Depreciation",
      dispatch: "Trip & Dispatch — orders, deliveries, invoices",
      drivers: "Driver Management",
      maintenance: "Maintenance & Parts Inventory",
      fuel: "Fuel & Cost Allocation",
      master: "Master data",
      events: "Events",
    },
    capabilities: {
      title: "Capabilities — the module switches",
      hint: "One row per capability string; the toggle switches it for this connection, the rules underneath say what fires it, whether a person approves it and whether the ERP document is left as a draft or submitted. Nothing here writes to the ERP.",
      legend:
        "Risk: R read · W1 link / mirror · W2 posting a draft · W3 money or master data (always a proposal, D-06).",
    },
    rulesCount: "{{count}} rules",
    approvalOn: "approval",
    override: "override",
    noRules:
      "No sync rule yet for this capability — 'Reset missing rules' adds the defaults.",
    resetRules: "Reset missing rules",
    w3Locked: "A W3 capability always waits for a decision",
    enabledCount: "{{on}} of {{count}} on",
    col: {
      event: "Event",
      direction: "Direction · trigger",
      enabled: "Enabled",
      approval: "Approval",
      submit: "ERP document",
      mapping: "Mapping",
    },
    sync: {
      title: "Sync activity",
      hint: "The pulls and refreshes, the events the ERP sent, and the relay's queue for the ERP — newest first.",
      runs: "Runs",
      noRuns: "No run yet — pulls and dimension refreshes appear here.",
      inbound: "Received ERP events",
      inboundFilter: "Events:",
      noEvents: "No event received.",
      badSignature: "signature rejected",
      replay: "Replay",
      outbox: "Relay queue (ERP target)",
      outboxHint:
        "OneFleet events the ERP rules react to; a gated write becomes a proposal, an ungated one runs on the connection.",
      outboxFilter: "Queue:",
      noOutbox: "Nothing queued.",
      retry: "Retry",
    },
    dead: {
      title: "Dead letter",
      hint: "What the relay gave up on or keeps failing on, and the ERP events that failed — with the last error. Retry and replay are OneFleet-side; a write still goes through the approval gate.",
      outbox: "Failed and dead outbox rows",
      outboxHint:
        "Dead = every attempt used; failed = waiting for its next attempt.",
      inbound: "Failed ERP events",
    },
    errors: {
      class: {
        authentication: "credentials",
        permission: "permission",
        missing_master_data: "master data missing",
        mandatory_field: "a field the ERP wants",
        business_rule: "the ERP's own rule",
        document_state: "the document's state",
        conflict: "changed on both sides",
        transport: "the site did not answer",
        capability: "not built for this ERP",
        unknown: "not seen before",
      },
    },
    backfill: {
      title: "Backfill — the fleet that was already there",
      hint: "Before the first pull writes anything: which of the ERP's records are records OneFleet already has, recognised by an existing link, the id the ERP carries, the VIN or the plate — and which ones nothing here recognises, named one by one so a VIN can be fixed before a twin is created.",
      resource: {
        assets: "Assets (with their vehicles)",
        vehicles: "Vehicles",
      },
      report: "Report only",
      link: "Link them now",
      linkHint:
        "Runs the ordinary pull: what is recognised is linked, the rest is created.",
      dryRun: "Nothing was written.",
      matched: "{{matched}} of {{total}} recognised ({{pct}} %)",
      by: {
        link: "already linked",
        public_id: "by the id the ERP carries",
        vin: "by VIN",
        plate: "by plate",
        internal_id: "by asset id",
        unmatched: "nothing recognised",
      },
      unmatchedHint: "Nothing here matches these — a pull would create them:",
      col: { label: "Record", plate: "Plate", vin: "VIN", erp: "ERP id" },
    },
    reconcile: {
      title: "Reconcile — links and reviews",
      hint: "Every link between a OneFleet record and an ERP document with its state; the records the ERP created that a person must review; the pulls that re-link by OneFleet id, VIN, then plate.",
      counts: "Links by state",
      run: "Reconcile now",
      repush: "Push again",
      repushHint:
        "Propose catching the ERP document up with what OneFleet holds — a person approves it.",
      repushed: "Proposed: the catch-up waits in the proposals inbox.",
      conflicts: "Out of step",
      conflictsHint:
        "The nightly reconcile (02:15) re-reads the ERP documents behind the links; a rule on manual waits for you.",
      noConflicts: "Nothing is out of step.",
      ours: "ours",
      theirs: "the ERP's",
      takeErp: "Take the ERP's",
      keepOurs: "Keep ours",
      status: {
        resolved_erp: "the ERP's values applied",
        resolved_local: "ours kept",
        resolved_manual: "decided",
        ignored: "ignored",
      },
      links: "Links",
      col: {
        record: "Record",
        document: "ERP document",
        state: "State",
        synced: "Synced",
      },
      unlink: "Unlink",
      noLinks: "No link in this state.",
      review: "Needs review",
      reviewHint:
        "Vehicles the ERP created without a matching plate or VIN — open one, complete it, set its status.",
      noReview: "Nothing to review.",
    },
    mappings: {
      title: "Mappings — the field maps",
      hint: "Versioned YAML in the OneFleet repository; per field: who owns it, the transform, the value map, the forward-only guard. Read-only.",
      fields: "{{count}} fields",
      submit: "submit",
      identity: "Identity",
      marker: "marker",
      conflict: "Conflict",
      owner: "Owner",
      transform: "Transform",
      notes: "Notes",
      required: "required",
      monotonic: "forward-only",
      hashed: "in the change hash",
      none: "No mapping for this pack yet — the Oracle Fusion pack arrives with WP-B8 (after `--describe` on a named pod, D-12).",
    },
  },
  proposals: {
    title: "Proposals",
    hint: "Every write a rule or an agent proposed — approve executes it on the ERP, reject records why. Nothing is written without a decision here or on the record's ERP card.",
    allCapabilities: "All capabilities",
    empty: "No proposal in this state.",
    action: "Action",
    evidence: "Evidence",
    result: "Result",
    notYet: "Not executed yet.",
    reason: "Reason",
    actor: "Triggered by",
    preview: {
      title: "The document this writes",
      problem: "Approving would fail:",
      customer: "Customer",
      date: "Date",
      line: "Line",
      qty: "Qty",
      rate: "Rate",
      amount: "Amount",
      total: "Total",
      proofs_one: "{{count}} proof of delivery goes with it",
      proofs_other: "{{count}} proofs of delivery go with it",
      raw: "The event that raised it",
      refs: {
        vehicle_no: "Vehicle",
        lr_no: "Waybill",
        driver: "Driver",
        cost_center: "Cost centre",
        project: "Project",
        site: "Site",
        set_warehouse: "Warehouse",
        business_unit: "Business unit",
        transaction_type: "Transaction type",
        cross_reference: "Reference",
      },
    },
    rule: "Rule",
    reasonHint: "Reason (optional)",
    decidedAt: "Decided {{when}}",
    decidedBy: "by {{who}}",
    approveExecute: "Approve and execute",
    expiresAt: "expires {{when}}",
    expired: "expired",
    onBehalf:
      "Proposed by the agent acting as {{who}} — approving runs the action as you.",
    actions: {
      "orders.assign": "Assign a driver (Dispatch Copilot)",
    },
    policy: "Policy",
    policyLine:
      "ERP writes = proposal (W1 link/mirror, W2 drafts, W3 money and master data with dual control); reads run as the caller; the driver and customer tracks never reach a cloud model.",
    aiLater:
      "The agents' proposals (re-assignment, work-order drafts, material requests, allocation journals) join this inbox with the AI store and policies.",
  },
  developer: {
    title: "Developer",
    hint: "The `/v1` API as an integrator sees it: API keys, outbound webhooks with their deliveries, the company's events, the request logs, and the sandbox twin that test-mode keys work on.",
    secretOnce: "Shown once — copy it now.",
    copy: "Copy",
    dismiss: "Dismiss",
    keys: {
      title: "API keys",
      hint: "A key signs `/v1` calls (`Authorization: Bearer of_live_… / of_test_…`); a test-mode key works on the sandbox twin. The secret is shown once.",
      col: { name: "Name", key: "Key", mode: "Mode", lastUsed: "Last used" },
      test: "sandbox",
      live: "live",
      testMode: "test mode (sandbox)",
      create: "Create key",
      revoke: "Revoke",
      empty: "No API key yet.",
      secretLabel: "New API key",
    },
    webhooks: {
      title: "Outbound webhooks",
      hint: "OneFleet POSTs the company's events to the URLs registered here, signed with the endpoint's secret; a failing endpoint is retried on the outbox's schedule and paused after ten failures in a row.",
      url: "URL",
      events: "Events (`*`, `order.*`, `vehicle.created` …)",
      create: "Register endpoint",
      pause: "Pause",
      resume: "Resume",
      rotate: "Rotate secret",
      test: "Test",
      testOk: "Delivered",
      testFailed: "Not delivered",
      testClose: "close",
      delete: "Delete",
      confirmDelete: "Remove this endpoint? Its deliveries stop at once.",
      failures: "{{count}} failure(s) in a row",
      lastSuccess: "last delivered",
      never: "nothing delivered yet",
      empty: "No endpoint yet.",
      secretLabel: "Signing secret",
      signature:
        'Each POST carries `X-OneFleet-Event`, `X-OneFleet-Delivery` and `X-OneFleet-Signature: t=<ts>,v1=<hmac-sha256(secret, "<ts>.<body>")>`; the body is `{id, type, created_at, company, api_version, aggregate, data}`.',
    },
    deliveries: {
      title: "Deliveries",
      empty: "No delivery yet for this endpoint.",
    },
    events: {
      title: "Events",
      hint: "Everything the company's records emitted — what the webhooks and the ERP relay fan out.",
      type: "Event type",
      all: "All types",
      col: {
        id: "Id",
        type: "Type",
        targets: "Targets",
        status: "Relay",
        when: "When",
      },
      empty: "No event yet.",
    },
    logs: {
      title: "Request logs",
      hint: "The `/v1` calls of the last 30 days: who, what, the status and the latency. The console's own traffic is not logged here.",
      col: {
        when: "When",
        who: "Who",
        request: "Request",
        status: "Status",
        ms: "ms",
      },
      filters: {
        anyWho: "Anyone",
        method: "Method",
        anyMethod: "Any method",
        path: "path contains…",
        status: "Status",
        anyStatus: "Any status",
      },
      empty: "No `/v1` call yet.",
    },
    socket: {
      title: "Realtime socket",
      hint: "The same socket.io hub the console listens to: an API key in the handshake joins the company's rooms; subscribe to a vehicle, a driver, an order for its own events.",
      url: "Address",
      auth: "Handshake",
      rooms: "Your rooms",
      subscribable: "Subscribable",
      events: "Events",
      copy: "Copy the snippet",
      copied: "Copied",
    },
    sandbox: {
      title: "Sandbox",
      hint: "Test-mode keys read and write the sandbox twin of this company. The reset empties it — the live data is never touched.",
      reset: "Reset the sandbox",
      confirm:
        "Empty this company's sandbox twin? Every row test-mode keys created is deleted; the live data stays.",
      done: "{{rows}} row(s) deleted across {{tables}} table(s).",
      readOnly: "The reset needs the developer and the settings permissions.",
    },
  },
  aiSettings: {
    title: "AI",
    hint: "What the AI API answers through the edge, the model roles, the MCP tool pool and the policy that governs writes. Tenant toggles, budgets, the policy editor, usage and evals arrive with the AI store.",
    readiness: "Readiness",
    api: "AI API",
    gateway: "Gateway",
    store: "AI store",
    chatModel: "Chat model",
    tracks: "Tracks",
    openGateway: "Open the gateway UI",
    models: "Model roles",
    roles: {
      chat_small: "Chat (small)",
      chat_large: "Chat (large)",
      extract: "Extraction",
      vision: "Vision",
      embed: "Embeddings",
    },
    available: "{{count}} models offered by the gateway",
    modelsHint:
      "Every model call goes through OneSphere's gateway on the OneFleet tracks (D-05); dev runs the demo models until a resident model is chosen.",
    registry: "MCP tool pool",
    registryHint:
      "The servers that announced to the AI API and their tools; the caller's identity travels to the core, so a tool sees only what the person may see.",
    tools: "{{count}} tools",
    announced: "announced {{when}}",
    notAnnounced: "not announced",
    col: {
      tool: "Tool",
      mode: "Mode",
      risk: "Risk",
      roles: "Roles",
      description: "Description",
    },
    read: "read",
    write: "write",
    policy: "Policy (defaults)",
    policy1:
      "Ask OneFleet reads as the signed-in person; anonymous callers get no data.",
    policy2:
      "Every write tool is a proposal — dispatch writes, work-order drafts, ERP documents — decided by a person.",
    policy3:
      "Money proposals above the tenant threshold need a second approver from finance (dual control).",
    policy4:
      "Driver and customer conversations stay on the on-prem models; cloud is policy-gated per prompt class.",
    later:
      "Editable per tenant with history, plus usage and evals, with the AI store.",
    openProposals: "Proposals",
    tenant: {
      title: "This tenant's AI",
      hint: "The model each role routes to, the budgets a call must stay inside, and whether the agents narrate. Leave a field empty to inherit the deployment's.",
      usage:
        "Used: {{day}} tokens today · {{month}} this month · {{cloud}} cloud call(s) today",
      noLimit: "no limit",
      save: "Save",
      on: "on",
      off: "off",
      cloud: "The cloud gate is {{state}} — it is the policy's, edited in",
      updated: "Version {{version}}, saved {{when}} by {{who}}",
      role: {
        chat_small: "Chat (small)",
        chat_large: "Chat (large)",
        extract: "Extract",
        vision: "Vision",
        embed: "Embeddings",
      },
      budget: {
        tokens_day: "Tokens a day",
        tokens_month: "Tokens a month",
        cloud_calls_day: "Cloud calls a day",
      },
      switch: {
        narrate: "Narrate the answers",
        narrate_driver: "Narrate for drivers",
        parse_model: "Model on the order parse",
      },
    },
    schedules: {
      title: "Daily schedules",
      hint: "What the worker asks the agents every day for this company (the deployment's clock). Proposals land in the inbox under the policy; a person decides. The last run per job is what the worker recorded.",
      language: "Language",
      col: {
        job: "Job",
        at: "At",
        switches: "Switches",
        tells: "Tells",
        last: "Last run",
      },
      enabled: "enabled",
      propose: "file the proposals",
      threshold: "tell when unplanned ≥",
      never: "never ran",
      counts:
        "{{findings}} findings · {{proposals}} proposals · {{notifications}} notifications",
      unplanned: "{{count}} unplanned",
      skipped: {
        no_open_orders: "nothing to plan — no open order for tomorrow",
      },
      footer:
        "Changes apply to the next run. The route advice never applies a plan: the manifests stay a dispatcher's click.",
      readOnly: "Read-only: the switches need the settings permission.",
      jobs: {
        fuel_findings: {
          name: "Fuel Analyst",
          what: "the findings over every report; finance and the fleet managers told",
        },
        maintenance_scan: {
          name: "Maintenance Advisor",
          what: "the whole fleet scanned; draft work orders, parts requests and lifecycle hints filed as proposals",
        },
        route_advice: {
          name: "Route Advisor",
          what: "tomorrow's plan from the open orders, the advice on it; dispatchers told when orders are left unplanned (opt-in)",
        },
      },
    },
    docs: {
      title: "Documents",
      hint: "The company's manuals, procedures and policies Ask OneFleet and the agents quote — searched as the signed-in person over the collections their roles may read; a passage comes back with its document, page and heading. Text, Markdown or PDF.",
      count: "{{count}} document(s)",
      noCollections: "No collection yet — the first document creates one.",
      empty:
        'No document yet. An administrator adds the manual, the procedures and the policies below; Ask OneFleet then answers a "how do I…" from them.',
      col: {
        title: "Title",
        collection: "Collection",
        state: "State",
        added: "Added",
      },
      collections: {
        manuals: "Manuals",
        sops: "Procedures (SOPs)",
        policies: "Policies",
      },
      indexed: "indexed · {{pages}} page(s) · {{chunks}} passage(s)",
      failed: "failed:",
      pending: "indexing…",
      remove: "Remove",
      add: "Add a document",
      untitled: "Untitled",
      titlePlaceholder: "Fuel receipts procedure",
      file: "File (txt, md, pdf)",
      text: "Or paste the text (Markdown headings become the passages' headings)",
      textPlaceholder:
        "# Fuel receipts\n\n## Recording a receipt\n\nEvery fuel purchase is recorded the same day…",
      roles: "Who may read",
      rolesHint:
        "none ticked = everyone in the company; the roles apply to the whole collection",
      submit: "Add and index",
      indexing: "Indexing…",
      search: "Try a question",
      searchPlaceholder: "how do I record a fuel receipt",
      searchGo: "Search",
      noHits: "Nothing in the documents about “{{q}}”.",
      lexical: "words",
      semantic: "meaning",
      footer:
        "Documents are indexed at once through the gateway's embeddings track; an identical file is not added twice. Removing a document removes its passages.",
      readOnly:
        "Read-only: adding and removing documents need the realm's admin role.",
    },
  },
  trips: {
    title: "Trips",
    hint: "What the drivers' odometer photos at the start and the close of their jobs add up to per vehicle — against the planned km of the same orders, the month's litres and the L/100 km on trip km. Amber from 15 % beyond the plans, red from 30 %.",
    period: "Month",
    kpi: {
      trips: "Closed trips",
      odometer: "On the odometers",
      planned: "Planned",
      variance: "Variance",
      litres: "Fuel",
      l100: "Consumption",
    },
    col: {
      vehicle: "Vehicle",
      trips: "Trips",
      odometer: "Odometer km",
      planned: "Planned km",
      variance: "Variance",
      litres: "Litres",
      l100: "L/100 km",
      fuel: "Fuel spend",
    },
    empty:
      "No trip closed with both odometer readings in {{period}} — the driver app records them at the start and the close of a job (Assist › odometer photo).",
    footer:
      "{{beyond}} vehicle(s) worth a look this month (beyond the plan, or readings that cannot be right). The Fuel Analyst reconciles the litres with these km; the Route Advisor reads the same summary.",
    openAnalyst: "Fuel Analyst",
    openRuns: "Runs",
    suspect:
      "Far under the plan: a reading is missing or repeated — check the start / close photos.",
  },
  runs: {
    title: "Optimisation runs",
    hint: "A day's open orders planned over the trucks with a driver by the tenant's solver — apply dispatches the manifests, propose files it for a second person",
    empty: "No run yet — plan a day.",
    pick: "Pick a run on the left, or plan a day.",
    last: "Last plan:",
    row: "{{routes}} routes · {{orders}} orders · {{unassigned}} unassigned",
    state: {
      draft: "Draft",
      computed: "Computed",
      proposed: "Proposed",
      applied: "Applied",
      rejected: "Rejected",
      failed: "Failed",
    },
    fallback: "heuristic fallback ({{provider}})",
    fallbackShort: "heuristic",
    solver: "solver {{provider}}",
    computed: "computed {{when}}",
    scope: {
      day: "the day's open orders",
      orders: "{{count}} orders",
      vehicles: "{{count}} vehicles",
    },
    proposalPending:
      "Filed as a proposal — a second dispatcher decides in the inbox.",
    openInbox: "Open the inbox →",
    summary: {
      routes: "Routes",
      orders: "Orders",
      unassigned: "Unassigned",
      distance: "Distance",
    },
    actions: {
      compute: "Compute again",
      apply: "Apply — dispatch the manifests",
      applyConfirm:
        "Create and dispatch {{count}} manifest(s) now? The orders are assigned to the trucks' drivers.",
      propose: "Propose (a second person decides)",
      delete: "Delete",
      deleteConfirm: "Delete this run?",
    },
    noRoutes: "No route — nothing could be planned.",
    unassigned: "{{count}} unassigned",
    skipped: "{{count}} vehicles skipped",
    manifests: "{{count}} manifests",
    dispatched: "dispatched",
    route: {
      facts: "{{stops}} stops · {{distance}} · {{time}} · {{load}} kg",
      noDriver: "no driver",
    },
    stop: { pickup: "pickup", dropoff: "drop-off" },
    plan: {
      open: "Plan a day",
      close: "Close",
      date: "Day",
      name: "Name",
      namePlaceholder: "Plan {{date}}",
      vehicles: "Vehicles (empty = every active truck with a driver)",
      noDriver: "no primary driver — skipped by the plan",
      picked: "{{count}} picked",
      allWithDriver: "Every active truck with a primary driver.",
      forceHeuristic: "Skip the solver — nearest-neighbour heuristic (flagged)",
      submit: "Plan",
      computing: "Computing…",
    },
  },
  rates: {
    title: "Service rates",
    hint: "The rate lines flow into quotes, the order's purchase rate and the ERP invoice lines — money in the rate's currency",
    empty: "No rate yet.",
    new: "New rate",
    close: "Close",
    readOnly: "Read only — administrators edit the rates.",
    showInactive: "show inactive",
    method: {
      fixed: "Fixed",
      per_km: "Per km",
      tiered: "Distance bands",
      per_parcel: "Per parcel",
    },
    col: {
      name: "Rate",
      method: "Method",
      base: "Base",
      perKm: "Per km",
      min: "Minimum",
      bands: "Bands",
      surcharges: "Surcharges",
      scope: "Scope",
      priority: "Priority",
      active: "Active",
    },
    noBands: "—",
    cod: "COD",
    peak: "Peak",
    any: "any",
    anyType: "any type",
    edit: "Edit",
    editTitle: "Edit {{name}}",
    days: "{{count}} day(s)",
    active: "active",
    inactive: "inactive",
    f: {
      name: "Name",
      method: "Method",
      currency: "Currency",
      base: "Base fee",
      perKm: "Per km",
      min: "Minimum fee",
      maxKm: "Max distance (km)",
      priority: "Priority (lower first)",
      type: "Order type",
      zone: "Zone",
      cod: "COD surcharge (%)",
      bands:
        "Distance bands — one per line: `min-max: fee` (no max = fallback)",
      parcels: "Parcel bands — one per line: `size ≤ kg: fee`",
      terms: "Duration terms",
      days: "Estimated days",
      submit: "Create the rate",
      save: "Save the rate",
    },
  },
  zones: {
    title: "Service areas and zones",
    hint: "Polygons with triggers — enter, exit, dwell, speeding — that feed the alerts, the dispatch filters and the zone-scoped rates",
    empty: "No zone yet.",
    new: "New zone",
    close: "Close",
    draft: "new zone",
    kind: {
      service_area: "Service area",
      geofence: "Geofence",
      depot: "Depot",
      no_go: "No-go",
      speed: "Speed zone",
    },
    alert: {
      enter: "on enter",
      exit: "on exit",
      dwell: "on dwell",
      speeding: "on speeding",
    },
    rule: {
      enter: "enter",
      exit: "exit",
      dwell: "dwell {{min}} min",
      speed: "≤ {{kmh}} km/h",
      alert: "alert: {{alert}}",
      none: "no trigger",
    },
    corners: "{{count}} corners",
    editCorners: "Edit the corners",
    dragHint: "drag the corners on the map, then save",
    saveCorners: "Save the corners",
    active: "active",
    inactive: "inactive",
    delete: "Delete",
    deleteConfirm: "Delete the zone {{name}}?",
    f: {
      name: "Name",
      kind: "Kind",
      color: "Colour",
      dwell: "Dwell (min)",
      speed: "Speed limit (km/h)",
      alert: "Alert",
      points: "{{count}} corners placed — click the map for more",
      clickMap: "Click the map to place the corners (3 or more)",
      clear: "Clear",
      paste: "…or paste a GeoJSON Polygon, or `lng, lat` per line",
      submit: "Create the zone",
    },
  },
  schedule: {
    title: "Week schedule",
    hint: "Templates build the week, the conflicts are computed, publishing files the Shift Assignments for the ERP — a person decides",
    driver: "Driver",
    weekOf: "Week {{from}} – {{to}}",
    thisWeek: "this week",
    state: { draft: "Draft", published: "Published" },
    shiftState: {
      draft: "draft",
      published: "published",
      cancelled: "cancelled",
    },
    counts:
      "{{shifts}} shifts · {{drivers}} drivers · {{hours}} h · {{conflicts}} conflicts",
    publishedAt: "published {{when}}",
    build: "Build from templates",
    built: "{{count}} shift(s) created from the templates.",
    addShift: "Add shift",
    publish: "Publish week",
    publishConfirm:
      "Publish {{count}} shift(s)? With an ERP connection that takes shifts, the Shift Assignments are filed as a proposal.",
    publishForce: "{{count}} high-severity conflict(s). Publish anyway?",
    close: "Close",
    delete: "Delete",
    cancelShift: "Cancel shift",
    conflicts: "Conflicts ({{count}})",
    noConflicts: "No conflict this week.",
    noDrivers: "No active driver.",
    noTemplates: "No template yet.",
    noUpcoming: "No upcoming shift.",
    upcoming: "Upcoming shifts",
    templates: "Templates",
    templatesTitle: "Shift templates",
    templatesHint:
      "The weekly patterns Build from templates turns into shifts — every N weeks from an anchor Monday, within a validity window.",
    templatesCount: "{{count}} active template(s)",
    templatesOpen: "Manage the templates",
    everyWeeks: "every {{count}} weeks",
    dragHint:
      "Drag a draft block to another driver or day — the same time and length; the conflicts recompute.",
    tzHint:
      "The tenant's timezone — the days, blocks and times are read in it.",
    newTemplate: "New template",
    tplDrivers: "{{count}} drivers",
    tplAll: "every active driver",
    activate: "Activate",
    deactivate: "Deactivate",
    deleteTemplateConfirm: "Delete the template {{name}}?",
    legend: "Legend",
    legendLeave: "approved time off",
    legendHeld: "request held",
    legendDashed: "Dashed = draft; solid = published; struck = cancelled.",
    kind: { shift: "Shift", run: "Run", standby: "Standby" },
    rule: {
      overlap: "overlap",
      leave: "time off",
      document_expired: "document expired",
      document_expiring: "document expiring",
      unavailable: "unavailable",
      daily_duty: "daily duty",
      weekly_hours: "weekly hours",
      daily_rest: "daily rest",
    },
    strip: {
      today: "{{count}} shifts today",
      requests: "{{count}} open requests",
      documents: "{{expired}} expired · {{expiring}} expiring",
    },
    erp: {
      title: "ERP:",
      none: "no connection",
      shifts: "shifts",
      leave: "leave",
      fusionShifts: "Oracle Fusion takes no shift assignment in v1 (HDL later)",
    },
    f: {
      name: "Name",
      start: "Start",
      end: "End",
      kind: "Kind",
      shiftType: "ERP shift type",
      drivers: "Drivers (empty = every active driver)",
      days: "Days of the week",
      interval: "Every N weeks",
      anchor: "From the week of",
      validFrom: "Valid from",
      validTo: "Valid to",
      day: "Day",
      notes: "Notes",
      submit: "Create the template",
    },
  },
  timeOff: {
    title: "Time off",
    hint: "A request becomes a Leave Application proposal when the ERP takes leave — its approver decides; otherwise a fleet manager decides here",
    empty: "No request.",
    new: "Request time off",
    note: "Note (optional)",
    approve: "Approve",
    reject: "Reject",
    cancel: "Cancel",
    cancelConfirm: "Cancel this request?",
    decided: "decided by {{who}} {{when}}",
    proposalPending:
      "Filed as a proposal — a person decides in the inbox, then the ERP's approver.",
    state: {
      requested: "Requested",
      proposed: "Proposed",
      approved: "Approved",
      rejected: "Rejected",
      cancelled: "Cancelled",
    },
    kind: {
      leave: "Leave",
      sick: "Sick",
      training: "Training",
      other: "Other",
    },
    source: { console: "console", driver_app: "driver app", erp: "ERP" },
    f: {
      kind: "Kind",
      leaveType: "ERP leave type",
      from: "From",
      to: "To",
      halfDay: "half day",
      reason: "Reason",
      submit: "Request",
    },
  },
  availability: {
    title: "Availability and hours of service",
    hint: "Hours of service for a day from the position fixes, and the availability windows the week's conflicts read",
    windows: "Availability windows",
    windowsHint:
      "Recurring by weekday, or one dated window; unavailable windows raise conflicts on the schedule",
    pickDriver: "Pick a driver",
    empty: "No window — available at any time.",
    kind: { available: "available", unavailable: "unavailable" },
    f: {
      kind: "Kind",
      weekday: "Weekday",
      onDate: "One day (optional)",
      submit: "Add window",
    },
  },
  hos: {
    day: "Day",
    empty: "No active driver.",
    ok: "OK",
    breakDue: "break due",
    breakIn: "Break due after {{in}} more driving",
    left: "{{left}} left",
    driving: "Driving",
    duty: "Duty",
    week: "Week",
    profile: "HOS profile",
    violation: {
      daily_driving_exceeded: "daily driving exceeded",
      daily_duty_exceeded: "daily duty exceeded",
      weekly_driving_exceeded: "weekly driving exceeded",
      break_overdue: "break overdue",
    },
    f: {
      regime: "Regime",
      drive: "Daily drive (h)",
      duty: "Daily duty (h)",
      week: "Weekly drive (h)",
      breakAfter: "Break after (h)",
      breakMin: "Break (min)",
      rest: "Daily rest (h)",
      save: "Set profile",
    },
  },
  scorecards: {
    title: "Scorecards",
    hint: "One row per driver per month, rolled up from orders, positions, device events, fuel and HOS",
    period: "Month",
    compute: "Compute the month",
    computing: "Computing…",
    computedAt: "computed {{when}}",
    empty: "No scorecard for this month — compute it.",
    weights:
      "Score: on-time 40 · POD 15 · safety 25 (harsh + speeding per 100 km) · HOS 10 · fuel 10 (against the vehicle's usual).",
    col: {
      score: "Score",
      orders: "Done / orders",
      onTime: "On time",
      pod: "POD",
      events: "Harsh / speeding",
      hos: "HOS viol.",
      days: "Days",
    },
  },
  expiries: {
    title: "Expiries",
    empty: "Nothing expiring within 90 days.",
    inDays: "in {{days}} d",
    expiredAgo: "expired {{days}} d ago",
  },
  documents: {
    add: "Add document",
    empty:
      "No document yet — the licence above comes from the driver's profile.",
    issued: "issued {{on}}",
    deleteConfirm: "Delete the document {{name}}?",
    sweep:
      "Statuses are recomputed every morning at 06:15; an alert is raised once per threshold crossed (30 / 14 / 7 days, then expiry).",
    kind: {
      licence: "Licence",
      medical: "Medical",
      id: "Identity",
      permit: "Permit",
      training: "Training",
      contract: "Contract",
      insurance: "Insurance",
      technical_inspection: "Technical inspection",
      emissions: "Emissions certificate",
      road_tax: "Road tax",
      transport_licence: "Transport licence",
      registration: "Registration",
      other: "Other",
    },
    state: { valid: "valid", expiring: "expiring", expired: "expired" },
    f: {
      kind: "Kind",
      name: "Name",
      number: "Number",
      authority: "Issued by",
      issued: "Issued on",
      expires: "Expires on",
      notes: "Notes",
      submit: "Add",
    },
  },
  advisor: {
    title: "Maintenance Advisor",
    manual: "Manual «{{title}}» p.{{page}}:",
    hint: "The findings ranked by the rules — services due, fault codes, open issues, parts short, repeated failures — narrated by the model; the proposals go to the inbox",
    run: "Run the advisor",
    running: "Scanning…",
    propose: "File {{count}} proposal(s)",
    filing: "Filing…",
    filed:
      "{{filed}} proposal(s) filed in the inbox, {{executed}} executed by the policy.",
    empty: "No finding.",
    noProposal: "nothing to propose",
    severity: { high: "critical", medium: "to handle", low: "to watch" },
    kind: {
      overdue: "overdue",
      due_soon: "due soon",
      issue: "issue",
      dtc: "fault code",
      parts_short: "parts short",
      repeated_failures: "repeated failures",
    },
    action: {
      "maintenance.draft_work_order": "draft work order",
      "maintenance.request_parts": "parts request (ERP)",
      "assets.lifecycle_hint": "disposal review",
    },
  },
  adminAddons: {
    forTenant: "For {{name}} ({{slug}})",
    tenants: {
      title: "Tenants",
      hint: "Every company OneFleet knows: its locale, active members, ERP connections and how much it holds. Pick a row — the provisioning form, the connections and the map provider below act on it.",
      new: "New tenant",
      cancel: "Cancel",
      none: "No tenant yet.",
      created:
        "Tenant {{slug}} created — add its /Scope/Company group in the realm, then provision its ERP connection.",
      mapsDefault: "defaults",
      fleetTitle: "vehicles / drivers / orders",
      status: { active: "active", inactive: "inactive" },
      deactivate: "Deactivate",
      reactivate: "Reactivate",
      col: {
        tenant: "Tenant",
        erpCompany: "ERP company",
        locale: "Currency · country · timezone",
        members: "Members",
        fleet: "Vehicles / drivers / orders",
        connections: "ERP connections",
        maps: "Maps",
        since: "Since",
        status: "Status",
      },
      form: {
        name: "Name",
        slug: "Slug",
        erpCompany: "ERP company (the /Scope/Company group)",
        currency: "Currency",
        country: "Country",
        timezone: "Timezone",
        create: "Create the tenant",
        withGroup:
          "Also create its /Scope/Company/<ERP company> group in the realm (when the API is configured for the realm's admin API)",
        hint: "Creates the company row in OneFleet. Its people sign in through the realm's /Scope/Company/<ERP company> group — created here when ticked, else by keycloak-scope; the ERP connection is the form above; nothing is written to an ERP.",
      },
    },
    realm: {
      title: "Realm group",
      hint: "The Keycloak group the tenant's people join to see it (D-28): /Scope/Company/<ERP company>.",
      unconfigured:
        "The API has no realm administrator configured (KEYCLOAK_ADMIN_URL / USER / PASSWORD) — create the group with keycloak-sync-scope or in the realm console.",
      exists: "present in the realm",
      missing: "missing in the realm",
      create: "Create the group",
      created: "Created.",
      footer:
        "Only groups are touched here — people, roles and clients stay the realm console's.",
    },
    modules: {
      title: "Module switches",
      hint: "The D-17 modules per ERP connection — how many capability switches are on, how many of them write to the ERP. They are set on ERP Hub › Capabilities.",
      none: "No ERP connection yet — provision one above.",
      col: { module: "Module" },
      name: {
        assets: "Assets & lifecycle",
        dispatch: "Dispatch & sales",
        drivers: "Drivers & HR",
        maintenance: "Maintenance & parts",
        fuel: "Fuel & cost allocation",
        master: "Master data",
        events: "Events",
      },
      writes: "{{count}} write",
      manage: "ERP Hub › Capabilities →",
    },
    geo: {
      title: "Map provider",
      hint: "Tiles, geocoding, routing and optimisation for this tenant (D-14): Google Maps with its keys, the self-hosted OSM stack of the OneFleet compose, or straight lines. The server key is sealed at rest and never read back.",
      pickTenant: "Pick a tenant above.",
      current: "Today: {{provider}} ({{source}}) · {{key}}",
      source: {
        tenant: "set for this tenant",
        defaults: "the deployment's defaults",
      },
      keyStored: "server key stored",
      keyNone: "no server key",
      provider: "Provider",
      providers: {
        straight: "Straight lines (no provider)",
        "osm-selfhosted": "Self-hosted OSM (OSRM · VROOM · Photon)",
        google: "Google Maps",
      },
      country: "Country bias (ISO-2)",
      avgKmh: "Assumed city speed (km/h)",
      apiKey: "Google server key (Geocoding + Routes)",
      browserKey: "Google browser key (Maps JS, referrer-restricted)",
      styleUrl: "MapLibre style URL (blank = the OSM raster style)",
      lat: "Map centre — latitude",
      lng: "Map centre — longitude",
      save: "Save the provider",
      reset: "Back to the defaults",
      saved: "Saved — the console maps use {{provider}} from the next load.",
      footer:
        "A missing server key keeps the stored one; the routing and optimisation services of the compose (`geo` profile) answer the self-hosted provider.",
    },
  },
  settingsGeneral: {
    title: "General",
    hint: "The tenant as OneFleet knows it: the identity the realm and the ERP gave it (read-only), the display name, currency and timezone, the locale and units the screens use.",
    identity: "Identity",
    scanning: {
      title: "Scanning",
      hint: "How materials and documents are entered. Scanning is the default: a barcode, a QR code or an RFID tag identifies what moved, and a reader can be the mobile app's camera, a hand scanner, a wedge or a gate.",
      enabled: "Enter by scanning",
      enabledHint: "Switch this off and everything is typed by hand.",
      materials: "Materials move on a scan",
      materialsHint:
        "A line must present the item's barcode, or the serial of the very box.",
      documents: "Documents move on a scan",
      documentsHint:
        "A driver or picker advances a job by scanning its own label, not somebody else's.",
      allowTyped: "Allow typing instead, when a label is unreadable",
      allowTypedHint:
        "Leave this off: a hatch left open is hand entry with extra steps. Every typed movement is recorded as typed, with who typed it.",
    },
    slug: "Slug",
    erpCompany: "ERP company",
    name: "Display name",
    identityHint:
      "The slug and the ERP company come from OneSphere's Admin › Add-ons and the realm's scope group; they do not change here.",
    locale: "Locale and units",
    currency: "Currency",
    timezone: "Timezone",
    language: "Language",
    distanceUnit: "Distance",
    weekStart: "Week starts on",
    days: { monday: "Monday", sunday: "Sunday" },
    saved: "Saved.",
    readOnly: "Read-only: changes need the settings permission.",
  },
  orderTypes: {
    title: "Order types and workflows",
    hint: "Each order type carries a versioned workflow — states, transitions with their guards and actions, the POD it asks for, the ERP hooks on the states. Orders keep the version they started on.",
    meta: "{{status}} · POD {{pod}} · workflow v{{version}}",
    noPod: "none",
    openWorkflow: "Open the workflow",
    close: "Close",
    noWorkflow: "No published workflow.",
    workflowMeta:
      "Active version {{version}} of {{versions}} · initial state {{initial}}",
    customerVisible: "customer-visible",
    col: {
      state: "State",
      activity: "Activity",
      scope: "Waypoint",
      hooks: "ERP hooks",
      event: "Event",
      from: "From",
      to: "To",
      guards: "Guards",
      actions: "Actions",
    },
    simulate: { events: "Dry run — events, in order", run: "Simulate" },
    form: {
      key: "Key",
      name: "Name",
      pod: "POD",
      podRequired: "POD required",
      create: "New order type",
      hint: "A new type starts on the default delivery workflow; open it to edit a draft of its states, transitions and ERP hooks and publish it.",
    },
    designer: {
      versions: "Versions",
      status: { active: "active", draft: "draft", retired: "retired" },
      newDraft: "New draft from the active version",
      openDraft: "Edit draft v{{version}}",
      name: "Workflow name",
      initial: "Initial state",
      notes: "Notes",
      statusText: "Status text",
      flags: "Flags",
      flag: {
        customer_visible: "customer",
        tracking: "tracking",
        complete: "complete",
        terminal: "terminal",
      },
      remove: "remove",
      addState: "Add a state",
      addTransition: "Add a transition",
      addHook: "Add an ERP hook",
      onEnter: "On entering",
      capability: "Capability",
      approval: "Approval",
      validate: "Validate (dry run)",
      save: "Save the draft",
      publish: "Publish",
      discard: "Discard the draft",
      saveFirst: "Save the draft first",
      publishHint:
        "Publishing makes the draft the active version and retires the previous one; orders in flight keep the version they started on.",
      noPath: "No path from the initial state — check the transitions.",
      description: "Description",
      typeStatus: "Type",
      saveType: "Save the type",
      saved: "Saved.",
    },
    footer:
      "■ terminal state · ▶ initial state. The dry run applies the events to the stored workflow (or the draft) and shows each transition and the ERP hooks it would fire — nothing is written.",
  },
  branding: {
    title: "Branding",
    hint: "What customers and drivers see of the company: the name and tagline on the public tracking page and in the driver app, the colours, the logo, the support contacts, the line under a delivery's status.",
    field: {
      display_name: "Display name",
      tagline: "Tagline",
      support_phone: "Support phone",
      support_email: "Support e-mail",
      tracking_message: "Message on the tracking page",
      primary_color: "Primary colour",
      accent_color: "Accent colour",
    },
    placeholder: {
      tracking_message: "Thank you for your trust — questions? Call us.",
    },
    clear: "clear",
    logo: "Logo",
    noLogo: "No logo yet.",
    uploadLogo: "Upload a logo",
    removeLogo: "Remove the logo",
    logoHint:
      "PNG, JPEG, SVG or WebP up to {{kb}} KB; public at /v1/branding/<company>/logo.",
    preview: "Preview",
    previewOrder: "ORD-2026-0001 · Kinshasa → Matadi",
    previewStatus: "Delivered · today 14:05",
    previewHint:
      "The public tracking page and the driver app read GET /v1/branding/{{slug}} — no sign-in.",
    footer:
      "Notifications keep their wording (Settings › Notifications); nothing here reaches the ERP.",
  },
  security: {
    title: "Security",
    hint: "The driver sign-in and session rules and the request-log retention, over the deployment's defaults; people, roles, two-factor and the password policy are the OneSphere realm's.",
    knobs: "Rules",
    col: {
      knob: "Rule",
      effective: "In force",
      source: "Source",
      own: "The tenant's value",
    },
    knob: {
      driver_otp_ttl_s: "One-time code life (seconds)",
      driver_otp_max_attempts: "Wrong attempts before a new code",
      driver_session_days: "Driver session life (days)",
      request_log_days: "API request logs kept (days)",
    },
    knobHint: {
      driver_otp_ttl_s: "The SMS code a driver types at sign-in.",
      driver_otp_max_attempts:
        "Then the code is spent and the driver asks for another.",
      driver_session_days: "Then the app signs the driver in again.",
      request_log_days: "The /v1 request logs on the Developer screen.",
    },
    source: { tenant: "the tenant's", default: "the default ({{value}})" },
    emptyIsDefault: "An empty field means the default.",
    sessions: "Live driver sessions",
    sessionsHint:
      "Signed-in driver apps with a valid session; signing everyone out ends them now — the apps ask for a code again.",
    signOutAll: "Sign every driver out",
    signedOut: "{{count}} session(s) revoked.",
    devEcho:
      "Dev stack: the one-time code is echoed by the API (no SMS provider).",
    keys: "Active API keys",
    keysHint: "Keys and their scopes, request logs and webhooks:",
    iam: "People, roles, 2FA",
    iamHint:
      "Members, roles (of_* realm roles), two-factor and the password policy are managed in the OneSphere realm.",
    realmLink: "Open the realm account console",
    footer:
      "Every value applies from the next sign-in; live sessions keep the life they were given.",
  },
  integrations: {
    title: "Integrations",
    hint: "Everything the company is plugged into, in one read — each card links to the screen where it is managed.",
    manage: "Manage →",
    tone: { ok: "on", warn: "attention", off: "off", info: "info" },
    yes: "yes",
    no: "no",
    keys: "{{count}} active API key(s)",
    erp: {
      title: "ERP",
      none: "No ERP connection yet.",
      health: "health {{at}}",
      pending: "{{count}} proposal(s) pending approval",
    },
    maps: {
      title: "Maps",
      provider: "Provider",
      tiles: "Tiles",
      key: "API key",
      tenant: "the tenant's",
      defaults: "the deployment's",
    },
    telematics: {
      title: "Telematics",
      gateway: "Gateway",
      devices:
        "{{total}} device(s) · {{active}} active · {{fresh}} with a fix in 24 h",
      events: "{{open}} open device event(s) · last {{at}}",
    },
    fuel: {
      title: "Fuel providers",
      providers: "{{count}} active provider(s)",
      batches: "{{count}} batch(es) · last {{at}}",
    },
    messaging: {
      title: "Messaging",
      "dev-echo": "dev echo",
      platform: "platform services",
      smtp: "SMTP",
      "not-configured": "not configured",
    },
    webhooks: {
      title: "Webhooks & API",
      endpoints:
        "{{total}} endpoint(s) · {{active}} active · {{failing}} failing",
    },
    ai: {
      title: "AI",
      api: "AI API",
      gateway: "Gateway",
      coreOnly: "core only (no AI API)",
    },
    footer:
      "Read-only: the ERP connections are provisioned from OneSphere Administration, the map provider from Admin › Add-ons, the feeds and keys from Developer.",
  },
  customFields: {
    title: "Custom fields",
    hint: "Extra fields on the records — vehicles, drivers, orders, work orders … — typed, optional or required, grouped and ordered on the forms; the values ride on every record as custom_fields.",
    allSubjects: "All records",
    options: "Options",
    create: "Add the field",
    empty: "No custom field yet.",
    confirmRemove:
      "Remove the field {{label}}? The values already stored stay on the records.",
    footer:
      "Label, required, group and order save on blur; the key and the type are fixed once created (a new field for a new type).",
    col: {
      subject: "Record",
      key: "Key",
      label: "Label",
      type: "Type",
      required: "Required",
      group: "Group",
      order: "Order",
    },
    subjects: {
      vehicle: "Vehicle",
      asset: "Asset",
      equipment: "Equipment",
      driver: "Driver",
      fleet: "Fleet",
      vendor: "Vendor",
      contact: "Customer / contact",
      place: "Place",
      order: "Order",
      work_order: "Work order",
      fuel_report: "Fuel report",
      issue: "Issue",
      company: "Company",
    },
  },
  driverAppSettings: {
    title: "Driver app",
    hint: "What the installed driver app and the PWA read from the tenant at sign-in: the tracking cadence, the offline window, the proof rules — read with the driver's profile at sign-in; the app's own defaults apply where the tenant says nothing.",
    tracking: "Tracking and offline",
    interval: "Foreground tracking interval (seconds)",
    offlineDays: "Jobs kept offline (days)",
    navigate: "Navigate with",
    apps: {
      google: "Google Maps",
      waze: "Waze",
      system: "the phone's default",
    },
    proof: "Proof of delivery",
    photoRequired: "A photo is required with every proof",
    proofHint:
      "The order type's POD method (signature, photo, QR, note) still applies; this adds a photo on top.",
    signIn: "Sign-in",
    signInHint:
      "Drivers sign in with their phone and a one-time code (SMS through the platform services; echoed on the dev stack).",
    footer:
      "Background GPS, push and the store pipeline are the Capacitor shell's (WP-U18); these settings travel to it unchanged.",
  },
  notifSettings: {
    title: "Notifications",
    hint: "The templates the rules and the agents send from — an agent names a template and its parameters, never the text — and the company's recent notifications.",
    templatesHint:
      "Every template exists in FR, EN and LN; the language is the recipient's.",
    recent: "Recent notifications",
    none: "Nothing sent yet.",
    col: {
      template: "Template",
      title: "Title",
      params: "Parameters",
      channels: "Channels",
      when: "When",
      channel: "Channel",
      to: "To",
      status: "Status",
    },
    footer:
      "The SMS / WhatsApp channels come with the platform services; e-mail goes through the tenant's SMTP as a proposal.",
    wording: {
      custom: "custom",
      placeholders: "Placeholders",
      body: "Body",
      save: "Save the wording",
      reset: "Back to the default",
    },
  },
  analytics: {
    title: "Dashboards",
    hint: "The fleet's service and cost, computed on request from OneFleet's own records — on-time deliveries, the cost per km from the TCO (fuel, maintenance, depreciation), the fuel consumption, the maintenance backlog. The ERP-owned money joins through the caches.",
    window: "Window",
    days: "{{count}} days",
    since: "since {{day}}",
    openReports: "Reports",
    footer:
      "Computed {{when}} — nothing is stored; the report screen gives the same rows as CSV.",
    kpi: {
      completed: "Deliveries completed",
      onTime: "On time",
      onTimeHint: "completed at or before the scheduled time",
      l100: "L/100 km",
      litres: "{{litres}} L on the trips' km",
      costPerKm: "Cost per km",
      costHint: "TCO {{from}} → {{to}}",
      backlog: "Open work orders",
      backlogHint: "{{count}} older than 30 days",
    },
    charts: {
      onTime: {
        title: "On-time deliveries by day",
        onTime: "on time",
        late: "late",
      },
      cost: {
        title: "Cost per km by vehicle (major units)",
        fuel: "fuel",
        maintenance: "maintenance",
        depreciation: "depreciation",
        other: "other",
        empty:
          "No vehicle with trip km in the window yet — the drivers' odometer readings at the start and the close of a job give the km.",
      },
      fuel: {
        title: "Fuel L/100 km by week",
        l100: "L/100 km",
        litres: "litres",
        empty: "No matched fuel report in the window.",
      },
      backlog: {
        title: "Maintenance backlog by age",
        days: "days",
        oldest: "Oldest: {{title}} ({{days}} days)",
        open: "open the board",
      },
    },
    reports: {
      title: "Reports",
      hint: "The datasets behind the dashboards, for the window you pick, as a table and as CSV — the same numbers Ask OneFleet reads through the analytics tools.",
      openDashboards: "Dashboards",
      rows: "{{count}} row(s)",
      csv: "Download CSV",
      downloading: "Preparing…",
      saved: "Saved {{name}}",
      empty: "No row in this window.",
      truncated: "The first 200 rows are shown; the CSV carries them all.",
      axis: "ERP dimension",
      axes: { cost_center: "Cost centre", project: "Project" },
      names: {
        on_time_by_day: "On-time deliveries by day",
        cost_per_km_by_vehicle: "Cost per km by vehicle",
        fuel_trend_by_week: "Fuel L/100 km by week",
        maintenance_backlog: "Maintenance backlog by age",
        driver_scorecards: "Driver scorecards",
        fuel_exceptions: "Fuel exceptions (audit)",
        nbv_vs_market: "Net book value vs market",
        cost_by_dimension: "Fleet cost by ERP dimension",
      },
      descriptions: {
        on_time_by_day:
          "Completed orders per day: on time (at or before the scheduled time) and late",
        cost_per_km_by_vehicle:
          "The TCO window per vehicle — fuel, maintenance, depreciation, other — over the trips' km",
        fuel_trend_by_week:
          "The fleet's litres and the trips' km per ISO week, the consumption they imply",
        maintenance_backlog:
          "The open work orders with their age since creation, in the 0-7 / 8-30 / 31-90 / 90+ buckets",
        driver_scorecards:
          "The latest computed month per driver — the score, its band and the metrics behind it",
        fuel_exceptions:
          "Every rule finding on a fuel report in the window with its decision — the auditor's list",
        nbv_vs_market:
          "The ERP's net book value of every financed vehicle / asset against the market value kept on the record",
        cost_by_dimension:
          "The TCO window summed by the vehicles' cost centre or project — the ERP's dimension as the axis",
      },
    },
    builder: {
      toggle: "Builder",
      columns: "Columns",
      filters: "Filters (all must hold)",
      addFilter: "filter",
      groupBy: "Group by",
      aggregate: "Aggregate",
      measure: "Measure",
      sort: "Sort",
      reset: "Reset",
    },
    schedules: {
      title: "Scheduled reports",
      hint: "A report with its window (and its builder query when saved with one), daily or weekly, filed as a CSV and sent to a role's inbox by the worker at 06:30.",
      report: "Report",
      cadence: "Cadence",
      daily: "daily",
      weekly: "weekly on",
      weekday: "Weekday",
      role: "Role",
      add: "Add",
      runNow: "Run now",
      ran: "{{count}} report(s) filed",
      lastRun: "last run {{when}}",
      lastFile: "last CSV",
      enable: "Enable",
      disable: "Disable",
      delete: "Delete",
      empty: "No scheduled report yet.",
    },
  },
  alerts: {
    title: "Alerts centre",
    hint: "Every alert the rules raised — documents, hours of service, devices, services and inspections due, zones — by severity and status, with who took it and what resolved it. A known alert is bumped, never duplicated; the monitor runs every day at 06:00 and on demand here.",
    scanResult: "{{total}} finding(s), {{high}} high",
    openTotal: "{{count}} open",
    lastRaised: "last raised {{when}}",
    allKinds: "All kinds",
    total: "{{count}} alert(s)",
    empty: "No alert in this view.",
    times: "seen {{count}} times",
    severity: { high: "high", medium: "medium", low: "low" },
    status: {
      active: "open + acknowledged",
      open: "open",
      acknowledged: "acknowledged",
      resolved: "resolved",
      all: "all",
    },
    col: {
      severity: "Severity",
      alert: "Alert",
      subject: "Subject",
      seen: "Last seen",
      status: "Status",
      kind: "Kind",
    },
    rules: {
      title: "Rules",
      hint: "What the Compliance Monitor checks and when it raises — rules only, no model (D-06); the thresholds and switches are the tenant's (an administrator edits them here).",
      hintEdit:
        "What the Compliance Monitor checks and when it raises — rules only, no model (D-06). Switch a kind off, force its severity, silence its notification, move a threshold; a blank threshold returns to the default. The next run follows.",
      col: {
        rule: "Rule",
        raises: "Raises",
        threshold: "Thresholds",
        tells: "Tells",
      },
      names: {
        documents: "Documents",
        hos: "Hours of service",
        devices: "Telematics devices",
        maintenance: "Services due",
        inspections: "Inspections due",
        zones: "Zones and routes",
        deliveries: "Deliveries",
      },
      thresholds: {
        documents:
          "expired = high; expiring within the soon window = medium; the rest of the horizon = low",
        hos: "a driving or duty limit passed = high; a break due = medium",
        devices:
          "no fix for the silence window = medium, high past the high threshold; a VIN mismatch = high; a fault code = low",
        maintenance:
          "a service overdue by km or date = high (medium with a work order); due soon = low",
        inspections:
          "a form due on a vehicle = low; past its frequency again = medium; never filled or past the high factor = high",
        zones:
          "a no-go zone entered = high (once per zone and day); speeding in a speed zone = medium, high from the day's count; a dwell past the zone's rule = low (medium when the zone asks for an alert); a truck off its job's planned route past the distance and the grace time = high (once per excursion)",
        deliveries:
          "a count at the door that comes up short = high (once per count), naming the missing lines, the truck and the driver",
      },
      thresholdNames: {
        document_soon_days: "soon within",
        document_horizon_days: "horizon",
        device_silent_high_h: "silent → high after",
        inspection_high_factor: "high at",
        speeding_high_count: "high from",
      },
      enabled: "enabled",
      severity: "severity",
      severityRule: "rule's severity",
      notify: "notify",
      tenantValue: "set for this tenant (default {{def}})",
      footer:
        "The Compliance card on the drivers' screens runs the same monitor with the same thresholds; the daily 06:00 run too.",
    },
    custom: {
      heading: "Custom rules on events",
      hint: "When this event happens and these fields look like that, raise an alert of this severity with this title — and tell this role. The relay applies them to every OneFleet-born event.",
      new: "New rule",
      close: "Close",
      name: "Name",
      event: "Event (a type or a glob)",
      when: "Conditions (all must hold)",
      addCondition: "condition",
      value: "value",
      title: "Alert title ({placeholders} from the event)",
      message: "Message (optional)",
      notify: "Tell the role",
      nobody: "nobody",
      oncePer: "One alert per",
      once: {
        event: "event",
        subject: "record (bumped on repeats)",
        day: "record and day",
      },
      sample: "Sample payload (JSON) for a try",
      try: "Try it",
      wouldRaise: "Would raise",
      noMatch: "No match on that sample",
      badJson: "The sample is not valid JSON",
      save: "Save the rule",
      enable: "Enable",
      disable: "Disable",
      delete: "Delete",
      confirmDelete: "Delete the rule “{{name}}”?",
      empty: "No custom rule yet.",
    },
  },
  compliance: {
    title: "Compliance Monitor",
    hint: "Rules only — documents expiring, hours of service, silent or mismatched devices, services due; the run raises the alerts (a known alert is bumped, never duplicated) and notifies the owning roles from templates; the worker runs it every day at 06:00",
    scan: "Scan",
    run: "Run the monitor",
    raised: "{{new}} new alert(s), {{sent}} notification(s)",
    noNotify: "no notification",
    alerts: "Open alerts",
    noAlert: "No open alert.",
    acknowledged: "acknowledged",
    since: "since {{when}}",
    ack: "Acknowledge",
    resolve: "Resolve",
    resolutionPrompt: "What resolved it? (optional)",
    notifications: "My notifications",
    unread: "{{count}} unread",
    noNotification: "Nothing for you yet.",
    markRead: "mark read",
    open: "open",
    kind: {
      document_expired: "document expired",
      document_expiring: "document expiring",
      hos_violation: "HOS violation",
      hos_break_due: "break due",
      device_no_signal: "device silent",
      device_vin_mismatch: "VIN mismatch",
      device_dtc: "fault code",
      maintenance_overdue: "service overdue",
      maintenance_due_soon: "service due soon",
      inspection_overdue: "inspection due",
      no_go_entered: "no-go zone entered",
      speeding: "speeding",
      dwell_exceeded: "dwell exceeded",
      off_route: "off route",
      items_missing: "items missing at delivery",
    },
  },
  bell: {
    title: "Notifications",
    unread: "{{count}} unread",
    allRead: "all read",
    empty: "Nothing for you yet.",
    unavailable: "OneFleet is not answering.",
    all: "All notifications and alerts",
    kind: {
      document_expired: "document expired",
      document_expiring: "document expiring",
      hos_violation: "HOS violation",
      hos_break_due: "break due",
      device_no_signal: "device silent",
      device_vin_mismatch: "VIN mismatch",
      device_dtc: "fault code",
      maintenance_overdue: "service overdue",
      maintenance_due_soon: "service due soon",
      inspection_overdue: "inspection due",
      no_go_entered: "no-go zone entered",
      speeding: "speeding",
      dwell_exceeded: "dwell exceeded",
      off_route: "off route",
      items_missing: "items missing at delivery",
    },
  },
  routeAdvisor: {
    title: "Route Advisor",
    hint: "The plan read as you — the day served one trip at a time against the plan, each route's load, the orders left out, what to check before applying; the apply is a proposal a second dispatcher decides",
    run: "Ask the advisor",
    propose: "File the apply proposal",
    before: "One trip per order",
    beforeHint: "{{count}} order(s), straight line (lower bound)",
    beforeHintRoad: "{{count}} order(s), one trip each, road distance",
    after: "The plan",
    afterHint:
      "{{routes}} route(s) · {{orders}} order(s) · {{unassigned}} unplanned",
    saving: "Saving",
    utilisation: "utilisation {{pct}} %",
    applyProposal: "apply (W3)",
    kind: {
      plan_failed: "plan failed",
      plan_not_computed: "not computed",
      fallback_plan: "heuristic plan",
      unassigned_share: "many unplanned",
      unassigned_order: "unplanned",
      skipped_order: "left out",
      vehicles_without_driver: "no driver",
      route_near_capacity: "near capacity",
      route_beyond_day: "beyond the day",
      idle_vehicles: "idle vehicles",
      single_stop_routes: "single-stop routes",
      past_day: "past day",
      apply_ready: "ready to apply",
      proposal_pending: "proposal pending",
      applied: "applied",
      trip_variance: "beyond the plan",
      trip_readings_suspect: "readings suspect",
    },
    col: {
      vehicle: "Vehicle",
      driver: "Driver",
      orders: "Orders",
      time: "Time",
      load: "Load",
    },
  },
  fuelAnalyst: {
    title: "Fuel Analyst",
    hint: "Open anomalies per vehicle, consumption drift, the matching queue, supplier batches ready, allocation runs to propose — the rules find, the model narrates; every write is a proposal under dual control",
    run: "Ask the analyst",
    narrative: "Explain this preview",
    explain: "Explain",
    kind: {
      anomalies: "anomalies",
      consumption_drift: "drift",
      fuel_beyond_trips: "fuel beyond the trips",
      trip_consumption: "consumption on trip km",
      matching_stale: "queue ageing",
      unclaimed_transactions: "unclaimed lines",
      batch_ready: "batch ready",
      allocation_previewed: "run previewed",
      allocation_unallocated: "unallocated",
    },
    action: {
      "fuel.propose_batch": "purchase invoice (ERP)",
      "allocation.propose_run": "allocation journal (ERP)",
    },
    col: { vehicle: "Vehicle", costKm: "Cost / km", reports: "Reports" },
  },
  printed: {
    heading: "Printed documents",
    intro:
      "The papers a fleet puts on a desk: the waybill a driver carries, the job card on the workshop bench, the hand-over note for a truck. Each one ships with correct wording — change it only where your own words belong.",
    readOnly: "Changing the wording needs the settings permission.",
    shipped: "As shipped",
    yours: "Your words",
    edit: "Edit",
    close: "Close",
    title: "Title",
    body: "Body (Markdown)",
    save: "Save",
    reset: "Back to the shipped one",
    copyShipped: "Start from the shipped one",
    placeholders: "What you may use",
    hint: "Click one to add it. The API refuses anything else when you save, so a document never fails while somebody is waiting to sign it.",
    blockHint:
      "A table built for you — place it, and decide what stands around it",
    print: "Print",
    printHint: "Opens the page your browser prints.",
    subject: {
      order: "Printed from an order",
      work_order: "Printed from a work order",
      vehicle: "Printed from a vehicle",
    },
    kind: {
      "order.waybill": "Waybill",
      "work_order.job_card": "Job card",
      "vehicle.handover": "Hand-over note",
    },
  },
  carriers: {
    title: "Another fleet",
    hint: "Give this job to a carrier when none of our own trucks can take it.",
    price: "Price it",
    quoted: "They quote {{amount}}, about {{minutes}} minutes.",
    handOver: "Hand it over",
    note: "Anything they should know (optional)",
    track: "Their tracking page",
    takeBack: "Take it back",
    takeBackHint: "We ask them; the order stays where they left it.",
    unmapped: "a word we do not know",
    state: {
      requested: "Asked",
      accepted: "Accepted",
      collected: "Collected",
      delivered: "Delivered",
      cancelled: "Given back",
      failed: "Failed",
    },
  },
  comments: {
    title: "Notes",
    count: "{{count}} note",
    count_other: "{{count}} notes",
    placeholder: "What should the next person to open this record know?",
    hint: "Everyone who can open this record reads these.",
    add: "Add a note",
    empty: "No note on this record yet.",
    someone: "Somebody",
    edit: "Edit",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    pin: "Pin",
    unpin: "Unpin",
    pinned: "Pinned",
    edited: "edited",
    sendToCustomer: "The customer will read this",
    toCustomerHint: "This note appears in the customer's portal.",
    toCustomer: "sent to the customer",
    fromCustomer: "from the customer",
  },
  dimensions: {
    saved: "Saved â but the ERP will not take these codes",
    fix: "Fix the code",
    continue: "Go on anyway",
    of: { cost_center: "Cost centre", project: "Project", site: "Site" },
  },
  assistant: {
    title: "Ask OneFleet",
    subtitle:
      "Reads the fleet as you — tools on the live data, evidence, no writes without a proposal",
    prompts: {
      q1: "Late orders today",
      q2: "Which orders are unassigned?",
      q3: "Who is online?",
      q4: "KPIs this week",
    },
  },
};

export const fleetFr: typeof fleetEn = {
  title: "Flotte",
  subtitle:
    "Véhicules, chauffeurs, dispatch, maintenance, carburant et coûts — OneFleet, branché sur l'ERP",
  overview: {
    sections: "Sections de la flotte",
    register: "Registre de la flotte",
    registerHint:
      "Celui de OneFleet : ce que la flotte a fait sur trente jours, et les véhicules avec lesquels elle l'a fait. L'ERP porte l'immobilisation à laquelle un véhicule est relié ; le véhicule, lui, vit ici.",
    kpi: {
      completed: "Livraisons terminées",
      since: "depuis le {{day}}",
      onTime: "À l'heure",
      l100: "L / 100 km",
      litres: "{{litres}} litres",
      costPerKm: "Coût au km",
      workOrders: "Ordres de travail ouverts",
      backlog: "{{count}} de plus de 30 jours",
    },
    table: {
      vehicle: "Véhicule",
      type: "Type",
      status: "Statut",
      odometer: "Compteur",
      erpAsset: "Immobilisation ERP",
      none: "Aucun véhicule dans cette société",
      all: "Les {{count}} véhicules",
    },
    open: "Ouvrir",
    comingSoon: "{{count}} écrans prévus",
  },
  screens: {
    dispatch: {
      board: "Tableau",
      orders: "Commandes",
      runs: "Tournées",
      trips: "Trajets",
      map: "Carte en direct",
      rates: "Tarifs de service",
      serviceAreas: "Zones de service",
      quotes: "Devis",
    },
    vehicles: {
      list: "Véhicules",
      fleets: "Flottes",
      assets: "Équipements",
      warranties: "Garanties",
      telematics: "Boîtiers télématiques",
    },
    drivers: {
      list: "Chauffeurs",
      schedule: "Planning",
      templates: "Modèles de poste",
      availability: "Disponibilités",
      timeOff: "Absences",
      scorecards: "Scores",
    },
    maintenance: {
      workOrders: "Ordres de travail",
      schedules: "Échéanciers",
      inspections: "Inspections",
      issues: "Signalements",
      parts: "Stock de pièces",
    },
    fuel: {
      reports: "Rapports carburant",
      transactions: "Transactions",
      matching: "Rapprochement",
      anomalies: "Anomalies",
      allocation: "Ventilation des coûts",
    },
    partners: {
      customers: "Clients",
      contacts: "Contacts",
      vendors: "Fournisseurs",
      places: "Lieux",
    },
    analytics: { dashboards: "Tableaux de bord", reports: "Rapports" },
    erp: {
      status: "État",
      connections: "Connexions",
      capabilities: "Capacités",
      sync: "Synchronisation",
      reconcile: "Rapprochement",
      mappings: "Correspondances",
      deadLetter: "Rejets",
    },
    copilot: {
      proposals: "Propositions",
      sessions: "Sessions",
      tasks: "Tâches",
      policies: "Règles",
      alerts: "Alertes",
    },
    settings: {
      general: "Général",
      orderTypes: "Types de commande et workflows",
      customFields: "Champs personnalisés",
      driverApp: "Application chauffeur",
      notifications: "Notifications",
      documents: "Documents imprimés",
      branding: "Identité visuelle",
      ai: "IA",
      integrations: "Intégrations",
      security: "Sécurité",
      developer: "Développeur",
    },
  },
  placeholder: {
    title: "Écran prévu",
    body: "Cet écran est livré par OneFleet {{wp}}. Sa maquette et ses étapes de recette sont dans la conception UI et les scripts UAT OneFleet.",
    section: "{{count}} écrans dans cette section",
  },
  status: {
    title: "Services OneFleet",
    hint: "Joints par la passerelle OneSphere en /fleet-api et /fleet-rt ; l'API IA répond sur /fleet-ai à partir de WP-A1",
    api: "API",
    realtime: "Temps réel",
    postgres: "Postgres",
    valkey: "Valkey",
    checking: "Vérification…",
    up: "En service",
    ready: "Prêt",
    degraded: "Dégradé",
    down: "Injoignable",
    version: "Version {{version}} · {{sha}}",
    socket: {
      idle: "Inactif",
      connecting: "Connexion…",
      connected: "Connecté",
      auth_required: "Connecté — l'authentification arrive avec WP-B2",
      disconnected: "Déconnecté",
      error: "Injoignable",
    },
    mock: "Mode simulation : les réponses viennent du navigateur, pas de la pile OneFleet",
    identity: "Identité",
    identityError: "OneFleet n'a pas accepté le jeton de session",
    identityNoCompany:
      "Connecté, mais sans périmètre société : demandez un groupe /Scope/Company à un administrateur",
    permissions: "permissions",
    worker: "Worker",
    workerSeen: "Battement il y a {{age}} s",
    workerStale: "Dernier battement il y a {{age}} s — périmé",
    workerNone: "Aucun battement",
    workerRelay: "relais outbox actif",
    logs: "Journaux",
    restart: "Redémarrer",
    logsOf: "Dernières lignes de {{service}}",
    logsNone:
      "Rien d'expédié encore — le service écrit sa fin de journal toutes les deux secondes.",
    restartConfirm:
      "Redémarrer le service {{service}} ? Il s'arrête et la politique du conteneur le relance en quelques secondes.",
    restartRequested:
      "Redémarrage de {{service}} demandé — le voyant passe au rouge, puis au vert.",
    ai: "API IA",
    aiGateway: "Passerelle debout · {{on}}/{{total}} voies avec clé",
    aiGatewayDown: "API IA debout, passerelle injoignable",
  },
  vehicles: {
    search: "Rechercher",
    searchHint: "plaque, VIN, marque, modèle",
    filterStatus: "État",
    allStatuses: "Tous les états",
    total: "{{count}} véhicules",
    plate: "Plaque",
    vehicle: "Véhicule",
    type: "Type",
    statusLabel: "État",
    odometer: "Compteur",
    costCenter: "Centre de coût",
    empty:
      "Aucun véhicule ne correspond — le registre se remplit depuis OneFleet (just seed) ou depuis les Assets ERPNext (WP-B7)",
    next: "Page suivante",
    first: "Première page",
    status: {
      active: "Actif",
      maintenance: "En maintenance",
      inactive: "Inactif",
      retired: "Retiré",
      needs_review: "À vérifier",
    },
    types: {
      truck: "Camion",
      van: "Fourgon",
      car: "Voiture",
      pickup: "Pick-up",
      bus: "Bus",
      motorcycle: "Moto",
      trailer: "Remorque",
      forklift: "Chariot élévateur",
      other: "Autre",
    },
    fuel: {
      diesel: "Diesel",
      petrol: "Essence",
      electric: "Électrique",
      hybrid: "Hybride",
      lpg: "GPL",
    },
    erpAsset: "Actif ERP",
    new: "Nouveau véhicule",
    pull: "Importer les actifs ERP",
    pulling: "Import…",
    pullHint:
      "Lit les Assets et Vehicles de {{name}} et les relie par id OneFleet, VIN puis plaque ; les inconnus arrivent « à vérifier ». Rien n'est écrit dans l'ERP.",
    form: {
      newTitle: "Nouveau véhicule",
      editTitle: "Modifier {{plate}}",
      back: "Retour",
      locked:
        "Créer et modifier un véhicule demande le rôle gestionnaire de flotte ou administrateur.",
      identity: "Identité",
      vin: "VIN",
      internalId: "Id interne",
      make: "Marque",
      model: "Modèle",
      year: "Année",
      displayName: "Nom affiché",
      fuel: "Carburant",
      usage: "Usage et comptabilité",
      capacityWeight: "Capacité (kg)",
      capacityVolume: "Capacité (l)",
      project: "Projet",
      odometerHint:
        "Le compteur n'avance que vers l'avant : une valeur plus basse est ignorée (la télématique et les pleins l'alimentent aussi).",
      erpTitle: "ERP",
      erpOn:
        "Connecté à {{name}} : l'enregistrement dépose une proposition de miroir en Vehicle ERPNext. Une personne l'approuve sur la fiche 360 ou le Hub ERP — rien n'est écrit avant.",
      erpOff:
        "Aucune connexion ERP active : le véhicule reste dans OneFleet seul tant qu'une connexion n'est pas provisionnée depuis Administration › Add-ons.",
      saving: "Enregistrement…",
      save: "Enregistrer",
      create: "Créer le véhicule",
      cancel: "Annuler",
    },
  },
  erp: {
    title: "Hub ERP",
    hint: "Deux voies ERP par société — ERPNext et Oracle Fusion — derrière un seul port ; les modules s'activent par société ici",
    tracks: "Voies ERP",
    erpnext: "ERPNext — le site OneSphere (erp.localhost)",
    fusion:
      "Oracle Fusion — doublure WireMock jusqu'à la désignation d'un pod projet",
    provisioned:
      "Les connexions sont provisionnées depuis Administration › Extensions d'OneSphere et listées sous Connexions",
    openAdmin: "Ouvrir Administration › Extensions",
    conn: {
      title: "Connexions ERP",
      hint: "Une connexion par ERP ; les lectures sont libres, chaque écriture vers l'ERP est une proposition qu'une personne approuve",
      none: "Aucune connexion — provisionnez-en une depuis Administration › Extensions",
      provisionedFrom: "provisionnée depuis {{from}}",
      healthy: "Opérationnelle",
      unhealthy: "En défaut",
      webhooks: "{{count}} webhooks",
      noWebhooks: "Pas de webhooks",
      poll: "interroge toutes les {{every}} min · dernier {{at}}",
      pollOff: "interrogation arrêtée",
      pollHint:
        "Pas de webhooks sur ce système : le worker tire ce qui a changé depuis le dernier passage (LastUpdateDate).",
      pollEnable: "Activer l'interrogation",
      pollDisable: "Arrêter l'interrogation",
      lastHealth: "Dernier contrôle {{at}}",
      test: "Tester",
      links: "{{count}} liens",
      inbound: "{{count}} événements reçus",
      pending: "{{count}} propositions en attente",
    },
    pull: {
      run: "Synchroniser",
      running: "Synchronisation…",
      assets: "Immobilisations (véhicules)",
      vehicles: "Véhicules (HRMS)",
      drivers: "Employés → chauffeurs",
      customers: "Clients",
      suppliers: "Fournisseurs",
      dimensions: "Centres de coût, projets, sites",
    },
    proposals: {
      title: "Écritures ERP proposées",
      count: "{{count}} en attente",
      hint: "Rien n'est écrit dans l'ERP sans une décision ici (ou depuis la boîte des propositions)",
      empty: "Aucune proposition en attente",
      by: "proposée par {{who}}",
      dualControl: "double contrôle",
      approve: "Approuver et écrire",
      reject: "Rejeter",
    },
    form: {
      name: "Nom de la connexion",
      erpCompany: "Société ERP (le groupe /Scope/Company)",
      baseUrl: "URL ERPNext (telle que les services OneFleet la joignent)",
      site: "Site / en-tête Host",
      apiKey: "Clé API de l'utilisateur d'intégration",
      apiSecret: "Secret API de l'utilisateur d'intégration",
      registerWebhooks:
        "Enregistrer maintenant les webhooks OneFleet sur le site ERP (une écriture ERP — le bridge crée les documents Webhook)",
      submit: "Provisionner la connexion",
      done: "Connexion {{id}} provisionnée — voir le Hub ERP",
      hint: "Les identifiants sont scellés à l'arrivée et jamais renvoyés ; le test de connexion, les capacités et la lecture des dimensions s'exécutent aussitôt (lectures)",
      system: "Voie ERP",
      systems: { erpnext: "ERPNext (cet OneSphere)", fusion: "Oracle Fusion" },
      fusion: {
        podUrl: "URL du pod Fusion (https://<pod>.fa.<région>.oraclecloud.com)",
        username: "Utilisateur d'intégration",
        password: "Mot de passe",
        businessUnit: "Unité opérationnelle",
        faBook: "Livre des immobilisations",
        costCenterSet: "Code du jeu de valeurs centre de coûts",
        vehicleItem: "Article véhicule",
        transactionSource: "Source de transaction",
        siteDefault: "Site / emplacement par défaut",
        driverJobs: "Intitulés de poste chauffeur (séparés par des virgules)",
        hint: "L'API REST du pod est lue à la connexion (société, unités, livres — lectures seules) ; toute écriture vers un pod Fusion reste une proposition qu'une personne approuve sur le pod nommé (D-07). Les pods de production se saisissent ici, jamais dans un fichier .env.",
      },
    },
  },
  orders: {
    search: "Recherche",
    searchHint: "id interne, numéro de suivi, notes",
    filterStatus: "Statut",
    allStatuses: "Tous les statuts",
    summary:
      "{{total}} commandes · {{active}} actives · {{unassigned}} non affectées",
    new: "Nouvelle commande",
    order: "Commande",
    route: "Trajet",
    statusLabel: "Statut",
    driver: "Chauffeur",
    scheduled: "Planifiée",
    created: "créée",
    distance: "Distance",
    billingLabel: "Facturation",
    empty:
      "Aucune commande — créez-en une, ou lancez just seed pour les commandes d'exemple DLG",
    unassigned: "Non affectée",
    priority: "priorité {{n}}",
    stops: "{{count}} arrêts",
    tracking: "Suivi",
    actions: "Actions",
    assignDriver: "Affecter un chauffeur",
    assignVehicle: "Véhicule",
    driversVehicle: "Celui du chauffeur — {{plate}}",
    noDriversVehicle: "— le chauffeur n'a pas de véhicule —",
    suggestDriver: "Suggérer un chauffeur",
    directions: "Itinéraire",
    directionsCount: "{{count}} manœuvres · {{distance}} · {{time}}",
    directionsNone:
      "Pas d'étapes pour cet itinéraire (fournisseur ligne droite) — passez le fournisseur de carte sur OSRM ou Google.",
    openGoogleMaps: "Ouvrir dans Google Maps",
    directionsHint:
      "Calculé par {{provider}} au routage de la commande ; l'app chauffeur lit les mêmes étapes sur /v1/orders/:id/directions.",
    turns: {
      depart: "Partir sur {{road}}",
      arrive: "Arrivée à l'arrêt",
      roundabout: "Au rond-point, prendre la sortie {{exit}} vers {{road}}",
      merge: "S'insérer sur {{road}}",
      fork: "Rester sur {{road}}",
      continue: "Continuer sur {{road}}",
      turn: {
        left: "Tourner à gauche sur {{road}}",
        right: "Tourner à droite sur {{road}}",
        "slight left": "Serrer à gauche sur {{road}}",
        "slight right": "Serrer à droite sur {{road}}",
        "sharp left": "Tourner franchement à gauche sur {{road}}",
        "sharp right": "Tourner franchement à droite sur {{road}}",
        uturn: "Faire demi-tour vers {{road}}",
      },
    },
    suggesting: "Classement…",
    suggestSource: "règle",
    suggestHint:
      "classés par disponibilité, distance à l'enlèvement, charge et véhicule — l'affectation reste votre clic",
    noCandidates: "Aucun chauffeur disponible.",
    assignAndDispatch: "Affecter et dispatcher",
    reassign: "Réaffecter",
    quickAssign: "Affecter",
    noEvents: "Aucun événement possible depuis cet état",
    eventNote: "Note pour le prochain événement (facultatif)",
    waypoints: "Étapes",
    eta: "ETA",
    payload: "Chargement",
    noEntities: "Aucun article sur le chargement",
    entity: "Article",
    qty: "Qté",
    weight: "kg",
    item: "Article ERP",
    price: "Prix",
    orderPrice: "Prix de la commande",
    costCenter: "Centre de coûts",
    project: "Projet",
    site: "Site",
    pod: "Preuve de livraison",
    podNone: "Non requise",
    cod: "Paiement à la livraison",
    activity: "Activité",
    internal: "interne",
    erp: "Documents ERP",
    noErpDocs:
      "Pas encore de document ERP — la clôture de la commande propose le bon de livraison et le brouillon de facture",
    erpHint:
      "Les écritures ERP sont des propositions qu'une personne approuve dans Copilote › Propositions",
    openProposals: "Ouvrir les propositions",
    openErp: "Ouvrir dans ERPNext",
    open: "Ouvrir la commande",
    notes: "Notes",
    straightLine: "ligne droite",
    status: {
      created: "Créée",
      dispatched: "Dispatchée",
      started: "Démarrée",
      picked_up: "Enlevée",
      en_route: "En route",
      completed: "Livrée",
      canceled: "Annulée",
    },
    billing: {
      unbilled: "Non facturée",
      draft: "Brouillon",
      invoiced: "Facturée",
      paid: "Payée",
    },
    events: {
      dispatch: "Dispatcher",
      unassign: "Désaffecter",
      start: "Démarrer",
      pickup: "Enlevée",
      next: "Étape suivante",
      complete: "Clôturer",
      cancel: "Annuler",
    },
    kind: {
      pickup: "Enlèvement",
      dropoff: "Livraison",
      stop: "Arrêt",
      return: "Retour",
    },
    wpStatus: {
      pending: "À venir",
      current: "En cours",
      arrived: "Arrivé",
      completed: "Terminée",
      skipped: "Sautée",
    },
    proof: {
      signature: "Signature",
      photo: "Photo",
      qr: "Scan QR",
      note: "Note",
      any: "Au choix",
    },
    proofs: "Preuves",
    proofUnavailable: "indisponible",
    placeType: {
      depot: "Dépôt",
      customer_site: "Site client",
      warehouse: "Entrepôt",
      fuel_station: "Station-service",
      workshop: "Atelier",
      port: "Port",
      other: "Lieu",
    },
    form: {
      type: "Type de commande",
      internalId: "Id interne",
      customer: "Client",
      customerHint: "rechercher les clients (contacts liés à l'ERP)",
      placeHint: "rechercher un lieu ou saisir une adresse",
      searching: "Recherche…",
      noPlace: "Aucun lieu ni adresse trouvé",
      address: "adresse",
      geocoded: "géocodée",
      change: "changer",
      priority: "Priorité (0–9)",
      driver: "Chauffeur",
      assignLater: "Affecter plus tard",
      addEntity: "ajouter un article",
      entityHint: "ce qui est transporté",
      remove: "retirer",
      preview: "Aperçu du trajet",
      previewHint:
        "Choisissez un enlèvement et une livraison pour prévisualiser le trajet via le fournisseur de cartes de la société",
      time: "Durée",
      provider: "Fournisseur",
      checkDropoff: "une livraison est définie",
      checkCustomer: "un client lié à l'ERP (nécessaire au bon de livraison)",
      checkItems:
        "les articles portent un article ERP (ou l'article de service du profil)",
      checkDriver: "un chauffeur est affecté maintenant (facultatif)",
      create: "Créer la commande",
      creating: "Création…",
      quote: {
        title: "Devis",
        hint: "Tarifer les étapes et les colis selon les tarifs de service du tenant ; créer avec un tarif donne à la commande son prix d'achat — les lignes de facture ERP en viennent.",
        ask: "Obtenir un devis",
        asking: "Tarification…",
        none: "Aucun tarif ne s'applique à ces étapes — la commande peut être créée sans prix.",
        notApplicable: "non applicable",
        accept: "Créer avec ce tarif",
      },
      parseBadge: "brouillon règles + modèle · vous confirmez",
      parsedRule: "brouillon des règles · vous confirmez",
      parsedAi: "brouillon IA + règles · vous confirmez",
      describe: "Décrire la commande",
      describeHint:
        "une phrase — ou collez l'e-mail ou le message WhatsApp du client — FR / EN / LN ; le copilote remplit le formulaire, vous vérifiez et créez",
      fromMessage: {
        email: "Lu depuis un e-mail",
        whatsapp: "Lu depuis un message WhatsApp",
        dropped:
          "{{count}} ligne(s) écartée(s) (en-têtes, fil cité, signature)",
        sender: "l'expéditeur est devenu le client",
      },
      describePlaceholder:
        "Livrer 12 cartons de médicaments du Dépôt Limete à l'Hôpital Kintambo demain à 8h, urgent, signature",
      parse: "Lire",
      parsing: "Lecture…",
      read: "Lu",
    },
  },
  board: {
    counts:
      "{{active}} actives · {{unassigned}} non affectées · {{vehicles}} véhicules sur la carte",
    selectHint:
      "Sélectionnez une commande dans une colonne ou sur la carte pour voir son résumé, affecter un chauffeur et déclencher l'événement suivant",
  },
  lanes: {
    view: { board: "Tableau", lanes: "Couloirs" },
    day: "Jour",
    prev: "Jour précédent",
    next: "Jour suivant",
    today: "Aujourd'hui",
    counts:
      "{{orders}} commandes sur la journée · {{drivers}} chauffeurs · les 200 dernières commandes",
    empty: "Aucun chauffeur ni commande ce jour-là",
  },
  map: {
    onlineOnly: "En ligne seulement",
    zones: "Zones ({{count}})",
    counts:
      "{{vehicles}} véhicules · {{drivers}} chauffeurs · {{online}} en ligne",
    online: "En ligne",
    offline: "Hors ligne",
    empty:
      "Pas encore de position — l'application chauffeur, un boîtier GPS ou une saisie manuelle place un véhicule",
    track: "Trace : {{count}} points sur les 6 dernières heures",
    source: {
      device: "boîtier",
      app: "app chauffeur",
      manual: "manuel",
      provider: "fournisseur",
    },
  },
  erpCard: {
    title: "ERP",
    sync: {
      linked: "Relié",
      pending: "En attente",
      proposed: "Proposé",
      conflict: "Conflit",
      error: "Erreur",
      not_linked: "Non relié",
    },
    pending: "{{count}} à décider",
    lastSynced: "Dernière synchro {{when}}",
    openHub: "Hub ERP",
    showRecord: "La fiche",
    hideRecord: "Masquer la fiche",
    readAt: "Lue sur l'ERP à {{when}}",
    noConnection:
      "Aucune connexion ERP active — à provisionner depuis Administration › Add-ons.",
    capabilityOff: "Désactivé sur cette connexion : {{capability}}",
    notLinkedHint:
      "Pas encore relié à un document ERP — « Importer les actifs ERP » relie par id OneFleet, VIN ou plaque ; un miroir est proposé à la création ou à la modification.",
    proposedHint:
      "Une proposition de miroir de cette fiche attend une décision ci-dessous.",
    money: {
      title: "Comptabilité (lue depuis l'ERP)",
      cost: "Coût brut",
      nbv: "Valeur nette comptable",
      accumulated: "Amortissement cumulé",
      method: "Méthode",
      usefulLife: "Durée d'utilité",
      months: "{{count}} mois",
      purchased: "Acheté le",
      costCenter: "Centre de coût",
      assetStatus: "État de l'actif",
      insurer: "Assureur",
      policy: "Police",
      insuredValue: "Valeur assurée",
      insuranceWindow: "Couverture",
      owner: "Propriété de",
      leaseEnds: "Fin de location",
    },
    noMoney:
      "Pas encore de valeurs comptables — elles arrivent avec le lien Asset (import ou webhook) et le rafraîchissement nocturne.",
    asOf: "au {{when}}",
    proposals: "Propositions",
    proposalsHint:
      "chaque écriture ERP est une proposition qu'une personne décide",
    noProposals: "Aucune proposition sur cette fiche.",
    waitingDecision: "En attente de la finance / d'un administrateur",
    approval: {
      pending: "En attente",
      approved: "Approuvée",
      executed: "Exécutée",
      rejected: "Rejetée",
      failed: "Échouée",
      expired: "Expirée",
    },
    capabilities: {
      "assets.vehicle.write": "Miroir en Vehicle ERP",
      "links.write": "Poser l'id OneFleet sur le document ERP",
      "assets.lifecycle.write": "Action de cycle de vie",
      "hr.driver.write": "Miroir en Driver ERP",
      "sales.delivery.write": "Bon de livraison",
      "sales.invoice_draft.write": "Brouillon de facture",
      "sales.trip.write": "Tournée de livraison",
    },
    sandbox: "bac à sable",
    healthy: "joignable",
    unhealthy: "injoignable",
  },
  asset360: {
    tabs: {
      label: "Sections de l'actif",
      overview: "Vue d'ensemble",
      lifecycle: "Cycle de vie & ERP",
      activity: "Activité",
    },
    assignment: "Affectation",
    assignedTo: "Monté sur",
    unassigned: "Non affecté",
    lastSeen: "Dernière position",
    noWarranty: "Aucune garantie sur cet actif",
  },
  vehicle360: {
    back: "Véhicules",
    edit: "Modifier",
    updated: "mis à jour {{when}}",
    online: "En ligne",
    offline: "Hors ligne",
    tabs: {
      label: "Véhicule",
      overview: "Vue d'ensemble",
      telematics: "Télématique",
      maintenance: "Maintenance",
      fuel: "Carburant",
      documents: "Documents",
      lifecycle: "Cycle de vie & ERP",
      activity: "Activité",
    },
    documents: "Papiers du véhicule",
    documentsHint:
      "Assurance, contrôle technique, certificat d'émissions, vignette, licence de transport — ce que le véhicule doit porter et renouveler.",
    documentsOwner: "Le registre de OneFleet",
    documentsOwnerBody:
      "Aucun ERP ne modélise ces papiers (le véhicule d'ERPNext n'a qu'une date de « dernier contrôle carbone ») : OneFleet les garde et le moniteur de conformité les surveille.",
    spec: "Spécification",
    engineHours: "Heures moteur",
    usage: "Compteur et usage",
    lastPosition: "Dernière position",
    near: "Près de",
    speed: "Vitesse",
    source: {
      device: "boîtier",
      app: "app chauffeur",
      manual: "manuel",
      provider: "fournisseur",
    },
    orders: "Commandes",
    ordersActive: "{{count}} en cours",
    noOrders: "Aucune commande affectée à ce véhicule pour l'instant.",
    noPosition:
      "Pas encore de position — un boîtier (D-21), l'app chauffeur ou une saisie manuelle sur la carte en direct le place sur la carte.",
    openLifecycle: "Comptabilité, liens et propositions",
    devices: "Boîtiers",
    deviceActive: "actif",
    deviceInactive: "inactif",
    obd: "OBD-II",
    lastFix: "dernier point",
    noDevices:
      "Aucun boîtier installé — les positions viennent de l'app chauffeur ou des saisies manuelles.",
    devicesHint:
      "Les boîtiers GPS / OBD-II indépendants remontent par la passerelle Traccar (D-21, D-24) ; le point d'un boîtier est la position de référence du véhicule.",
    track24h: "Trace (24 h)",
    trackPoints: "{{count}} points enregistrés",
    obdPanel: "Moteur (OBD-II)",
    obdFields: {
      rpm: "Régime",
      coolant: "Liquide de refroidissement",
      coolantTemp: "Liquide de refroidissement",
      fuelLevel: "Niveau carburant",
      engineLoad: "Charge moteur",
      battery: "Batterie",
      batteryLevel: "Batterie",
      power: "Alimentation",
      engineHours: "Heures moteur",
      obdOdometer: "Compteur OBD",
      vin: "VIN",
    },
    sensorHistory: "Historique OBD",
    sensorWindow: "dernières {{hours}} h",
    sensorPoints: "{{count}} relevés",
    dtcs: "Codes défaut :",
    dtcsIssue: "le signalement maintenance",
    vinMismatch:
      "Le boîtier annonce le VIN {{reported}}, celui du véhicule est {{vehicle}} — le traceur est-il dans le bon camion ?",
    deviceEvents: "Événements du boîtier",
    eventKinds: {
      no_signal: "Sans signal",
      signal_restored: "Signal revenu",
      dtc: "Codes défaut",
      vin_mismatch: "VIN différent",
      provisioned: "Provisionné",
      provider: "Passerelle",
    },
    eventOpen: "ouvert",
    silentSince: "silencieux depuis {{when}}",
    noDeviceEvents: "Aucun événement sur ce boîtier.",
    fuelUsual: "Consommation habituelle",
    fuelLitres: "Litres (derniers rapports)",
    fuelSpend: "Dépense",
    fuelReports: "Rapports",
    fuelConsumption: "L/100 km",
    noFuel: "Aucun rapport carburant pour ce véhicule.",
    fuelHint:
      "La consommation se calcule entre deux pleins complets ; l'habituelle est la moyenne glissante des six derniers. Saisissez un plein depuis l'écran Carburant ou l'appli chauffeur.",
    lifecycle: "Cycle de vie",
    lifecycleStates: {
      planned: "Planifié",
      acquired: "Acquis",
      in_service: "En service",
      under_maintenance: "En maintenance",
      out_of_service: "Hors service",
      for_disposal: "À céder",
      disposed: "Cédé / retiré",
    },
    lifecycleHint:
      "OneFleet tient le cycle de vie opérationnel et ses preuves ; l'ERP tient les livres — transfert, réévaluation et cession sont des propositions qu'il écrit une fois approuvées.",
    ownership: "Qui modifie quoi",
    ownsOneFleet:
      "spécification, compteur, état, télématique, documents, travaux",
    ownsErp:
      "coût, valeur nette comptable, amortissement, transferts, cession, stock, achats",
    activity: "Activité",
    noActivity: "Aucun événement enregistré pour ce véhicule.",
    events: {
      "vehicle.created": "Créé",
      "vehicle.updated": "Modifié",
      "vehicle.deleted": "Supprimé",
      "vehicle.restored": "Restauré",
      "vehicle.linked": "Relié à l'ERP",
      "vehicle.online_changed": "Changement d'état en ligne",
      "vehicle.location_changed": "Position",
      "geofence.entered": "Entrée dans une zone",
      "geofence.exited": "Sortie d'une zone",
      "geofence.dwell": "Stationne dans une zone",
      "geofence.speeding": "Excès de vitesse dans une zone",
    },
  },
  drivers: {
    search: "Rechercher",
    searchHint: "nom, téléphone, permis, identifiant interne",
    filterStatus: "Statut",
    filterLive: "État en direct",
    allLive: "Tous les états",
    total: "{{count}} chauffeurs",
    expiring: "{{count}} permis expirant ou expiré(s)",
    new: "Nouveau chauffeur",
    empty: "Aucun chauffeur ne correspond.",
    driver: "Chauffeur",
    liveState: "État en direct",
    vehicle: "Véhicule",
    lastSeen: "Vu pour la dernière fois",
    erpEmployee: "Employé / Chauffeur ERP",
    status: { active: "Actif", inactive: "Inactif", suspended: "Suspendu" },
    live: {
      available: "Disponible",
      busy: "Occupé",
      off_duty: "Hors service",
      unknown: "Inconnu",
    },
    licence: {
      title: "Permis",
      expired: "expiré depuis {{days}} j",
      expiresIn: "expire dans {{days}} j",
    },
    form: {
      newTitle: "Nouveau chauffeur",
      editTitle: "Modifier {{name}}",
      hint: "Le numéro de téléphone est l'identifiant de l'application chauffeur (code à usage unique). Avec une connexion ERP dont la règle hr.driver.write est active, l'enregistrement dépose une proposition de miroir dans l'ERP — une personne l'approuve (D-06).",
      create: "Créer le chauffeur",
      identity: "Identité",
      name: "Nom",
      phone: "Téléphone (E.164)",
      email: "E-mail",
      internalId: "Identifiant interne",
      city: "Ville",
      country: "Pays (ISO-2)",
      skills: "Compétences",
      skillsHint:
        "séparées par des virgules : hazmat, frigo, longue distance …",
      licenceNumber: "Numéro de permis",
      licenceClass: "Catégorie",
      licenceExpires: "Expire le",
      state: "État",
      accounting: "Comptabilité",
    },
  },
  driver360: {
    back: "Chauffeurs",
    back360: "Chauffeur",
    tabs: {
      overview: "Vue d'ensemble",
      documents: "Documents",
      scorecard: "Tableau de bord",
      schedule: "Planning",
      activity: "Activité",
    },
    profile: "Profil",
    hos: "Profil temps de conduite",
    hosDefault: "défaut de la société",
    orders: "Commandes",
    noOrders: "Aucune commande affectée.",
    documents: "Documents et échéances",
    documentsHint:
      "Le permis est conservé sur la fiche chauffeur ; les autres pièces (certificat médical, permis matières dangereuses, pièce d'identité) auront leur espace avec WP-U16 — les seuils d'alerte s'appliquent déjà.",
    licence: "Permis de conduire",
    noExpiry: "aucune échéance enregistrée",
    expiry: {
      none: "Aucune date d'échéance — ajoutez-la sur le formulaire chauffeur.",
      expired:
        "Expiré depuis {{days}} jour(s) — le chauffeur ne devrait pas être affecté.",
      d7: "Expire sous 7 jours — renouveler maintenant.",
      d14: "Expire sous 14 jours.",
      d30: "Expire sous 30 jours.",
      ok: "Valide.",
    },
    papers: {
      medical: "Certificat médical",
      hazmat: "Permis matières dangereuses",
      identity: "Pièce d'identité",
    },
    papersLater: "pas encore suivi",
    thresholds: "Seuils d'alerte",
    thresholdsBody:
      "Les échéances sont signalées à 30, 14 et 7 jours et une fois dépassées ; le Moniteur de conformité (WP-A9) en fait des alertes et la fin de contrat de l'employé ERP désactive le chauffeur (erp_wins).",
    events: {
      "driver.created": "Chauffeur créé",
      "driver.updated": "Chauffeur modifié",
      "driver.linked": "Lié à l'ERP",
      "driver.location_changed": "Position",
      "driver.online_changed": "État en ligne",
    },
  },
  fleets: {
    title: "Flottes",
    hint: "Les flottes de la société — des groupes de véhicules et de chauffeurs pour le dispatch, les zones de service et les rapports. Ouvrez-en une pour voir ses membres.",
    vehicles: "Véhicules ({{count}})",
    drivers: "Chauffeurs ({{count}})",
    empty: "Aucune flotte.",
    remove: "Retirer de la flotte",
    add: { vehicles: "Ajouter un véhicule…", drivers: "Ajouter un chauffeur…" },
    addButton: "Ajouter",
  },
  assets: {
    title: "Équipements et actifs",
    hint: "Les actifs hors véhicules (remorques, conteneurs, générateurs …) avec l'actif ERP lié, et les équipements montés sur les véhicules ou les actifs.",
    searchHint: "code, nom, VIN, plaque, série",
    code: "Code",
    asset: "Actif",
    identification: "Identification",
    class: "Classe",
    serial: "Numéro de série",
    warranties: "Garanties",
    empty: "Aucun actif ne correspond.",
    equipment: "Équipements montés",
    equipmentHint:
      "Équipements montés (hayons, groupes froid, citernes …) avec leur numéro de série et le véhicule porteur.",
    equipmentEmpty: "Aucun équipement enregistré.",
    form: {
      new: "Nouvel équipement",
      newAsset: "Nouvel actif",
      edit: "Modifier l'actif",
      name: "Nom",
      type: "Type",
      typeHint: "hayon, groupe froid, citerne…",
      serial: "Numéro de série",
      save: "Enregistrer l'équipement",
      cancel: "Annuler",
    },
    types: {
      trailer: "Remorque",
      container: "Conteneur",
      reefer: "Frigorifique",
      generator: "Générateur",
      forklift: "Chariot élévateur",
      tank: "Citerne",
      equipment: "Équipement",
      other: "Autre",
    },
  },
  partners: {
    search: "Rechercher",
    searchHint: "nom, e-mail, téléphone, n° fiscal",
    total: "{{count}} fiche(s)",
    allTypes: "Tous les types",
    edit: "Modifier",
    remove: "Retirer",
    confirmRemove:
      "Retirer {{name}} ? La fiche reste dans l'historique ; les commandes et documents qui la citent continuent de fonctionner.",
    pullHint:
      "Rapatrier depuis {{name}} (lecture seule ; les fiches existantes sont liées par leur identifiant ERP)",
    pulling: "Rapatriement…",
    pull: {
      customers: "Rapatrier les clients ERP",
      suppliers: "Rapatrier les fournisseurs ERP",
    },
    erpOwnedHint:
      "L'ERP maîtrise ce champ dès que la fiche est liée ; modifiez-le là-bas.",
    status: { active: "actif", inactive: "inactif" },
    col: {
      name: "Nom",
      customer: "Client",
      reach: "Téléphone / e-mail",
      taxId: "N° fiscal",
      groupTerritory: "Groupe · territoire",
      place: "Lieu",
      status: "État",
      erpCustomer: "Client ERP",
      erpSupplier: "Fournisseur ERP",
      usedBy: "Utilisé par",
      type: "Type",
      address: "Adresse",
      owner: "Propriétaire",
      coordinates: "Lat, lng",
    },
    customers: {
      title: "Clients",
      one: "Client",
      hint: "Les organisations pour lesquelles OneFleet livre. Liés au Client ERP : le nom, le n° fiscal, le groupe et le territoire viennent de l'ERP (marqués ERP) ; le téléphone, les lieux et les notes restent ici. Un client créé ici n'atteint l'ERP que par une proposition qu'une personne approuve.",
      new: "Nouveau client",
      empty:
        "Aucun client encore — ajoutez-en un, ou rapatriez les clients ERP.",
      footer:
        "Badges : lié = le Client ERP est connu ; proposé = une poussée attend l'approbation dans la boîte des propositions ; non lié = OneFleet seul, par choix.",
    },
    contacts: {
      title: "Contacts",
      hint: "Les personnes chez les clients — qui appeler sur un site, qui signe une livraison. Les contacts sont à OneFleet ; l'ERP garde l'organisation.",
      new: "Nouveau contact",
      empty: "Aucune personne de contact encore.",
    },
    usage: {
      vehicles: "véhicules",
      assets: "actifs",
      warranties: "garanties",
      drivers: "chauffeurs",
      fleets: "flottes",
      personnel: "personnes",
      fuel_providers: "prestataires carburant",
      work_orders: "ordres de travail",
    },
    personnel: {
      title: "Personnes chez ce fournisseur",
      hint: "Qui appeler — les contacts du fournisseur, gardés dans OneFleet (non poussés vers l'ERP).",
      name: "Nom",
      role: "Rôle",
      add: "Ajouter",
      remove: "Retirer",
      empty: "Personne pour l'instant.",
    },
    vendors: {
      title: "Fournisseurs",
      one: "Fournisseur",
      hint: "Transporteurs, mécaniciens, fournisseurs de pièces et de carburant, assureurs — avec leur Fournisseur ERP et où chacun est utilisé (véhicules, ordres de travail, prestataires carburant, garanties…). Un nouveau fournisseur propose un Fournisseur ERP — un existant au même n° fiscal, e-mail ou nom est lié, pas dupliqué.",
      new: "Nouveau fournisseur",
      empty:
        "Aucun fournisseur encore — ajoutez-en un, ou rapatriez les fournisseurs ERP.",
      footer:
        "L'identité légale et fiscale est celle du Fournisseur ERP une fois lié ; le type et les coordonnées sont à OneFleet.",
    },
    vendorTypes: {
      carrier: "transporteur",
      mechanic: "mécanicien",
      supplier: "fournisseur",
      fuel: "carburant",
      insurer: "assureur",
      other: "autre",
    },
    places: {
      title: "Lieux",
      hint: "Les dépôts, sites clients, entrepôts, stations, ateliers et ports vers lesquels OneFleet route — géocodés ici sur le fournisseur de carte du tenant ; une adresse ERP n'est liée que lorsqu'un document en a besoin.",
      searchHint: "nom, adresse, ville",
      new: "Nouveau lieu",
      empty: "Aucun lieu encore — ajoutez d'abord le dépôt.",
      mapHint:
        "{{count}} lieu(x) sur la carte — cliquez une ligne ou un marqueur.",
      ownCompany: "La société",
      draft: "nouveau lieu",
      addressHint: "rue et numéro, ou un repère",
      city: "Ville",
      province: "Province",
      location: "Position",
      lookup: "Chercher l'adresse",
      looking: "Recherche…",
      noHits:
        "Le géocodeur n'a rien trouvé pour cette adresse — saisissez les coordonnées.",
      lat: "Latitude",
      lng: "Longitude",
      needLocation:
        "Un lieu a besoin de coordonnées : cherchez l'adresse ou saisissez latitude / longitude.",
    },
    placeTypes: {
      depot: "dépôt",
      customer_site: "site client",
      warehouse: "entrepôt",
      fuel_station: "station",
      workshop: "atelier",
      port: "port",
      other: "autre",
    },
    form: {
      editTitle: "Modifier {{name}}",
      create: "Créer",
      identity: "Identité",
      title: "Fonction",
      titleHint: "responsable de site, réception…",
      customerHint:
        "Un client est une organisation pour laquelle OneFleet livre. Avec une connexion ERP dont la règle master.customer.write est active, sa création dépose une proposition de miroir en Client ERP.",
      contactHint:
        "Une personne de contact chez un client ; le téléphone est ce que l'application chauffeur affiche sur l'arrêt.",
      vendorHint:
        "Un fournisseur auprès duquel OneFleet achète ou à qui elle confie du travail ; avec master.supplier.write active, sa création propose un Fournisseur ERP.",
      placeHint:
        "Un point géocodé sur la carte du tenant ; l'adresse est cherchée sur le fournisseur configuré ou les coordonnées saisies.",
      erpFields: "Champs maîtrisés par l'ERP",
      erpFieldsHint:
        "Modifiables tant que la fiche n'est pas liée à un Client ERP ; l'ERP en est ensuite propriétaire.",
      erpSupplierHint:
        "Modifiable tant que la fiche n'est pas liée à un Fournisseur ERP ; l'ERP en est ensuite propriétaire.",
      erpOwnedNote:
        "Cette fiche est liée à l'ERP : son nom et ses champs fiscaux sont ceux de l'ERP — modifiez-les là-bas ; le téléphone, le lieu et les notes sont à vous.",
      group: "Groupe de clients",
      territory: "Territoire",
      website: "Site web",
      notes: "Notes",
    },
  },
  warranties: {
    claims: {
      title: "Réclamations",
      hint: "Ce qui a été réclamé aux fournisseurs au titre de ces garanties et ce qui est revenu — déposé et suivi sur le panneau de l'ordre de travail ; un montant accepté vient en déduction du coût de l'intervention dans les allocations.",
      status: "Statut",
      all: "tous les statuts",
      none: "Aucune réclamation encore.",
      totals: "Totaux",
      col: {
        claim: "Réclamation",
        status: "Statut",
        workOrder: "Ordre de travail",
        claimed: "Réclamé",
        recovered: "Récupéré",
        reference: "Référence",
      },
    },
    title: "Garanties",
    hint: "Chaque garantie sur un véhicule, un actif ou un équipement avec sa période de couverture ; celles qui expirent sont signalées 30 jours avant.",
    name: "Garantie",
    subject: "Couvre",
    window: "Période",
    state: "État",
    coverage: "Couverture",
    empty: "Aucune garantie enregistrée.",
    subjects: { vehicle: "véhicule", asset: "actif", equipment: "équipement" },
    states: {
      expired: "expirée",
      expiring: "expire bientôt",
      running: "en cours",
      future: "à venir",
      open: "sans terme",
    },
    form: {
      new: "Nouvelle garantie",
      subject: "Sur",
      vendor: "Fournisseur",
      starts: "Début",
      ends: "Fin",
      terms: "Conditions",
      coverageHint: "km: 100000\nmois: 24",
      save: "Enregistrer la garantie",
    },
  },
  telematics: {
    protocols: {
      title: "Où pointe un traceur",
      hint: "Les protocoles que la passerelle écoute. Donnez à l'installateur l'adresse de la ligne que parle son boîtier ; cochez ceux que cette flotte utilise et nommez les modèles achetés.",
      askAdmin: "demandez à l'administrateur",
      notPublished: "ce déploiement n'ouvre pas encore ce port",
      col: {
        protocol: "Protocole",
        endpoint: "Adresse pour le boîtier",
        models: "Modèles",
        used: "Utilisé",
      },
    },
    title: "Boîtiers télématiques",
    hint: "Les traceurs installés sur les véhicules — la passerelle et les adaptateurs fournisseurs alimentent les positions ; la position d'un boîtier fait foi pour le véhicule (D-21 / D-24).",
    device: "Boîtier",
    provider: "Fournisseur",
    installed: "Installé",
    lastFix: "Dernière position",
    noFix: "aucune position",
    empty: "Aucun boîtier installé.",
    newDevice: "Nouveau boîtier",
    cancel: "Annuler",
    add: "Ajouter le boîtier",
    deviceId: "Identité du boîtier",
    deviceIdHint: "IMEI / identifiant unique du fournisseur",
    name: "Nom",
    model: "Modèle",
    obd: "OBD-II (données moteur, niveau de carburant, compteur, codes défaut)",
    providers: {
      traccar: "Traccar (la passerelle)",
      wialon: "Flux Wialon",
      geotab: "Flux Geotab",
      samsara: "Flux Samsara",
    },
    events: {
      title: "Événements boîtiers ouverts",
      hint: "Ce que la passerelle et la veille ont levé sur la flotte — un boîtier silencieux, un code défaut, un VIN discordant ; acquittez ce qu'une personne a vu.",
      empty: "Aucun événement boîtier ouvert.",
      acknowledge: "Acquitter",
      acknowledged: "acquitté {{when}}",
    },
  },
  maintenance: {
    title: "Ordres de travail",
    hint: "Brouillon → Approuvé → En cours → Terminé → Chiffré (ERP). Terminer un ordre dépose le document de réparation ERP en proposition ; demander des pièces dépose une demande de matériel — une personne approuve les deux (D-06).",
    newFromIssue: "Ouvrir un ordre depuis un signalement",
    status: {
      draft: "Brouillon",
      approved: "Approuvé",
      in_progress: "En cours",
      done: "Terminé",
      costed: "Chiffré (ERP)",
      cancelled: "Annulé",
    },
    priority: {
      low: "basse",
      normal: "normale",
      high: "haute",
      urgent: "urgente",
    },
    kind: {
      preventive: "Préventif",
      corrective: "Correctif",
      inspection: "Inspection",
      recall: "Rappel",
      other: "Autre",
    },
    readiness: {
      ready: "pièces prêtes",
      short: "pièces manquantes",
      requested: "pièces demandées",
      no_parts: "sans pièce",
    },
    due: {
      overdue: "En retard",
      due_soon: "Bientôt",
      ok: "OK",
      unknown: "Inconnu",
    },
    summary: {
      open: "Ordres ouverts",
      inProgress: "En cours",
      due: "En retard / bientôt",
      issues: "Signalements ouverts",
      partsBelow: "Pièces sous le seuil",
    },
    findings: {
      title: "Constats de l'assistant",
      hint: "Échéanciers bientôt dus ou en retard d'après le kilométrage et le calendrier — créez le brouillon, les pièces et la liste de contrôle prévues suivent.",
      draft: "Créer le brouillon",
      openWo: "ordre ouvert",
    },
    lines: {
      kind: "Type",
      description: "Description",
      qty: "Qté",
      unit: "Unitaire",
      total: "Total",
      source: "Source",
      kinds: {
        part: "Pièce",
        labour: "Main-d'œuvre",
        service: "Service",
        external: "Externe",
      },
      sources: {
        stock: "du stock",
        material_request: "demande de matériel",
        internal: "interne",
        external: "externe",
      },
    },
    claims: {
      title: "Réclamations de garantie",
      summary: "réclamé {{claimed}} · récupéré {{recovered}}",
      file: "Déposer une réclamation",
      warranty: "Garantie",
      amount: "Montant réclamé ({{currency}})",
      create: "Créer la réclamation",
      submit: "soumettre au fournisseur",
      accept: "acceptée",
      reject: "refusée",
      rejectedByVendor: "refusée par le fournisseur",
      acceptedAmount: "Montant accepté",
      reference: "référence du fournisseur",
      confirm: "Confirmer",
      pay: "payée",
      withdraw: "retirer",
      none: "Aucune garantie ne couvre ce véhicule — rien à réclamer.",
      status: {
        draft: "brouillon",
        submitted: "soumise",
        accepted: "acceptée",
        rejected: "refusée",
        paid: "payée",
        cancelled: "retirée",
      },
    },
    detail: {
      lines: "Lignes",
      coverage: "Couverture de garantie",
      coverageAll: "Toutes les garanties",
      noCoverage:
        "Aucune garantie en cours sur ce véhicule ou ses équipements.",
      coverageHint:
        "Une réparation sous garantie se réclame au fournisseur — à signaler sur les lignes et le document ERP.",
      short: "Manque en stock",
      asOf: "stock au {{when}}",
      requestParts: "Demander les pièces (ERP) — une proposition",
      checklist: "Liste de contrôle",
      findings: "Constats",
      budget: "Budget",
      estimate: "Estimation",
      approved: "Approuvé",
      actual: "Réel (ERP)",
      scheduled: "prévu {{when}}",
      completed: "terminé {{when}}",
      doneHint:
        "Terminé — le document de réparation ERP est proposé dans la boîte ; une fois approuvé, le coût ERP arrive ici et l'ordre passe à Chiffré.",
    },
    actions: {
      approve: "Approuver",
      start: "Démarrer",
      complete: "Terminer",
      cancel: "Annuler",
      odometer: "kilométrage à la fin",
    },
    woForm: {
      new: "Nouvel ordre de travail",
      title: "Nouvel ordre de travail",
      name: "Titre",
      kind: "Type",
      priority: "Priorité",
      scheduledAt: "Prévu le",
      description: "Description",
      part: "Code pièce",
      removeLine: "Retirer",
      save: "Créer l'ordre de travail",
      cancel: "Annuler",
    },
    completeDialog: {
      title: "Terminer {{number}}",
      hint: "Ce qui a réellement été fait : les lignes telles qu'utilisées (celles du devis, ajustées), les constats, le kilométrage. Le document de réparation ERP est proposé à partir de là — une personne l'approuve.",
      notes: "Notes",
      linesUsed: "Lignes utilisées",
      addFinding: "Constat",
      findingText: "Ce qui a été constaté",
      findingKind: "Type de constat",
      findingSeverity: "Gravité du constat",
      findingKinds: {
        observation: "Observation",
        defect: "Défaut",
        wear: "Usure",
        recommendation: "Recommandation",
      },
    },
    schedules: {
      title: "Échéanciers",
      hint: "Entretiens préventifs tous les N km et / ou N jours depuis le dernier ; l'état vient du kilométrage du véhicule, du calendrier et de la tendance de distance.",
      name: "Échéancier",
      interval: "Intervalle",
      lastDone: "Dernier",
      nextDue: "Prochain",
      state: "État",
      days: "jours",
      parts: "{{count}} pièces prévues",
      empty: "Aucun échéancier.",
    },
    issues: {
      title: "Signalements",
      hint: "Ce que les chauffeurs, les inspections et la console ont signalé sur les véhicules. Un signalement ouvre un ordre de travail et se résout avec lui.",
      report: "Signaler un problème",
      openWo: "Ouvrir un ordre",
      resolve: "Résoudre",
      close: "Clore",
      empty: "Aucun signalement dans cet état.",
      reportedBy: "signalé par {{who}}",
      filters: { active: "Actifs" },
      state: {
        open: "Ouvert",
        in_review: "En examen",
        resolved: "Résolu",
        closed: "Clos",
      },
      severity: {
        low: "Faible",
        medium: "Moyenne",
        high: "Haute",
        critical: "Critique",
      },
      categories: {
        engine: "Moteur",
        brakes: "Freins",
        tyres: "Pneus",
        body: "Carrosserie",
        electrical: "Électricité",
        other: "Autre",
      },
      form: {
        title: "Quel est le problème",
        vehicle: "Véhicule…",
        description: "Détails (facultatif)",
        save: "Signaler",
      },
    },
    parts: {
      title: "Stock de pièces",
      hint: "Le catalogue vient des articles de l'ERP ; le disponible est celui de l'ERP par entrepôt (à la dernière lecture), le réservé est ce que tiennent les ordres démarrés, projeté = disponible − réservé. Le stock n'est jamais modifié ici.",
      searchHint: "code, nom",
      state: "État",
      refresh: "Rafraîchir depuis l'ERP",
      new: "Nouvelle pièce",
      refreshed: "{{n}} emplacements rafraîchis",
      alerts: "{{count}} pièce(s) sous le seuil de réapprovisionnement :",
      alertsHint:
        "demandez-les depuis un ordre de travail (proposition de demande de matériel).",
      part: "Pièce",
      warehouse: "Entrepôt",
      onHand: "Disponible (ERP)",
      reserved: "Réservé",
      projected: "Projeté",
      ordered: "En commande",
      reorder: "Seuil",
      neededBy: "Demandé par",
      erpReserved: "+ {{qty}} retenus par l'ERP ({{count}} réservations)",
      states: {
        ok: "OK",
        at_reorder: "Au seuil",
        below: "Sous le seuil",
        out: "Rupture",
        ordered: "En commande",
        unknown: "Sans lecture",
      },
      asOf: "Stock ERP au {{when}}",
      empty:
        "Aucune pièce — importez les articles de l'ERP depuis le hub ERP (Rapprocher › imports : parts, stock).",
    },
  },
  inspections: {
    title: "Inspections",
    hint: "Des listes de contrôle remplies sur un véhicule — depuis la console ou l'application chauffeur — avec des points typés ; un point en échec ouvre un signalement de maintenance dont le Conseiller rédige l'ordre de travail. Une inspection soumise est un enregistrement.",
    fillNew: "Remplir une inspection",
    newForm: "Nouveau formulaire",
    cancel: "Annuler",
    forms: "Formulaires",
    noForms: "Aucun formulaire — construisez le premier.",
    formMeta: "{{items}} points · tous les {{freq}} jours",
    driverVisibleShort: "dans l'app chauffeur",
    fillThis: "Remplir",
    activate: "Activer",
    deactivate: "Désactiver",
    due: "À faire sur la flotte (7 jours)",
    noneDue: "Rien à faire.",
    never: "jamais remplie",
    overdueBy: "{{days}} j de retard",
    dueIn: "dans {{days}} j",
    all: "Toutes",
    empty: "Aucune inspection dans cet état.",
    draft: "brouillon",
    issue: "signalement",
    issueOpened: "un signalement a été ouvert",
    lastSubmitted:
      "{{form}} sur {{label}} soumise — {{failed}} point(s) en échec.",
    result: { pass: "Conforme", fail: "Non conforme" },
    kinds: {
      check: "OK / échec",
      number: "Relevé",
      text: "Texte",
      photo: "Photo",
    },
    col: {
      when: "Quand",
      form: "Formulaire",
      subject: "Véhicule",
      result: "Résultat",
      failed: "Points en échec",
      by: "Par",
    },
    builder: {
      name: "Nom du formulaire",
      description: "Description (facultatif)",
      frequency: "Tous les N jours",
      driverVisible: "Les chauffeurs peuvent la remplir",
      item: "Point",
      kind: "Type",
      bounds: "Bornes",
      category: "Catégorie",
      severity: "Gravité",
      required: "Req.",
      unit: "unité",
      addItem: "Ajouter un point",
      save: "Enregistrer le formulaire",
      remove: "Retirer",
    },
    fill: {
      form: "Formulaire",
      vehicle: "Véhicule…",
      odometer: "Compteur (km)",
      ok: "OK",
      fail: "Échec",
      note: "Ce qui ne va pas",
      notes: "Notes (facultatif)",
      photoUrl: "URL de la photo",
      submit: "Soumettre l'inspection",
      submitting: "Envoi…",
    },
  },
  lifecycle: {
    since: "depuis {{when}}",
    location: "emplacement",
    custodian: "dépositaire",
    disposed: "Cédé par {{kind}} {{when}}.",
    disposalKinds: { scrap: "mise au rebut", sale: "vente" },
    actions: "Actions du cycle de vie",
    actionsHint:
      "Les étapes opérationnelles s'appliquent immédiatement et entraînent l'état du véhicule ; transfert, réévaluation et cession touchent les livres et sont des propositions — une seconde personne les approuve dans la boîte.",
    actionLabels: {
      acquire: "Acquérir",
      place_in_service: "Mettre en service",
      start_maintenance: "Entrer en maintenance",
      end_maintenance: "Sortir de maintenance",
      take_out_of_service: "Mettre hors service",
      mark_for_disposal: "Marquer à céder",
      dispose: "Céder",
      transfer: "Transférer",
      revalue: "Réévaluer",
    },
    proposal: "proposition",
    noActions: "Aucune action depuis cet état.",
    readOnly: "Votre rôle lit le cycle de vie ; la finance propose les étapes.",
    reason: "Motif (optionnel)",
    propose: "Déposer la proposition",
    forms: {
      transfer: {
        title: "Transfert",
        hint: "→ Asset Movement dans l'ERP : nouvel emplacement et / ou dépositaire.",
        location: "Emplacement cible (Location ERP)",
        driver: "Nouveau dépositaire — un chauffeur…",
        employee: "…ou l'identifiant Employee ERP",
      },
      revalue: {
        title: "Réévaluation",
        hint: "→ Asset Value Adjustment sur le compte d'écart du profil.",
        value: "Nouvelle valeur ({{currency}})",
        current: "Valeur nette comptable actuelle : {{value}}",
      },
      dispose: {
        title: "Cession",
        hint: "→ mise au rebut par l'ERP, ou facture de vente sur l'article d'actif.",
        amount: "Montant de la vente ({{currency}})",
        customer: "Client ERP",
      },
    },
    inInbox: "dans la boîte",
    withdraw: "Retirer",
    eventStates: {
      applied: "appliqué",
      proposed: "proposé",
      approved: "approuvé",
      rejected: "rejeté",
      failed: "échoué",
    },
    eventKinds: {
      depreciation_posted: "Amortissement comptabilisé",
      repair: "Réparation enregistrée dans l'ERP",
    },
    timeline: "Chronologie",
    noEvents:
      "Aucun événement de cycle de vie — l'état est dérivé de l'état du véhicule.",
    depreciation: "Livres et amortissement (ERP)",
    refresh: "Relire depuis l'ERP",
    books: {
      cost: "Coût",
      nbv: "Valeur nette comptable",
      method: "Méthode",
      erpStatus: "État ERP",
    },
    months: "mois",
    noBooks:
      "Pas encore de livres ERP — reliez le véhicule à son actif (ERP Hub › Rapprocher).",
    posted: "comptabilisé",
    scheduled: "planifié",
    nextPosting: "prochain",
    endOfLife: "fin de vie",
    asOf: "lu {{when}}",
    date: "Date",
    amount: "Montant",
    accumulated: "Cumulé",
    journal: "Écriture",
    scheduledRow: "planifié",
    showAll: "Afficher les {{count}} lignes",
    showNext: "N'afficher que les prochaines",
    noSchedule: "Aucun plan d'amortissement lu — Relire depuis l'ERP.",
    tco: "TCO (12 mois)",
    tcoTotal: "coût de possession",
    tcoPerKm: "par km sur {{km}} km",
    tcoFleet: "flotte {{value}}",
    buckets: {
      fuel: "Carburant",
      maintenance: "Maintenance",
      depreciation: "Amortissement",
      other: "Assurance / autre",
    },
  },
  fuel: {
    summary: {
      waiting: "En attente de rapprochement",
      matched: "Rapprochés / comptabilisés",
      litres: "Litres ce mois",
      spend: "Dépense ce mois",
      anomalies: "Anomalies ouvertes",
      unmatched: "Lignes fournisseur orphelines",
    },
    status: {
      draft: "Brouillon",
      submitted: "Soumis",
      matched: "Rapproché",
      posted: "Comptabilisé (ERP)",
      rejected: "Rejeté",
    },
    payment: {
      card: "Carte carburant",
      cash: "Espèces",
      driver_paid: "Avancé par le chauffeur",
      account: "En compte",
      other: "Autre",
    },
    source: {
      driver_app: "appli chauffeur",
      console: "console",
      provider: "fournisseur",
      import: "import",
    },
    txState: {
      unmatched: "Orpheline",
      matched: "Rapprochée",
      ignored: "Ignorée",
    },
    batchState: {
      draft: "Brouillon",
      proposed: "Proposé",
      posted: "Comptabilisé (ERP)",
      cancelled: "Annulé",
    },
    anomalyState: {
      open: "Ouverte",
      dismissed: "Écartée",
      confirmed: "Confirmée",
    },
    runState: {
      draft: "Brouillon",
      previewed: "Prévisualisé",
      proposed: "Proposé",
      posted: "Comptabilisé (ERP)",
      rejected: "Rejeté",
      superseded: "Remplacé",
    },
    reportsObd: {
      obdOk: "jauge +{{rise}} %",
      obdHint:
        "Niveau OBD {{before}} % → {{after}} % autour du plein (≈ {{expected}} % attendus d'après les litres)",
    },
    rules: {
      over_capacity: "Au-delà du réservoir",
      station_distance: "Véhicule ailleurs",
      consumption_outlier: "Consommation atypique",
      duplicate: "Doublon",
      odometer_regression: "Compteur en recul",
      off_hours: "Hors horaires",
      no_vehicle_movement: "Sans déplacement",
      obd_level_flat: "Jauge sans hausse",
    },
    pools: {
      label: "Pool",
      fuel_matched: "Carburant rapproché",
      fuel_all: "Tout le carburant",
      maintenance_actual: "Réel maintenance (ERP)",
    },
    bases: {
      km_share_orders: "part km des courses",
      hours_share_orders: "part heures moteur des courses",
      days_share_orders: "jours en service par cible",
      order_cost_center: "la course du rapport",
      vehicle_cost_center: "le centre de coût du véhicule",
      fixed_pct: "pourcentages fixes",
    },
    reports: {
      title: "Rapports carburant",
      hint: "Chaque litre atterrit ici — le rapport du chauffeur, celui de la console ou la ligne du relevé fournisseur. Les pleins par carte attendent le relevé ; espèces et avances portent leur reçu. La comptabilisation ERP (Vehicle Log, note de frais) est une proposition dans la boîte.",
      record: "Saisir un plein",
      state: "État",
      number: "Rapport",
      vehicle: "Véhicule",
      when: "Quand",
      litres: "Litres",
      amount: "Montant",
      payment: "Paiement",
      anomalies: "Anomalies",
      erp: "ERP",
      score: "rapprochement {{score}} %",
      proposalPending: "proposition dans la boîte",
      empty: "Aucun rapport dans cet état.",
    },
    form: {
      vehicle: "Véhicule…",
      driver: "Chauffeur (optionnel)",
      odometer: "Compteur (km)",
      litres: "Litres",
      unit: "Prix / L",
      amount: "Montant",
      card: "Carte (optionnel)",
      station: "Station",
      receipt: "N° de reçu",
      fullTank: "Plein complet",
      save: "Enregistrer",
    },
    actions: {
      submit: "Soumettre",
      reject: "Rejeter",
      rejectReason: "Pourquoi ce rapport est-il rejeté ?",
    },
    forms: {
      provider: {
        new: "Nouveau fournisseur",
        title: "Nouveau fournisseur de carburant",
        name: "Nom",
        kind: "Type",
        currency: "Devise",
        supplier: "Fournisseur ERP",
        supplierHint: "le nom du Supplier dans l'ERP (optionnel)",
        delimiter: "Séparateur CSV",
        datetime: "Format de date",
        columns: "Colonnes du relevé",
        decimalComma: "Virgule décimale dans les montants",
        save: "Créer le fournisseur",
        kinds: {
          card: "Émetteur de cartes carburant",
          station: "Réseau de stations",
          api: "API fournisseur",
          csv: "Fichier de relevé",
        },
        column: {
          external_id: "Référence",
          card: "Carte",
          occurred_at: "Date",
          litres: "Litres",
          amount: "Montant",
          station: "Station",
        },
      },
      card: {
        new: "Nouvelle carte",
        title: "Nouvelle carte carburant",
        ref: "Référence de la carte",
        label: "Libellé",
        save: "Créer la carte",
      },
      report: {
        edit: "Modifier",
        erp: "ERP",
        title: "Modifier {{number}}",
        receiptFile: "Reçu (photo / PDF)",
        receiptLinked: "Un fichier de reçu est lié à ce rapport.",
        notes: "Notes",
        save: "Enregistrer le rapport",
      },
    },
    providers: {
      title: "Fournisseurs et cartes",
      provider: "Fournisseur",
      noSupplier: "sans fournisseur ERP",
      lastImport: "dernier import {{when}}",
      neverImported: "aucun relevé",
      unassigned: "sans véhicule",
      empty:
        "Aucun fournisseur carburant — le seed ajoute une carte de démo ; l'administrateur ajoute les vrais.",
      hint: "Les colonnes du CSV d'un fournisseur sont décrites dans sa configuration ; une carte affectée à un véhicule transforme chaque ligne de relevé en rapport de ce véhicule.",
    },
    feed: {
      none: "pas de flux — relevés à la main",
      pullEvery: "flux tiré toutes les {{every}} min",
      pushOnly: "le fournisseur pousse (jeton défini)",
      lastOk:
        "dernier {{when}} · {{created}} nouvelles, {{duplicates}} connues",
      lastFailed: "dernier tirage en échec : {{error}}",
      syncNow: "Synchroniser",
      url: "URL du flux (l'export du fournisseur — CSV ou JSON dans ses colonnes, mappées dans la configuration)",
      format: "Format",
      jsonPath: "Chemin JSON des lignes",
      auth: "Authentification",
      authKind: {
        none: "aucune",
        bearer: "Jeton Bearer",
        basic: "Basic (utilisateur:mot de passe)",
        header: "En-tête clé API",
      },
      credential: "Identifiant (scellé, jamais réaffiché)",
      sinceParam: "Paramètre « depuis la dernière synchro »",
      everyMin: "Toutes les (minutes)",
      save: "Enregistrer le flux",
      rotateToken: "Régénérer le jeton de push",
      tokenOnce: "Affiché une fois —",
      hint: "Le fournisseur peut pousser à la place : POST /webhooks/fuel/{{id}} avec X-OneFleet-Token ; les lignes sont dédoublonnées par leur identifiant externe dans les deux cas.",
    },
    import: {
      title: "Importer un relevé",
      placeholder:
        "ref;carte;date;qte;montant;station\nTX-1001;7041-****-4521;14/09/2026 08:37;117,50;470 000,00;TOTAL LIMETE",
      run: "Importer",
      stats:
        "{{created}} nouvelles lignes · {{matched}} rapprochées à un rapport en attente · {{reports}} rapports créés · {{duplicates}} déjà connues · {{invalid}} invalides · {{unassigned}} sans véhicule",
      hint: "Un même relevé importé deux fois compte des doublons, jamais du double. Les lignes sans véhicule attendent dans la file jusqu'à l'affectation de la carte.",
    },
    transactions: {
      title: "Transactions fournisseur",
      hint: "Ce que le fournisseur dit avoir livré, une ligne par ligne de relevé ; les lignes rapprochées pointent vers leur rapport, les autres attendent dans la file.",
      ref: "Référence",
      card: "Carte · véhicule",
      station: "Station",
      report: "Rapport",
      inBatch: "dans un lot",
      empty: "Aucune transaction dans cet état.",
    },
    batches: {
      title: "Lots fournisseur (AP)",
      hint: "Les transactions carte d'un fournisseur pour un mois, sommées par véhicule → une facture d'achat brouillon proposée à l'ERP (W3 : une seconde personne approuve dans la boîte).",
      billNo: "N° de facture fournisseur",
      build: "Constituer le lot du mois",
      period: "Période",
      transactions: "Lignes",
      lines: "Par véhicule",
      propose: "Proposer la facture",
      empty: "Aucun lot.",
    },
    matching: {
      title: "File de rapprochement",
      hint: "Les pleins par carte qui attendent encore la ligne du fournisseur, avec les candidats notés par la règle (véhicule ou carte, ± 2 h, litres ou montant ± 5 %) ; une personne relie les cas douteux.",
      waiting: "{{count}} rapports en attente",
      noneWaiting: "Tous les rapports sont rapprochés.",
      noCandidate: "aucune ligne fournisseur dans la fenêtre",
      link: "Relier",
      unmatched: "{{count}} lignes fournisseur orphelines",
      noneUnmatched: "Toutes les lignes fournisseur sont rattachées.",
      cardUnassigned: "carte sans véhicule",
      unmatchedHint:
        "Affectez la carte à un véhicule (Transactions) ou saisissez le rapport auquel la ligne appartient.",
    },
    anomalies: {
      title: "Anomalies",
      hint: "Les constats des règles déterministes sur les rapports, avec leurs preuves. Écartez ce qui s'explique, confirmez ce qui appelle une suite — l'analyste carburant explique, il ne décide jamais.",
      note: "Une note pour la décision (optionnel)",
      dismiss: "Écarter",
      confirm: "Confirmer",
      decided: "décidé par {{who}} {{when}}",
      source: { rule: "règle", ai: "IA" },
      empty: "Aucune anomalie dans cet état.",
    },
    allocation: {
      title: "Répartition des coûts",
      hint: "Les règles répartissent le carburant de la période (et le réel maintenance de l'ERP) sur les centres de coût ou les projets — par la part km des courses de chaque véhicule, son propre centre de coût ou des pourcentages fixes. L'écriture est un reclassement que l'ERP passe une fois qu'une personne l'approuve (W3, double contrôle).",
      lastRun: "dernier calcul {{period}} · {{state}}",
      newRun: "Nouveau calcul",
      rules: "Règles",
      newRule: "Nouvelle règle",
      ruleName: "Nom de la règle",
      priority: "Priorité",
      sourceCc: "Centre de coût crédité (optionnel — celui du véhicule sinon)",
      splitsHint:
        "Une répartition par ligne : centre de coût = pourcentage, ex. Depot Nord - DLG = 60",
      targetCostCenter: "centre de coût",
      targetProject: "projet",
      inactive: "inactive",
      noRules:
        "Aucune règle — le seed en ajoute une (part km avec repli sur le véhicule).",
      runs: "Calculs",
      noRuns: "Aucun calcul — choisissez un mois et prévisualisez.",
      run: "Calcul {{period}}",
      reversal: "contrepassation",
      items: "{{count}} documents source",
      lines: "{{count}} lignes",
      previewed: "prévisualisé {{when}}",
      proposalPending:
        "la proposition d'écriture attend dans la boîte (double contrôle)",
      total: "Réparti",
      unallocated: "{{amount}} non répartis",
      preview: "Prévisualiser à nouveau",
      propose: "Proposer l'écriture",
      reject: "Rejeter",
      supersede: "Remplacer (calcul de contrepassation)",
      byTarget: "Par cible",
      byPool: "Par pool",
      nothing: "Rien de réparti dans ce calcul.",
      journal: "Aperçu de l'écriture",
      journalHint:
        "Débit par cible, crédit par centre de coût d'origine, sur le compte de charge du pool défini dans le profil de connexion — la charge totale ne change pas.",
      dimension: "Dimension",
      debit: "Débit",
      credit: "Crédit",
      accountFromProfile: "(compte du profil)",
      source: "Source",
      rule: "Base",
      target: "Cible",
      share: "Part",
      evidence: "Preuves",
      from: "depuis {{cc}}",
      unallocatedList: "{{count}} non répartis :",
    },
  },
  copilot: {
    storeDown:
      "Le magasin IA n'est pas joignable — les lignes ci-dessous peuvent être incomplètes.",
    decisions: {
      allow: "autorisé",
      auto: "auto-approuvé",
      proposal: "proposition",
      deny: "refusé",
      stub: "simulé",
    },
    kinds: {
      chat: "Demander à OneFleet",
      parse: "Lecture de commande",
      candidates: "Classement des chauffeurs",
      assign: "Affectation",
      plain: "Discussion simple",
    },
    sessions: {
      title: "Sessions",
      hint: "Chaque conversation ouverte par le panneau copilote sur cette société — qui, quel agent, sur quelle page, combien de tâches.",
      hintOwn:
        "Vos propres conversations avec le copilote — un administrateur voit celles de la société.",
      who: "Qui",
      agent: "Agent",
      page: "Page",
      tasks: "Tâches",
      lastAt: "Dernier usage",
      empty:
        "Aucune session — posez une question au copilote depuis le panneau.",
    },
    tasks: {
      title: "Tâches",
      hint: "Une ligne par exécution d'agent : la source (règle / IA / IA+règle), le modèle, les outils appelés, les propositions déposées, les avertissements. Le texte de la question n'est jamais conservé — seulement sa taille.",
      hintOwn:
        "Vos propres exécutions — un administrateur voit celles de la société.",
      session: "session",
      cloud: "cloud",
      sizes: "question {{q}} car. · réponse {{a}} car.",
      empty: "Aucune tâche.",
      audit: "Audit des appels d'outils",
      auditHint:
        "Chaque appel d'outil autorisé, refusé, transformé en proposition ou auto-approuvé par la politique — avec la règle et le motif (administrateurs).",
      auditEmpty: "Aucun appel enregistré.",
      when: "Quand",
      tool: "Outil",
      decision: "Décision",
      reason: "Motif",
    },
    policies: {
      title: "Règles",
      hint: "Ce que les agents peuvent faire sur cette société : les outils accessibles par agent et par rôle, les classes d'écriture, les règles d'auto-approbation, le passage au cloud et les seuils. Tout le reste est une proposition qu'une personne décide (D-06).",
      agents: "Agents × outils",
      roles: "Rôles × outils",
      writes: "Classes d'écriture",
      rules: "Règles d'auto-approbation",
      enabled: "active",
      disabled: "inactive",
      cap: "max {{n}} / h",
      cloud: "Cloud",
      on: "activé",
      off: "désactivé",
      highValue: "Commande de grande valeur à partir de",
      dualControl: "Double contrôle à partir de",
      ttl: "Durées de vie des propositions",
      override: "Surcharge de la société (YAML)",
      defaultOnly: "politique par défaut, aucune surcharge",
      version: "surcharge v{{n}}",
      editorHint:
        "Les clés définies ici remplacent celles du défaut (les dictionnaires fusionnent, les listes remplacent). L'enregistrement est validé par l'API IA ; une surcharge vide revient au défaut. Chaque enregistrement crée une version.",
      commentHint: "Ce qui change (facultatif)",
      save: "Enregistrer une nouvelle version",
      clear: "Effacer",
      saved: "Enregistrée en v{{n}} — appliquée sous une minute.",
      history: "Versions",
      active: "active",
      load: "charger",
      noHistory: "Aucune surcharge enregistrée.",
    },
  },
  views: {
    label: "Vues enregistrées",
    pick: "Vues enregistrées…",
    none: "Aucune vue enregistrée",
    saveCurrent: "Enregistrer cette vue",
    nameHint: "Nom de la vue",
    save: "Enregistrer",
    remove: "Supprimer la vue « {{name}} »",
  },
  hub: {
    all: "Tous",
    connection: "Connexion ERP",
    direction: {
      push: "push",
      pull: "pull",
      bidir: "bidirectionnel",
      link_only: "lien seul",
    },
    submit: { draft: "Brouillon", submit: "Validé" },
    states: {
      pending: "En attente",
      processing: "En cours",
      done: "Fait",
      ok: "OK",
      running: "En cours",
      failed: "Échoué",
      dead: "Abandonné",
      error: "Erreur",
      ignored: "Ignoré",
      executed: "Exécuté",
      rejected: "Rejeté",
      approved: "Approuvé",
      expired: "Expiré",
      superseded: "Remplacée",
    },
    modules: {
      assets: "Véhicules · Cycle de vie & amortissement des actifs",
      dispatch: "Trajets & dispatch — commandes, livraisons, factures",
      drivers: "Gestion des chauffeurs",
      maintenance: "Maintenance & stock de pièces",
      fuel: "Carburant & allocation des coûts",
      master: "Données de base",
      events: "Événements",
    },
    capabilities: {
      title: "Capacités — les interrupteurs des modules",
      hint: "Une ligne par capacité ; l'interrupteur l'active pour cette connexion, les règles en dessous disent ce qui la déclenche, si une personne l'approuve et si le document ERP reste en brouillon ou est validé. Rien n'écrit dans l'ERP ici.",
      legend:
        "Risque : R lecture · W1 lien / miroir · W2 brouillon · W3 argent ou données de base (toujours une proposition, D-06).",
    },
    rulesCount: "{{count}} règles",
    approvalOn: "approbation",
    override: "surcharge",
    noRules:
      "Pas encore de règle pour cette capacité — « Recréer les règles manquantes » ajoute les valeurs par défaut.",
    resetRules: "Recréer les règles manquantes",
    w3Locked: "Une capacité W3 attend toujours une décision",
    enabledCount: "{{on}} sur {{count}} actives",
    col: {
      event: "Événement",
      direction: "Sens · déclencheur",
      enabled: "Active",
      approval: "Approbation",
      submit: "Document ERP",
      mapping: "Mapping",
    },
    sync: {
      title: "Activité de synchronisation",
      hint: "Les imports et rafraîchissements, les événements envoyés par l'ERP et la file du relais vers l'ERP — du plus récent au plus ancien.",
      runs: "Exécutions",
      noRuns:
        "Aucune exécution — les imports et rafraîchissements de dimensions apparaissent ici.",
      inbound: "Événements reçus de l'ERP",
      inboundFilter: "Événements :",
      noEvents: "Aucun événement reçu.",
      badSignature: "signature rejetée",
      replay: "Rejouer",
      outbox: "File du relais (cible ERP)",
      outboxHint:
        "Événements OneFleet auxquels les règles ERP réagissent ; une écriture soumise à approbation devient une proposition, sinon elle s'exécute sur la connexion.",
      outboxFilter: "File :",
      noOutbox: "Rien en file.",
      retry: "Réessayer",
    },
    dead: {
      title: "Lettres mortes",
      hint: "Ce que le relais a abandonné ou ne cesse d'échouer, et les événements ERP en échec — avec la dernière erreur. Réessayer et rejouer restent côté OneFleet ; une écriture passe toujours par l'approbation.",
      outbox: "Lignes de file échouées et abandonnées",
      outboxHint:
        "Abandonné = toutes les tentatives épuisées ; échoué = en attente de la prochaine tentative.",
      inbound: "Événements ERP en échec",
    },
    errors: {
      class: {
        authentication: "identifiants",
        permission: "autorisation",
        missing_master_data: "donnée de référence absente",
        mandatory_field: "un champ exigé par l'ERP",
        business_rule: "règle propre à l'ERP",
        document_state: "état du document",
        conflict: "modifié des deux côtés",
        transport: "le site n'a pas répondu",
        capability: "non développé pour cet ERP",
        unknown: "jamais rencontré",
      },
    },
    backfill: {
      title: "Reprise — la flotte déjà en place",
      hint: "Avant que la première synchronisation n'écrive quoi que ce soit : lesquels des enregistrements de l'ERP sont des enregistrements que OneFleet possède déjà — reconnus par un lien existant, par l'identifiant porté par l'ERP, par le VIN ou par la plaque — et lesquels ne sont reconnus par rien ici, nommés un par un pour qu'un VIN puisse être corrigé avant qu'un doublon ne soit créé.",
      resource: {
        assets: "Immobilisations (avec leurs véhicules)",
        vehicles: "Véhicules",
      },
      report: "Rapport seulement",
      link: "Les lier maintenant",
      linkHint:
        "Lance la synchronisation habituelle : ce qui est reconnu est lié, le reste est créé.",
      dryRun: "Rien n'a été écrit.",
      matched: "{{matched}} sur {{total}} reconnus ({{pct}} %)",
      by: {
        link: "déjà lié",
        public_id: "par l'identifiant de l'ERP",
        vin: "par VIN",
        plate: "par plaque",
        internal_id: "par identifiant d'immobilisation",
        unmatched: "rien ne l'a reconnu",
      },
      unmatchedHint:
        "Rien ici ne correspond à ceux-ci — une synchronisation les créerait :",
      col: {
        label: "Enregistrement",
        plate: "Plaque",
        vin: "VIN",
        erp: "Identifiant ERP",
      },
    },
    reconcile: {
      title: "Réconciliation — liens et vérifications",
      hint: "Chaque lien entre une fiche OneFleet et un document ERP avec son état ; les fiches créées par l'ERP qu'une personne doit vérifier ; les imports qui relient par id OneFleet, VIN puis plaque.",
      counts: "Liens par état",
      run: "Réconcilier maintenant",
      repush: "Repousser",
      repushHint:
        "Proposer de mettre le document ERP à jour avec ce que OneFleet détient — une personne approuve.",
      repushed:
        "Proposé : la mise à jour attend dans la boîte des propositions.",
      conflicts: "Écarts",
      conflictsHint:
        "La réconciliation nocturne (02:15) relit les documents ERP derrière les liens ; une règle en manuel vous attend.",
      noConflicts: "Aucun écart.",
      ours: "chez nous",
      theirs: "dans l'ERP",
      takeErp: "Prendre celle de l'ERP",
      keepOurs: "Garder la nôtre",
      status: {
        resolved_erp: "valeurs de l'ERP appliquées",
        resolved_local: "les nôtres conservées",
        resolved_manual: "tranché",
        ignored: "ignoré",
      },
      links: "Liens",
      col: {
        record: "Fiche",
        document: "Document ERP",
        state: "État",
        synced: "Synchronisé",
      },
      unlink: "Délier",
      noLinks: "Aucun lien dans cet état.",
      review: "À vérifier",
      reviewHint:
        "Véhicules créés par l'ERP sans plaque ni VIN correspondants — ouvrir, compléter, fixer l'état.",
      noReview: "Rien à vérifier.",
    },
    mappings: {
      title: "Mappings — les correspondances de champs",
      hint: "YAML versionné dans le dépôt OneFleet ; par champ : le propriétaire, la transformation, la table de valeurs, la garde « vers l'avant ». Lecture seule.",
      fields: "{{count}} champs",
      submit: "validation",
      identity: "Identité",
      marker: "marqueur",
      conflict: "Conflit",
      owner: "Propriétaire",
      transform: "Transformation",
      notes: "Notes",
      required: "requis",
      monotonic: "vers l'avant seulement",
      hashed: "dans le hachage",
      none: "Pas encore de mapping pour ce pack — le pack Oracle Fusion arrive avec WP-B8 (après `--describe` sur un pod nommé, D-12).",
    },
  },
  proposals: {
    title: "Propositions",
    hint: "Chaque écriture proposée par une règle ou un agent — approuver l'exécute dans l'ERP, rejeter consigne pourquoi. Rien n'est écrit sans une décision ici ou sur la carte ERP de la fiche.",
    allCapabilities: "Toutes les capacités",
    empty: "Aucune proposition dans cet état.",
    action: "Action",
    evidence: "Preuves",
    result: "Résultat",
    notYet: "Pas encore exécutée.",
    reason: "Motif",
    actor: "Déclenché par",
    preview: {
      title: "Le document que cela écrit",
      problem: "L'approbation échouerait :",
      customer: "Client",
      date: "Date",
      line: "Ligne",
      qty: "Qté",
      rate: "Prix",
      amount: "Montant",
      total: "Total",
      proofs_one: "{{count}} preuve de livraison l'accompagne",
      proofs_other: "{{count}} preuves de livraison l'accompagnent",
      raw: "L'événement qui l'a déclenché",
      refs: {
        vehicle_no: "Véhicule",
        lr_no: "Lettre de voiture",
        driver: "Chauffeur",
        cost_center: "Centre de coût",
        project: "Projet",
        site: "Site",
        set_warehouse: "Entrepôt",
        business_unit: "Unité commerciale",
        transaction_type: "Type de transaction",
        cross_reference: "Référence",
      },
    },
    rule: "Règle",
    reasonHint: "Motif (facultatif)",
    decidedAt: "Décidée {{when}}",
    decidedBy: "par {{who}}",
    approveExecute: "Approuver et exécuter",
    expiresAt: "expire {{when}}",
    expired: "expirée",
    onBehalf:
      "Proposée par l'agent au nom de {{who}} — approuver exécute l'action en votre nom.",
    actions: {
      "orders.assign": "Affecter un chauffeur (Copilote dispatch)",
    },
    policy: "Politique",
    policyLine:
      "Écritures ERP = proposition (W1 lien/miroir, W2 brouillons, W3 argent et données de base avec double contrôle) ; les lectures s'exécutent avec les droits de l'appelant ; les pistes chauffeur et client n'atteignent jamais un modèle cloud.",
    aiLater:
      "Les propositions des agents (réaffectation, brouillons d'ordres de travail, demandes de matériel, écritures d'allocation) rejoignent cette boîte avec le magasin IA et les politiques.",
  },
  developer: {
    title: "Développeur",
    hint: "L'API `/v1` telle qu'un intégrateur la voit : clés API, webhooks sortants et leurs livraisons, les événements de la société, les journaux de requêtes, et le bac à sable sur lequel travaillent les clés de test.",
    secretOnce: "Affiché une seule fois — copiez-le maintenant.",
    copy: "Copier",
    dismiss: "Fermer",
    keys: {
      title: "Clés API",
      hint: "Une clé signe les appels `/v1` (`Authorization: Bearer of_live_… / of_test_…`) ; une clé de test travaille sur le bac à sable. Le secret n'est affiché qu'une fois.",
      col: { name: "Nom", key: "Clé", mode: "Mode", lastUsed: "Dernier usage" },
      test: "bac à sable",
      live: "réel",
      testMode: "mode test (bac à sable)",
      create: "Créer la clé",
      revoke: "Révoquer",
      empty: "Aucune clé API pour l'instant.",
      secretLabel: "Nouvelle clé API",
    },
    webhooks: {
      title: "Webhooks sortants",
      hint: "OneFleet POSTe les événements de la société aux URL enregistrées ici, signés avec le secret du point de terminaison ; un point en échec est réessayé au rythme de l'outbox et mis en pause après dix échecs d'affilée.",
      url: "URL",
      events: "Événements (`*`, `order.*`, `vehicle.created` …)",
      create: "Enregistrer le point",
      pause: "Mettre en pause",
      resume: "Reprendre",
      rotate: "Changer le secret",
      test: "Tester",
      testOk: "Livré",
      testFailed: "Non livré",
      testClose: "fermer",
      delete: "Supprimer",
      confirmDelete:
        "Supprimer ce point de terminaison ? Ses livraisons s'arrêtent aussitôt.",
      failures: "{{count}} échec(s) d'affilée",
      lastSuccess: "dernière livraison",
      never: "rien de livré pour l'instant",
      empty: "Aucun point de terminaison pour l'instant.",
      secretLabel: "Secret de signature",
      signature:
        'Chaque POST porte `X-OneFleet-Event`, `X-OneFleet-Delivery` et `X-OneFleet-Signature: t=<ts>,v1=<hmac-sha256(secret, "<ts>.<body>")>` ; le corps est `{id, type, created_at, company, api_version, aggregate, data}`.',
    },
    deliveries: {
      title: "Livraisons",
      empty: "Aucune livraison pour ce point pour l'instant.",
    },
    events: {
      title: "Événements",
      hint: "Tout ce que les enregistrements de la société ont émis — ce que les webhooks et le relais ERP diffusent.",
      type: "Type d'événement",
      all: "Tous les types",
      col: {
        id: "Id",
        type: "Type",
        targets: "Cibles",
        status: "Relais",
        when: "Quand",
      },
      empty: "Aucun événement pour l'instant.",
    },
    logs: {
      title: "Journal des requêtes",
      hint: "Les appels `/v1` des 30 derniers jours : qui, quoi, le statut et la latence. Le trafic de la console n'y figure pas.",
      col: {
        when: "Quand",
        who: "Qui",
        request: "Requête",
        status: "Statut",
        ms: "ms",
      },
      filters: {
        anyWho: "Tous",
        method: "Méthode",
        anyMethod: "Toute méthode",
        path: "chemin contenant…",
        status: "Statut",
        anyStatus: "Tout statut",
      },
      empty: "Aucun appel `/v1` pour l'instant.",
    },
    socket: {
      title: "Socket temps réel",
      hint: "Le même hub socket.io que la console écoute : une clé API dans la poignée de main rejoint les salons de la société ; abonnez-vous à un véhicule, un chauffeur, une commande pour leurs événements.",
      url: "Adresse",
      auth: "Poignée de main",
      rooms: "Vos salons",
      subscribable: "Abonnables",
      events: "Événements",
      copy: "Copier l'extrait",
      copied: "Copié",
    },
    sandbox: {
      title: "Bac à sable",
      hint: "Les clés de test lisent et écrivent le jumeau bac à sable de cette société. La réinitialisation le vide — les données réelles ne sont jamais touchées.",
      reset: "Réinitialiser le bac à sable",
      confirm:
        "Vider le bac à sable de cette société ? Chaque ligne créée par les clés de test est supprimée ; les données réelles restent.",
      done: "{{rows}} ligne(s) supprimée(s) dans {{tables}} table(s).",
      readOnly:
        "La réinitialisation demande les permissions développeur et paramètres.",
    },
  },
  aiSettings: {
    title: "IA",
    hint: "Ce que l'API IA répond via la passerelle, les rôles de modèles, le pool d'outils MCP et la politique qui régit les écritures. Les réglages par société, budgets, éditeur de politique, usage et évaluations arrivent avec le magasin IA.",
    readiness: "État",
    api: "API IA",
    gateway: "Passerelle",
    store: "Magasin IA",
    chatModel: "Modèle de chat",
    tracks: "Pistes",
    openGateway: "Ouvrir l'interface de la passerelle",
    models: "Rôles de modèles",
    roles: {
      chat_small: "Chat (petit)",
      chat_large: "Chat (grand)",
      extract: "Extraction",
      vision: "Vision",
      embed: "Plongements",
    },
    available: "{{count}} modèles offerts par la passerelle",
    modelsHint:
      "Chaque appel de modèle passe par la passerelle OneSphere sur les pistes OneFleet (D-05) ; le dev utilise les modèles de démonstration jusqu'au choix d'un modèle résident.",
    registry: "Pool d'outils MCP",
    registryHint:
      "Les serveurs annoncés à l'API IA et leurs outils ; l'identité de l'appelant voyage jusqu'au cœur, un outil ne voit donc que ce que la personne peut voir.",
    tools: "{{count}} outils",
    announced: "annoncé {{when}}",
    notAnnounced: "non annoncé",
    col: {
      tool: "Outil",
      mode: "Mode",
      risk: "Risque",
      roles: "Rôles",
      description: "Description",
    },
    read: "lecture",
    write: "écriture",
    policy: "Politique (par défaut)",
    policy1:
      "Ask OneFleet lit avec les droits de la personne connectée ; un appelant anonyme n'obtient aucune donnée.",
    policy2:
      "Chaque outil d'écriture est une proposition — écritures de dispatch, brouillons d'ordres de travail, documents ERP — décidée par une personne.",
    policy3:
      "Les propositions financières au-delà du seuil de la société demandent un second approbateur de la finance (double contrôle).",
    policy4:
      "Les conversations chauffeur et client restent sur les modèles sur site ; le cloud est soumis à la politique par classe d'invite.",
    later:
      "Modifiable par société avec historique, plus usage et évaluations, avec le magasin IA.",
    openProposals: "Propositions",
    tenant: {
      title: "L'IA de ce locataire",
      hint: "Le modèle de chaque rôle, les budgets qu'un appel doit respecter et si les agents rédigent. Un champ vide hérite du déploiement.",
      usage:
        "Consommé : {{day}} jetons aujourd'hui · {{month}} ce mois · {{cloud}} appel(s) cloud aujourd'hui",
      noLimit: "sans limite",
      save: "Enregistrer",
      on: "activée",
      off: "désactivée",
      cloud:
        "La porte cloud est {{state}} — elle appartient à la politique, modifiée dans",
      updated: "Version {{version}}, enregistrée {{when}} par {{who}}",
      role: {
        chat_small: "Chat (petit)",
        chat_large: "Chat (grand)",
        extract: "Extraction",
        vision: "Vision",
        embed: "Plongements",
      },
      budget: {
        tokens_day: "Jetons par jour",
        tokens_month: "Jetons par mois",
        cloud_calls_day: "Appels cloud par jour",
      },
      switch: {
        narrate: "Rédiger les réponses",
        narrate_driver: "Rédiger pour les chauffeurs",
        parse_model: "Modèle sur l'analyse de commande",
      },
    },
    schedules: {
      title: "Planifications quotidiennes",
      hint: "Ce que le worker demande aux agents chaque jour pour cette société (l'horloge du déploiement). Les propositions arrivent dans la boîte de réception selon la politique ; une personne décide. La dernière exécution par tâche est ce que le worker a enregistré.",
      language: "Langue",
      col: {
        job: "Tâche",
        at: "À",
        switches: "Réglages",
        tells: "Prévient",
        last: "Dernière exécution",
      },
      enabled: "activée",
      propose: "déposer les propositions",
      threshold: "prévenir si non planifiées ≥",
      never: "jamais exécutée",
      counts:
        "{{findings}} constats · {{proposals}} propositions · {{notifications}} notifications",
      unplanned: "{{count}} non planifiée(s)",
      skipped: {
        no_open_orders:
          "rien à planifier — aucune commande ouverte pour demain",
      },
      footer:
        "Les changements s'appliquent à la prochaine exécution. Le conseil de tournée n'applique jamais un plan : les manifestes restent le clic d'un dispatcher.",
      readOnly:
        "Lecture seule : les réglages demandent la permission paramètres.",
      jobs: {
        fuel_findings: {
          name: "Analyste carburant",
          what: "les constats sur tous les rapports ; finance et les gestionnaires de flotte prévenus",
        },
        maintenance_scan: {
          name: "Conseiller entretien",
          what: "toute la flotte balayée ; brouillons d'ordres de travail, demandes de pièces et indices de cycle de vie déposés en propositions",
        },
        route_advice: {
          name: "Conseiller de tournée",
          what: "le plan de demain sur les commandes ouvertes, le conseil dessus ; les dispatchers prévenus quand des commandes restent non planifiées (sur activation)",
        },
      },
    },
    docs: {
      title: "Documents",
      hint: "Les manuels, procédures et politiques de la société qu'Ask OneFleet et les agents citent — recherchés en tant que la personne connectée sur les collections que ses rôles peuvent lire ; un passage revient avec son document, sa page et son titre. Texte, Markdown ou PDF.",
      count: "{{count}} document(s)",
      noCollections:
        "Aucune collection encore — le premier document en crée une.",
      empty:
        "Aucun document encore. Un administrateur ajoute le manuel, les procédures et les politiques ci-dessous ; Ask OneFleet répond ensuite à un « comment faire… » à partir d'eux.",
      col: {
        title: "Titre",
        collection: "Collection",
        state: "État",
        added: "Ajouté",
      },
      collections: {
        manuals: "Manuels",
        sops: "Procédures (SOP)",
        policies: "Politiques",
      },
      indexed: "indexé · {{pages}} page(s) · {{chunks}} passage(s)",
      failed: "échec :",
      pending: "indexation…",
      remove: "Retirer",
      add: "Ajouter un document",
      untitled: "Sans titre",
      titlePlaceholder: "Procédure des reçus carburant",
      file: "Fichier (txt, md, pdf)",
      text: "Ou collez le texte (les titres Markdown deviennent les titres des passages)",
      textPlaceholder:
        "# Reçus carburant\n\n## Enregistrer un reçu\n\nChaque plein est enregistré le jour même…",
      roles: "Qui peut lire",
      rolesHint:
        "rien de coché = toute la société ; les rôles s'appliquent à toute la collection",
      submit: "Ajouter et indexer",
      indexing: "Indexation…",
      search: "Essayer une question",
      searchPlaceholder: "comment enregistrer un reçu carburant",
      searchGo: "Chercher",
      noHits: "Rien dans les documents sur « {{q}} ».",
      lexical: "mots",
      semantic: "sens",
      footer:
        "Les documents sont indexés aussitôt via la piste embeddings de la passerelle ; un fichier identique n'est pas ajouté deux fois. Retirer un document retire ses passages.",
      readOnly:
        "Lecture seule : ajouter et retirer des documents demande le rôle admin du realm.",
    },
  },
  trips: {
    title: "Trajets",
    hint: "Ce que les photos de compteur des chauffeurs au départ et à la clôture de leurs courses totalisent par véhicule — face aux km prévus des mêmes commandes, aux litres du mois et aux L/100 km sur les km de trajet. Ambre à partir de 15 % au-delà du plan, rouge à partir de 30 %.",
    period: "Mois",
    kpi: {
      trips: "Trajets clos",
      odometer: "Au compteur",
      planned: "Prévu",
      variance: "Écart",
      litres: "Carburant",
      l100: "Consommation",
    },
    col: {
      vehicle: "Véhicule",
      trips: "Trajets",
      odometer: "Km compteur",
      planned: "Km prévus",
      variance: "Écart",
      litres: "Litres",
      l100: "L/100 km",
      fuel: "Dépense carburant",
    },
    empty:
      "Aucun trajet clos avec ses deux relevés de compteur en {{period}} — l'application chauffeur les enregistre au départ et à la clôture d'une course (Assistant › photo du compteur).",
    footer:
      "{{beyond}} véhicule(s) à regarder ce mois (au-delà du plan, ou relevés impossibles). L'analyste carburant rapproche les litres de ces km ; le conseiller de tournée lit le même récapitulatif.",
    openAnalyst: "Analyste carburant",
    openRuns: "Tournées",
    suspect:
      "Très en dessous du plan : un relevé manque ou se répète — vérifiez les photos de départ / de fin.",
  },
  runs: {
    title: "Tournées optimisées",
    hint: "Les commandes ouvertes d'une journée planifiées sur les camions avec chauffeur par le solveur du tenant — appliquer expédie les feuilles de route, proposer les soumet à une seconde personne",
    empty: "Aucune tournée — planifiez une journée.",
    pick: "Choisissez une tournée à gauche, ou planifiez une journée.",
    last: "Dernier plan :",
    row: "{{routes}} itinéraires · {{orders}} commandes · {{unassigned}} non affectées",
    state: {
      draft: "Brouillon",
      computed: "Calculée",
      proposed: "Proposée",
      applied: "Appliquée",
      rejected: "Rejetée",
      failed: "Échec",
    },
    fallback: "heuristique de repli ({{provider}})",
    fallbackShort: "heuristique",
    solver: "solveur {{provider}}",
    computed: "calculée {{when}}",
    scope: {
      day: "les commandes ouvertes du jour",
      orders: "{{count}} commandes",
      vehicles: "{{count}} véhicules",
    },
    proposalPending:
      "Soumise comme proposition — un second dispatcheur décide dans la boîte.",
    openInbox: "Ouvrir la boîte →",
    summary: {
      routes: "Itinéraires",
      orders: "Commandes",
      unassigned: "Non affectées",
      distance: "Distance",
    },
    actions: {
      compute: "Recalculer",
      apply: "Appliquer — expédier les feuilles de route",
      applyConfirm:
        "Créer et expédier {{count}} feuille(s) de route maintenant ? Les commandes sont affectées aux chauffeurs des camions.",
      propose: "Proposer (une seconde personne décide)",
      delete: "Supprimer",
      deleteConfirm: "Supprimer cette tournée ?",
    },
    noRoutes: "Aucun itinéraire — rien n'a pu être planifié.",
    unassigned: "{{count}} non affectées",
    skipped: "{{count}} véhicules ignorés",
    manifests: "{{count}} feuilles de route",
    dispatched: "expédiée",
    route: {
      facts: "{{stops}} arrêts · {{distance}} · {{time}} · {{load}} kg",
      noDriver: "sans chauffeur",
    },
    stop: { pickup: "enlèvement", dropoff: "livraison" },
    plan: {
      open: "Planifier une journée",
      close: "Fermer",
      date: "Jour",
      name: "Nom",
      namePlaceholder: "Plan {{date}}",
      vehicles: "Véhicules (vide = tous les camions actifs avec chauffeur)",
      noDriver: "pas de chauffeur principal — ignoré par le plan",
      picked: "{{count}} choisis",
      allWithDriver: "Tous les camions actifs avec un chauffeur principal.",
      forceHeuristic:
        "Sans solveur — heuristique du plus proche voisin (signalée)",
      submit: "Planifier",
      computing: "Calcul…",
    },
  },
  rates: {
    title: "Tarifs de service",
    hint: "Les lignes tarifaires alimentent les devis, le tarif d'achat de la commande et les lignes de facture ERP — montants dans la devise du tarif",
    empty: "Aucun tarif.",
    new: "Nouveau tarif",
    close: "Fermer",
    readOnly: "Lecture seule — les administrateurs modifient les tarifs.",
    showInactive: "afficher les inactifs",
    method: {
      fixed: "Fixe",
      per_km: "Au km",
      tiered: "Tranches de distance",
      per_parcel: "Au colis",
    },
    col: {
      name: "Tarif",
      method: "Méthode",
      base: "Base",
      perKm: "Au km",
      min: "Minimum",
      bands: "Tranches",
      surcharges: "Suppléments",
      scope: "Portée",
      priority: "Priorité",
      active: "Actif",
    },
    noBands: "—",
    cod: "Contre-remboursement",
    peak: "Pointe",
    any: "tous",
    anyType: "tout type",
    edit: "Modifier",
    editTitle: "Modifier {{name}}",
    days: "{{count}} jour(s)",
    active: "actif",
    inactive: "inactif",
    f: {
      name: "Nom",
      method: "Méthode",
      currency: "Devise",
      base: "Frais de base",
      perKm: "Au km",
      min: "Frais minimum",
      maxKm: "Distance max (km)",
      priority: "Priorité (le plus bas d'abord)",
      type: "Type de commande",
      zone: "Zone",
      cod: "Supplément contre-remboursement (%)",
      bands:
        "Tranches de distance — une par ligne : `min-max: frais` (sans max = repli)",
      parcels: "Tranches colis — une par ligne : `taille ≤ kg: frais`",
      terms: "Délai",
      days: "Jours estimés",
      submit: "Créer le tarif",
      save: "Enregistrer le tarif",
    },
  },
  zones: {
    title: "Zones de service",
    hint: "Des polygones avec déclencheurs — entrée, sortie, stationnement, excès de vitesse — qui alimentent les alertes, les filtres de dispatch et les tarifs par zone",
    empty: "Aucune zone.",
    new: "Nouvelle zone",
    close: "Fermer",
    draft: "nouvelle zone",
    kind: {
      service_area: "Zone de service",
      geofence: "Géorepérage",
      depot: "Dépôt",
      no_go: "Interdite",
      speed: "Zone de vitesse",
    },
    alert: {
      enter: "à l'entrée",
      exit: "à la sortie",
      dwell: "au stationnement",
      speeding: "en excès de vitesse",
    },
    rule: {
      enter: "entrée",
      exit: "sortie",
      dwell: "stationnement {{min}} min",
      speed: "≤ {{kmh}} km/h",
      alert: "alerte : {{alert}}",
      none: "aucun déclencheur",
    },
    corners: "{{count}} sommets",
    editCorners: "Modifier les sommets",
    dragHint: "déplacez les sommets sur la carte, puis enregistrez",
    saveCorners: "Enregistrer les sommets",
    active: "active",
    inactive: "inactive",
    delete: "Supprimer",
    deleteConfirm: "Supprimer la zone {{name}} ?",
    f: {
      name: "Nom",
      kind: "Type",
      color: "Couleur",
      dwell: "Stationnement (min)",
      speed: "Limite de vitesse (km/h)",
      alert: "Alerte",
      points: "{{count}} sommets placés — cliquez la carte pour en ajouter",
      clickMap: "Cliquez la carte pour placer les sommets (3 ou plus)",
      clear: "Effacer",
      paste: "…ou collez un Polygon GeoJSON, ou `lng, lat` par ligne",
      submit: "Créer la zone",
    },
  },
  schedule: {
    title: "Planning de la semaine",
    hint: "Les modèles construisent la semaine, les conflits sont calculés, la publication soumet les affectations de poste à l'ERP — une personne décide",
    driver: "Chauffeur",
    weekOf: "Semaine du {{from}} au {{to}}",
    thisWeek: "cette semaine",
    state: { draft: "Brouillon", published: "Publiée" },
    shiftState: {
      draft: "brouillon",
      published: "publié",
      cancelled: "annulé",
    },
    counts:
      "{{shifts}} postes · {{drivers}} chauffeurs · {{hours}} h · {{conflicts}} conflits",
    publishedAt: "publiée {{when}}",
    build: "Construire depuis les modèles",
    built: "{{count}} poste(s) créé(s) depuis les modèles.",
    addShift: "Ajouter un poste",
    publish: "Publier la semaine",
    publishConfirm:
      "Publier {{count}} poste(s) ? Avec une connexion ERP qui prend les postes, les Shift Assignments sont soumis comme proposition.",
    publishForce:
      "{{count}} conflit(s) de sévérité haute. Publier quand même ?",
    close: "Fermer",
    delete: "Supprimer",
    cancelShift: "Annuler le poste",
    conflicts: "Conflits ({{count}})",
    noConflicts: "Aucun conflit cette semaine.",
    noDrivers: "Aucun chauffeur actif.",
    noTemplates: "Aucun modèle.",
    noUpcoming: "Aucun poste à venir.",
    upcoming: "Postes à venir",
    templates: "Modèles",
    templatesTitle: "Modèles de poste",
    templatesHint:
      "Les motifs hebdomadaires que Construire depuis les modèles transforme en postes — toutes les N semaines à partir d'un lundi d'ancrage, dans une fenêtre de validité.",
    templatesCount: "{{count}} modèle(s) actif(s)",
    templatesOpen: "Gérer les modèles",
    everyWeeks: "toutes les {{count}} semaines",
    dragHint:
      "Glissez un bloc brouillon vers un autre chauffeur ou un autre jour — même heure, même durée ; les conflits sont recalculés.",
    tzHint:
      "Le fuseau horaire de la société — les jours, blocs et heures s'y lisent.",
    newTemplate: "Nouveau modèle",
    tplDrivers: "{{count}} chauffeurs",
    tplAll: "tous les chauffeurs actifs",
    activate: "Activer",
    deactivate: "Désactiver",
    deleteTemplateConfirm: "Supprimer le modèle {{name}} ?",
    legend: "Légende",
    legendLeave: "absence approuvée",
    legendHeld: "demande en attente",
    legendDashed: "Pointillé = brouillon ; plein = publié ; barré = annulé.",
    kind: { shift: "Poste", run: "Tournée", standby: "Astreinte" },
    rule: {
      overlap: "chevauchement",
      leave: "absence",
      document_expired: "document expiré",
      document_expiring: "document expirant",
      unavailable: "indisponible",
      daily_duty: "amplitude journalière",
      weekly_hours: "heures hebdo",
      daily_rest: "repos journalier",
    },
    strip: {
      today: "{{count}} postes aujourd'hui",
      requests: "{{count}} demandes ouvertes",
      documents: "{{expired}} expirés · {{expiring}} expirant",
    },
    erp: {
      title: "ERP :",
      none: "aucune connexion",
      shifts: "postes",
      leave: "absences",
      fusionShifts:
        "Oracle Fusion ne prend pas les affectations de poste en v1 (HDL plus tard)",
    },
    f: {
      name: "Nom",
      start: "Début",
      end: "Fin",
      kind: "Type",
      shiftType: "Type de poste ERP",
      drivers: "Chauffeurs (vide = tous les actifs)",
      days: "Jours de la semaine",
      interval: "Toutes les N semaines",
      anchor: "À partir de la semaine du",
      validFrom: "Valide du",
      validTo: "Valide jusqu'au",
      day: "Jour",
      notes: "Notes",
      submit: "Créer le modèle",
    },
  },
  timeOff: {
    title: "Absences",
    hint: "Une demande devient une proposition de Leave Application quand l'ERP prend les absences — son approbateur décide ; sinon un gestionnaire de flotte décide ici",
    empty: "Aucune demande.",
    new: "Demander une absence",
    note: "Note (facultative)",
    approve: "Approuver",
    reject: "Refuser",
    cancel: "Annuler",
    cancelConfirm: "Annuler cette demande ?",
    decided: "décidé par {{who}} {{when}}",
    proposalPending:
      "Soumise comme proposition — une personne décide dans la boîte, puis l'approbateur de l'ERP.",
    state: {
      requested: "Demandée",
      proposed: "Proposée",
      approved: "Approuvée",
      rejected: "Refusée",
      cancelled: "Annulée",
    },
    kind: {
      leave: "Congé",
      sick: "Maladie",
      training: "Formation",
      other: "Autre",
    },
    source: { console: "console", driver_app: "appli chauffeur", erp: "ERP" },
    f: {
      kind: "Type",
      leaveType: "Type de congé ERP",
      from: "Du",
      to: "Au",
      halfDay: "demi-journée",
      reason: "Motif",
      submit: "Demander",
    },
  },
  availability: {
    title: "Disponibilité et temps de service",
    hint: "Le temps de service d'une journée à partir des positions, et les fenêtres de disponibilité que lisent les conflits de la semaine",
    windows: "Fenêtres de disponibilité",
    windowsHint:
      "Récurrentes par jour de semaine, ou une fenêtre datée ; les indisponibilités lèvent des conflits sur le planning",
    pickDriver: "Choisir un chauffeur",
    empty: "Aucune fenêtre — disponible à tout moment.",
    kind: { available: "disponible", unavailable: "indisponible" },
    f: {
      kind: "Type",
      weekday: "Jour de semaine",
      onDate: "Un jour (facultatif)",
      submit: "Ajouter la fenêtre",
    },
  },
  hos: {
    day: "Jour",
    empty: "Aucun chauffeur actif.",
    ok: "OK",
    breakDue: "pause due",
    breakIn: "Pause due après {{in}} de conduite",
    left: "reste {{left}}",
    driving: "Conduite",
    duty: "Amplitude",
    week: "Semaine",
    profile: "Profil temps de service",
    violation: {
      daily_driving_exceeded: "conduite journalière dépassée",
      daily_duty_exceeded: "amplitude journalière dépassée",
      weekly_driving_exceeded: "conduite hebdo dépassée",
      break_overdue: "pause en retard",
    },
    f: {
      regime: "Régime",
      drive: "Conduite / jour (h)",
      duty: "Amplitude / jour (h)",
      week: "Conduite / semaine (h)",
      breakAfter: "Pause après (h)",
      breakMin: "Pause (min)",
      rest: "Repos journalier (h)",
      save: "Enregistrer le profil",
    },
  },
  scorecards: {
    title: "Fiches de performance",
    hint: "Une ligne par chauffeur et par mois, agrégée depuis les commandes, positions, événements boîtier, carburant et temps de service",
    period: "Mois",
    compute: "Calculer le mois",
    computing: "Calcul…",
    computedAt: "calculé {{when}}",
    empty: "Aucune fiche pour ce mois — calculez-la.",
    weights:
      "Score : ponctualité 40 · POD 15 · sécurité 25 (brusques + vitesse / 100 km) · temps de service 10 · carburant 10 (vs l'habituel du véhicule).",
    col: {
      score: "Score",
      orders: "Faites / commandes",
      onTime: "À l'heure",
      pod: "POD",
      events: "Brusques / vitesse",
      hos: "Viol. TS",
      days: "Jours",
    },
  },
  expiries: {
    title: "Échéances",
    empty: "Rien n'expire sous 90 jours.",
    inDays: "dans {{days}} j",
    expiredAgo: "expiré depuis {{days}} j",
  },
  documents: {
    add: "Ajouter un document",
    empty: "Aucun document — le permis ci-dessus vient du profil du chauffeur.",
    issued: "délivré le {{on}}",
    deleteConfirm: "Supprimer le document {{name}} ?",
    sweep:
      "Les statuts sont recalculés chaque matin à 06:15 ; une alerte est levée une fois par seuil franchi (30 / 14 / 7 jours, puis l'expiration).",
    kind: {
      licence: "Permis",
      medical: "Médical",
      id: "Identité",
      permit: "Autorisation",
      training: "Formation",
      contract: "Contrat",
      insurance: "Assurance",
      technical_inspection: "Contrôle technique",
      emissions: "Certificat d'émissions",
      road_tax: "Vignette",
      transport_licence: "Licence de transport",
      registration: "Carte rose",
      other: "Autre",
    },
    state: { valid: "valide", expiring: "expirant", expired: "expiré" },
    f: {
      kind: "Type",
      name: "Nom",
      number: "Numéro",
      authority: "Délivré par",
      issued: "Délivré le",
      expires: "Expire le",
      notes: "Notes",
      submit: "Ajouter",
    },
  },
  advisor: {
    title: "Conseiller maintenance",
    manual: "Manuel « {{title}} » p.{{page}} :",
    hint: "Les constats classés par les règles — entretiens dus, codes défaut, signalements ouverts, pièces manquantes, pannes répétées — racontés par le modèle ; les propositions vont dans la boîte",
    run: "Lancer le conseiller",
    running: "Analyse…",
    propose: "Déposer {{count}} proposition(s)",
    filing: "Dépôt…",
    filed:
      "{{filed}} proposition(s) déposée(s) dans la boîte, {{executed}} exécutée(s) par la politique.",
    empty: "Aucun constat.",
    noProposal: "rien à proposer",
    severity: { high: "critique", medium: "à traiter", low: "à surveiller" },
    kind: {
      overdue: "en retard",
      due_soon: "bientôt dû",
      issue: "signalement",
      dtc: "code défaut",
      parts_short: "pièces manquantes",
      repeated_failures: "pannes répétées",
    },
    action: {
      "maintenance.draft_work_order": "brouillon d'ordre de travail",
      "maintenance.request_parts": "demande de pièces (ERP)",
      "assets.lifecycle_hint": "revue de cession",
    },
  },
  adminAddons: {
    forTenant: "Pour {{name}} ({{slug}})",
    tenants: {
      title: "Tenants",
      hint: "Chaque société qu'OneFleet connaît : sa langue et ses unités, ses membres actifs, ses connexions ERP et ce qu'elle contient. Choisissez une ligne — le formulaire de provisionnement, les connexions et le fournisseur de cartes ci-dessous agissent sur elle.",
      new: "Nouveau tenant",
      cancel: "Annuler",
      none: "Aucun tenant encore.",
      created:
        "Tenant {{slug}} créé — ajoutez son groupe /Scope/Company dans le realm, puis provisionnez sa connexion ERP.",
      mapsDefault: "par défaut",
      fleetTitle: "véhicules / chauffeurs / commandes",
      status: { active: "actif", inactive: "inactif" },
      deactivate: "Désactiver",
      reactivate: "Réactiver",
      col: {
        tenant: "Tenant",
        erpCompany: "Société ERP",
        locale: "Devise · pays · fuseau",
        members: "Membres",
        status: "Statut",
        fleet: "Véhicules / chauffeurs / commandes",
        connections: "Connexions ERP",
        maps: "Cartes",
        since: "Depuis",
      },
      form: {
        name: "Nom",
        slug: "Slug",
        erpCompany: "Société ERP (le groupe /Scope/Company)",
        currency: "Devise",
        country: "Pays",
        timezone: "Fuseau horaire",
        create: "Créer le tenant",
        withGroup:
          "Créer aussi son groupe /Scope/Company/<société ERP> dans le realm (quand l'API est configurée pour l'API d'administration du realm)",
        hint: "Crée la ligne société dans OneFleet. Ses utilisateurs se connectent par le groupe /Scope/Company/<société ERP> du realm — créé ici si coché, sinon par keycloak-scope ; la connexion ERP est le formulaire ci-dessus ; rien n'est écrit dans un ERP.",
      },
    },
    realm: {
      title: "Groupe du realm",
      hint: "Le groupe Keycloak que rejoignent les utilisateurs du tenant pour le voir (D-28) : /Scope/Company/<société ERP>.",
      unconfigured:
        "L'API n'a pas d'administrateur du realm configuré (KEYCLOAK_ADMIN_URL / USER / PASSWORD) — créez le groupe avec keycloak-sync-scope ou dans la console du realm.",
      exists: "présent dans le realm",
      missing: "absent du realm",
      create: "Créer le groupe",
      created: "Créé.",
      footer:
        "Seuls les groupes sont touchés ici — personnes, rôles et clients restent à la console du realm.",
    },
    modules: {
      title: "Commutateurs de modules",
      hint: "Les modules D-17 par connexion ERP — combien de capacités sont activées, combien d'entre elles écrivent dans l'ERP. Elles se règlent sur ERP Hub › Capacités.",
      none: "Pas encore de connexion ERP — provisionnez-en une ci-dessus.",
      col: { module: "Module" },
      name: {
        assets: "Actifs et cycle de vie",
        dispatch: "Dispatch et ventes",
        drivers: "Chauffeurs et RH",
        maintenance: "Maintenance et pièces",
        fuel: "Carburant et allocation des coûts",
        master: "Données de base",
        events: "Événements",
      },
      writes: "{{count}} écriture(s)",
      manage: "ERP Hub › Capacités →",
    },
    geo: {
      title: "Fournisseur de cartes",
      hint: "Tuiles, géocodage, itinéraires et optimisation pour ce tenant (D-14) : Google Maps avec ses clés, la pile OSM auto-hébergée du compose OneFleet, ou la ligne droite. La clé serveur est scellée au repos et jamais relue.",
      pickTenant: "Choisissez un tenant ci-dessus.",
      current: "Aujourd'hui : {{provider}} ({{source}}) · {{key}}",
      source: {
        tenant: "défini pour ce tenant",
        defaults: "les valeurs par défaut du déploiement",
      },
      keyStored: "clé serveur enregistrée",
      keyNone: "pas de clé serveur",
      provider: "Fournisseur",
      providers: {
        straight: "Lignes droites (sans fournisseur)",
        "osm-selfhosted": "OSM auto-hébergé (OSRM · VROOM · Photon)",
        google: "Google Maps",
      },
      country: "Pays de préférence (ISO-2)",
      avgKmh: "Vitesse urbaine supposée (km/h)",
      apiKey: "Clé serveur Google (Geocoding + Routes)",
      browserKey: "Clé navigateur Google (Maps JS, limitée par référent)",
      styleUrl: "URL du style MapLibre (vide = le style raster OSM)",
      lat: "Centre de la carte — latitude",
      lng: "Centre de la carte — longitude",
      save: "Enregistrer le fournisseur",
      reset: "Retour aux valeurs par défaut",
      saved:
        "Enregistré — les cartes de la console utilisent {{provider}} au prochain chargement.",
      footer:
        "Une clé serveur absente garde celle enregistrée ; les services d'itinéraire et d'optimisation du compose (profil `geo`) répondent au fournisseur auto-hébergé.",
    },
  },
  settingsGeneral: {
    title: "Général",
    hint: "Le tenant tel qu'OneFleet le connaît : l'identité que le realm et l'ERP lui ont donnée (lecture seule), le nom affiché, la devise et le fuseau, la langue et les unités des écrans.",
    identity: "Identité",
    scanning: {
      title: "Lecture de codes",
      hint: "Comment les marchandises et les documents sont saisis. La lecture est le réglage par défaut : un code-barres, un QR code ou une étiquette RFID identifie ce qui a bougé, et le lecteur peut être l'appareil photo de l'application, un scanner à main, une douchette ou un portique.",
      enabled: "Saisie par lecture de code",
      enabledHint: "Désactivez-le et tout est saisi à la main.",
      materials: "Les marchandises bougent sur lecture",
      materialsHint:
        "Une ligne doit présenter le code de l'article, ou le numéro de série du colis lui-même.",
      documents: "Les documents bougent sur lecture",
      documentsHint:
        "Un chauffeur ou un préparateur avance une mission en lisant sa propre étiquette, pas celle d'une autre.",
      allowTyped:
        "Autoriser la saisie manuelle quand une étiquette est illisible",
      allowTypedHint:
        "À laisser désactivé : une porte de secours ouverte n'est que la saisie manuelle avec des étapes en plus. Toute saisie manuelle est enregistrée comme telle, avec son auteur.",
    },
    slug: "Slug",
    erpCompany: "Société ERP",
    name: "Nom affiché",
    identityHint:
      "Le slug et la société ERP viennent d'OneSphere Admin › Add-ons et du groupe de périmètre du realm ; ils ne changent pas ici.",
    locale: "Langue et unités",
    currency: "Devise",
    timezone: "Fuseau horaire",
    language: "Langue",
    distanceUnit: "Distance",
    weekStart: "La semaine commence le",
    days: { monday: "lundi", sunday: "dimanche" },
    saved: "Enregistré.",
    readOnly:
      "Lecture seule : les changements demandent la permission paramètres.",
  },
  orderTypes: {
    title: "Types de commande et flux",
    hint: "Chaque type de commande porte un flux versionné — états, transitions avec leurs gardes et actions, la preuve demandée, les hooks ERP sur les états. Les commandes gardent la version où elles ont commencé.",
    meta: "{{status}} · preuve {{pod}} · flux v{{version}}",
    noPod: "aucune",
    openWorkflow: "Ouvrir le flux",
    close: "Fermer",
    noWorkflow: "Aucun flux publié.",
    workflowMeta:
      "Version active {{version}} sur {{versions}} · état initial {{initial}}",
    customerVisible: "visible client",
    col: {
      state: "État",
      activity: "Activité",
      scope: "Étape",
      hooks: "Hooks ERP",
      event: "Événement",
      from: "De",
      to: "Vers",
      guards: "Gardes",
      actions: "Actions",
    },
    simulate: {
      events: "Simulation — événements, dans l'ordre",
      run: "Simuler",
    },
    form: {
      key: "Clé",
      name: "Nom",
      pod: "Preuve",
      podRequired: "Preuve requise",
      create: "Nouveau type",
      hint: "Un nouveau type démarre sur le flux de livraison par défaut ; ouvrez-le pour éditer un brouillon de ses états, transitions et hooks ERP et le publier.",
    },
    designer: {
      versions: "Versions",
      status: { active: "active", draft: "brouillon", retired: "retirée" },
      newDraft: "Nouveau brouillon depuis la version active",
      openDraft: "Éditer le brouillon v{{version}}",
      name: "Nom du flux",
      initial: "État initial",
      notes: "Notes",
      statusText: "Texte du statut",
      flags: "Indicateurs",
      flag: {
        customer_visible: "client",
        tracking: "suivi",
        complete: "terminé",
        terminal: "terminal",
      },
      remove: "retirer",
      addState: "Ajouter un état",
      addTransition: "Ajouter une transition",
      addHook: "Ajouter un hook ERP",
      onEnter: "À l'entrée de",
      capability: "Capacité",
      approval: "Approbation",
      validate: "Valider (simulation)",
      save: "Enregistrer le brouillon",
      publish: "Publier",
      discard: "Abandonner le brouillon",
      saveFirst: "Enregistrez d'abord le brouillon",
      publishHint:
        "Publier fait du brouillon la version active et retire la précédente ; les commandes en cours gardent la version où elles ont commencé.",
      noPath: "Aucun chemin depuis l'état initial — vérifiez les transitions.",
      description: "Description",
      typeStatus: "Type",
      saveType: "Enregistrer le type",
      saved: "Enregistré.",
    },
    footer:
      "■ état terminal · ▶ état initial. La simulation applique les événements au flux enregistré (ou au brouillon) et montre chaque transition et les hooks ERP qu'elle déclencherait — rien n'est écrit.",
  },
  branding: {
    title: "Identité visuelle",
    hint: "Ce que les clients et les chauffeurs voient de la société : le nom et l'accroche sur la page de suivi publique et dans l'app chauffeur, les couleurs, le logo, les contacts d'assistance, la ligne sous le statut d'une livraison.",
    field: {
      display_name: "Nom affiché",
      tagline: "Accroche",
      support_phone: "Téléphone d'assistance",
      support_email: "E-mail d'assistance",
      tracking_message: "Message sur la page de suivi",
      primary_color: "Couleur principale",
      accent_color: "Couleur d'accent",
    },
    placeholder: {
      tracking_message:
        "Merci pour votre confiance — une question ? Appelez-nous.",
    },
    clear: "effacer",
    logo: "Logo",
    noLogo: "Pas encore de logo.",
    uploadLogo: "Charger un logo",
    removeLogo: "Retirer le logo",
    logoHint:
      "PNG, JPEG, SVG ou WebP jusqu'à {{kb}} Ko ; public sur /v1/branding/<société>/logo.",
    preview: "Aperçu",
    previewOrder: "ORD-2026-0001 · Kinshasa → Matadi",
    previewStatus: "Livré · aujourd'hui 14:05",
    previewHint:
      "La page de suivi publique et l'app chauffeur lisent GET /v1/branding/{{slug}} — sans connexion.",
    footer:
      "Les notifications gardent leur formulation (Paramètres › Notifications) ; rien ici n'atteint l'ERP.",
  },
  security: {
    title: "Sécurité",
    hint: "Les règles de connexion et de session des chauffeurs et la rétention des journaux de requêtes, par-dessus les valeurs par défaut du déploiement ; les personnes, rôles, la double authentification et la politique de mot de passe sont ceux du realm OneSphere.",
    knobs: "Règles",
    col: {
      knob: "Règle",
      effective: "En vigueur",
      source: "Source",
      own: "Valeur du tenant",
    },
    knob: {
      driver_otp_ttl_s: "Durée du code à usage unique (secondes)",
      driver_otp_max_attempts: "Essais erronés avant un nouveau code",
      driver_session_days: "Durée de session chauffeur (jours)",
      request_log_days: "Journaux de requêtes API conservés (jours)",
    },
    knobHint: {
      driver_otp_ttl_s: "Le code SMS que le chauffeur saisit à la connexion.",
      driver_otp_max_attempts:
        "Ensuite le code est consommé et le chauffeur en demande un autre.",
      driver_session_days: "Ensuite l'app reconnecte le chauffeur.",
      request_log_days: "Les journaux /v1 de l'écran Développeur.",
    },
    source: { tenant: "le tenant", default: "le défaut ({{value}})" },
    emptyIsDefault: "Un champ vide signifie la valeur par défaut.",
    sessions: "Sessions chauffeur actives",
    sessionsHint:
      "Applications chauffeur connectées avec une session valide ; déconnecter tout le monde y met fin maintenant — les apps redemandent un code.",
    signOutAll: "Déconnecter tous les chauffeurs",
    signedOut: "{{count}} session(s) révoquée(s).",
    devEcho:
      "Pile dev : le code à usage unique est renvoyé par l'API (pas de fournisseur SMS).",
    keys: "Clés API actives",
    keysHint: "Clés et leurs portées, journaux de requêtes et webhooks :",
    iam: "Personnes, rôles, 2FA",
    iamHint:
      "Les membres, les rôles (rôles realm of_*), la double authentification et la politique de mot de passe se gèrent dans le realm OneSphere.",
    realmLink: "Ouvrir la console de compte du realm",
    footer:
      "Chaque valeur s'applique dès la prochaine connexion ; les sessions en cours gardent la durée reçue.",
  },
  integrations: {
    title: "Intégrations",
    hint: "Tout ce à quoi la société est raccordée, en une lecture — chaque carte mène à l'écran où cela se gère.",
    manage: "Gérer →",
    tone: { ok: "actif", warn: "attention", off: "inactif", info: "info" },
    yes: "oui",
    no: "non",
    keys: "{{count}} clé(s) API active(s)",
    erp: {
      title: "ERP",
      none: "Pas encore de connexion ERP.",
      health: "santé {{at}}",
      pending: "{{count}} proposition(s) en attente d'approbation",
    },
    maps: {
      title: "Cartes",
      provider: "Fournisseur",
      tiles: "Tuiles",
      key: "Clé API",
      tenant: "du tenant",
      defaults: "du déploiement",
    },
    telematics: {
      title: "Télématique",
      gateway: "Passerelle",
      devices:
        "{{total}} boîtier(s) · {{active}} actif(s) · {{fresh}} avec une position en 24 h",
      events: "{{open}} événement(s) ouvert(s) · dernier {{at}}",
    },
    fuel: {
      title: "Fournisseurs de carburant",
      providers: "{{count}} fournisseur(s) actif(s)",
      batches: "{{count}} lot(s) · dernier {{at}}",
    },
    messaging: {
      title: "Messagerie",
      "dev-echo": "écho dev",
      platform: "services de plateforme",
      smtp: "SMTP",
      "not-configured": "non configuré",
    },
    webhooks: {
      title: "Webhooks et API",
      endpoints:
        "{{total}} point(s) de terminaison · {{active}} actif(s) · {{failing}} en échec",
    },
    ai: {
      title: "IA",
      api: "API IA",
      gateway: "Passerelle",
      coreOnly: "cœur seul (pas d'API IA)",
    },
    footer:
      "Lecture seule : les connexions ERP se provisionnent depuis l'Administration OneSphere, le fournisseur de cartes depuis Admin › Add-ons, les flux et clés depuis Développeur.",
  },
  customFields: {
    title: "Champs personnalisés",
    hint: "Des champs en plus sur les fiches — véhicules, chauffeurs, commandes, ordres de travail… — typés, facultatifs ou requis, groupés et ordonnés sur les formulaires ; les valeurs voyagent sur chaque fiche en custom_fields.",
    allSubjects: "Toutes les fiches",
    options: "Options",
    create: "Ajouter le champ",
    empty: "Aucun champ personnalisé encore.",
    confirmRemove:
      "Retirer le champ {{label}} ? Les valeurs déjà stockées restent sur les fiches.",
    footer:
      "Libellé, requis, groupe et ordre s'enregistrent à la sortie du champ ; la clé et le type sont fixés à la création (un nouveau champ pour un nouveau type).",
    col: {
      subject: "Fiche",
      key: "Clé",
      label: "Libellé",
      type: "Type",
      required: "Requis",
      group: "Groupe",
      order: "Ordre",
    },
    subjects: {
      vehicle: "Véhicule",
      asset: "Actif",
      equipment: "Équipement",
      driver: "Chauffeur",
      fleet: "Flotte",
      vendor: "Fournisseur",
      contact: "Client / contact",
      place: "Lieu",
      order: "Commande",
      work_order: "Ordre de travail",
      fuel_report: "Rapport carburant",
      issue: "Signalement",
      company: "Société",
    },
  },
  driverAppSettings: {
    title: "Application chauffeur",
    hint: "Ce que l'application chauffeur installée et la PWA lisent du tenant à la connexion : la cadence de suivi, la fenêtre hors ligne, les règles de preuve — lus avec le profil du chauffeur à la connexion ; les valeurs par défaut de l'application s'appliquent là où le tenant ne dit rien.",
    tracking: "Suivi et hors ligne",
    interval: "Intervalle de suivi au premier plan (secondes)",
    offlineDays: "Courses gardées hors ligne (jours)",
    navigate: "Naviguer avec",
    apps: {
      google: "Google Maps",
      waze: "Waze",
      system: "l'application par défaut du téléphone",
    },
    proof: "Preuve de livraison",
    photoRequired: "Une photo est requise avec chaque preuve",
    proofHint:
      "La méthode de preuve du type de commande (signature, photo, QR, note) s'applique toujours ; ceci ajoute une photo en plus.",
    signIn: "Connexion",
    signInHint:
      "Les chauffeurs se connectent avec leur téléphone et un code à usage unique (SMS via les services de plateforme ; renvoyé en clair sur la pile dev).",
    footer:
      "Le GPS en arrière-plan, les notifications push et la chaîne de publication sont ceux de la coque Capacitor (WP-U18) ; ces réglages lui parviennent tels quels.",
  },
  notifSettings: {
    title: "Notifications",
    hint: "Les modèles depuis lesquels les règles et les agents envoient — un agent nomme un modèle et ses paramètres, jamais le texte — et les notifications récentes de la société.",
    templatesHint:
      "Chaque modèle existe en FR, EN et LN ; la langue est celle du destinataire.",
    recent: "Notifications récentes",
    none: "Rien d'envoyé encore.",
    col: {
      template: "Modèle",
      title: "Titre",
      params: "Paramètres",
      channels: "Canaux",
      when: "Quand",
      channel: "Canal",
      to: "À",
      status: "État",
    },
    footer:
      "Les canaux SMS / WhatsApp arrivent avec les services de plateforme ; l'e-mail part par le SMTP du tenant en proposition.",
    wording: {
      custom: "personnalisé",
      placeholders: "Espaces réservés",
      body: "Corps",
      save: "Enregistrer la formulation",
      reset: "Revenir au défaut",
    },
  },
  analytics: {
    title: "Tableaux de bord",
    hint: "Le service et le coût de la flotte, calculés à la demande depuis les données d'OneFleet — livraisons à l'heure, coût au km depuis le TCO (carburant, entretien, amortissement), consommation, arriéré d'entretien. L'argent de l'ERP s'y joint par les caches.",
    window: "Fenêtre",
    days: "{{count}} jours",
    since: "depuis le {{day}}",
    openReports: "Rapports",
    footer:
      "Calculé le {{when}} — rien n'est stocké ; l'écran des rapports donne les mêmes lignes en CSV.",
    kpi: {
      completed: "Livraisons terminées",
      onTime: "À l'heure",
      onTimeHint: "terminées à l'heure prévue ou avant",
      l100: "L/100 km",
      litres: "{{litres}} L sur les km des trajets",
      costPerKm: "Coût au km",
      costHint: "TCO {{from}} → {{to}}",
      backlog: "Ordres de travail ouverts",
      backlogHint: "{{count}} de plus de 30 jours",
    },
    charts: {
      onTime: {
        title: "Livraisons à l'heure par jour",
        onTime: "à l'heure",
        late: "en retard",
      },
      cost: {
        title: "Coût au km par véhicule (unités majeures)",
        fuel: "carburant",
        maintenance: "entretien",
        depreciation: "amortissement",
        other: "autre",
        empty:
          "Aucun véhicule avec des km de trajet dans la fenêtre — les relevés de compteur des chauffeurs au départ et à la clôture d'une course donnent les km.",
      },
      fuel: {
        title: "Carburant L/100 km par semaine",
        l100: "L/100 km",
        litres: "litres",
        empty: "Aucun rapport carburant rapproché dans la fenêtre.",
      },
      backlog: {
        title: "Arriéré d'entretien par ancienneté",
        days: "jours",
        oldest: "Le plus ancien : {{title}} ({{days}} jours)",
        open: "ouvrir le tableau",
      },
    },
    reports: {
      title: "Rapports",
      hint: "Les jeux de données derrière les tableaux de bord, pour la fenêtre choisie, en table et en CSV — les mêmes chiffres qu'Ask OneFleet lit par les outils d'analyse.",
      openDashboards: "Tableaux de bord",
      rows: "{{count}} ligne(s)",
      csv: "Télécharger le CSV",
      downloading: "Préparation…",
      saved: "Enregistré {{name}}",
      empty: "Aucune ligne dans cette fenêtre.",
      truncated:
        "Les 200 premières lignes sont affichées ; le CSV les porte toutes.",
      axis: "Dimension ERP",
      axes: { cost_center: "Centre de coûts", project: "Projet" },
      names: {
        on_time_by_day: "Livraisons à l'heure par jour",
        cost_per_km_by_vehicle: "Coût au km par véhicule",
        fuel_trend_by_week: "Carburant L/100 km par semaine",
        maintenance_backlog: "Arriéré d'entretien par ancienneté",
        driver_scorecards: "Scores des chauffeurs",
        fuel_exceptions: "Exceptions carburant (audit)",
        nbv_vs_market: "Valeur nette comptable vs marché",
        cost_by_dimension: "Coût de flotte par dimension ERP",
      },
      descriptions: {
        on_time_by_day:
          "Commandes terminées par jour : à l'heure (à l'heure prévue ou avant) et en retard",
        cost_per_km_by_vehicle:
          "La fenêtre TCO par véhicule — carburant, entretien, amortissement, autre — sur les km des trajets",
        fuel_trend_by_week:
          "Les litres de la flotte et les km des trajets par semaine ISO, la consommation qu'ils impliquent",
        maintenance_backlog:
          "Les ordres de travail ouverts avec leur ancienneté depuis la création, par tranche 0-7 / 8-30 / 31-90 / 90+",
        driver_scorecards:
          "Le dernier mois calculé par chauffeur — le score, sa bande et les mesures derrière",
        fuel_exceptions:
          "Chaque constat de règle sur un rapport carburant de la fenêtre avec sa décision — la liste de l'auditeur",
        nbv_vs_market:
          "La valeur nette comptable ERP de chaque véhicule / actif financé face à la valeur de marché gardée sur la fiche",
        cost_by_dimension:
          "La fenêtre TCO sommée par le centre de coûts ou le projet des véhicules — la dimension ERP en axe",
      },
    },
    builder: {
      toggle: "Constructeur",
      columns: "Colonnes",
      filters: "Filtres (tous doivent être vrais)",
      addFilter: "filtre",
      groupBy: "Grouper par",
      aggregate: "Agrégat",
      measure: "Mesure",
      sort: "Tri",
      reset: "Réinitialiser",
    },
    schedules: {
      title: "Rapports planifiés",
      hint: "Un rapport avec sa fenêtre (et sa requête du constructeur quand elle est enregistrée), quotidien ou hebdomadaire, déposé en CSV et envoyé dans la boîte d'un rôle par le worker à 06:30.",
      report: "Rapport",
      cadence: "Cadence",
      daily: "quotidien",
      weekly: "hebdomadaire le",
      weekday: "Jour",
      role: "Rôle",
      add: "Ajouter",
      runNow: "Lancer maintenant",
      ran: "{{count}} rapport(s) déposé(s)",
      lastRun: "dernier lancement {{when}}",
      lastFile: "dernier CSV",
      enable: "Activer",
      disable: "Désactiver",
      delete: "Supprimer",
      empty: "Aucun rapport planifié.",
    },
  },
  alerts: {
    title: "Centre d'alertes",
    hint: "Chaque alerte levée par les règles — documents, heures de service, boîtiers, entretiens et inspections dus, zones — par gravité et état, avec qui l'a prise en compte et ce qui l'a résolue. Une alerte connue est remontée, jamais dupliquée ; le moniteur tourne chaque jour à 06:00 et à la demande ici.",
    scanResult: "{{total}} constat(s), {{high}} élevé(s)",
    openTotal: "{{count}} ouverte(s)",
    lastRaised: "dernière levée {{when}}",
    allKinds: "Tous les types",
    total: "{{count}} alerte(s)",
    empty: "Aucune alerte dans cette vue.",
    times: "vue {{count}} fois",
    severity: { high: "élevée", medium: "moyenne", low: "faible" },
    status: {
      active: "ouvertes + prises en compte",
      open: "ouverte",
      acknowledged: "prise en compte",
      resolved: "résolue",
      all: "toutes",
    },
    col: {
      severity: "Gravité",
      alert: "Alerte",
      subject: "Sujet",
      seen: "Vue le",
      status: "État",
      kind: "Type",
    },
    rules: {
      title: "Règles",
      hint: "Ce que le moniteur de conformité vérifie et quand il lève — règles seules, sans modèle (D-06) ; les seuils et les interrupteurs sont ceux du tenant (un administrateur les règle ici).",
      hintEdit:
        "Ce que le moniteur de conformité vérifie et quand il lève — règles seules, sans modèle (D-06). Désactivez un type, forcez sa gravité, coupez sa notification, déplacez un seuil ; un seuil vide revient à la valeur par défaut. La prochaine exécution suit.",
      col: {
        rule: "Règle",
        raises: "Lève",
        threshold: "Seuils",
        tells: "Prévient",
      },
      names: {
        documents: "Documents",
        hos: "Heures de service",
        devices: "Boîtiers télématiques",
        maintenance: "Entretiens dus",
        inspections: "Inspections dues",
        zones: "Zones et itinéraires",
        deliveries: "Livraisons",
      },
      thresholds: {
        documents:
          "expiré = élevée ; expire dans la fenêtre « bientôt » = moyenne ; le reste de l'horizon = faible",
        hos: "une limite de conduite ou de service dépassée = élevée ; une pause due = moyenne",
        devices:
          "aucune position pendant la fenêtre de silence = moyenne, élevée passé le seuil haut ; VIN discordant = élevée ; un code défaut = faible",
        maintenance:
          "un entretien en retard au km ou à la date = élevée (moyenne avec un ordre de travail) ; bientôt dû = faible",
        inspections:
          "un formulaire dû sur un véhicule = faible ; à nouveau passé sa fréquence = moyenne ; jamais rempli ou passé le facteur haut = élevée",
        zones:
          "une zone interdite franchie = élevée (une fois par zone et par jour) ; excès de vitesse dans une zone de vitesse = moyenne, élevée à partir du nombre du jour ; un stationnement passé la règle de la zone = faible (moyenne si la zone demande une alerte) ; un camion hors de l'itinéraire prévu au-delà de la distance et du délai de grâce = élevée (une fois par écart)",
        deliveries:
          "un comptage à la porte qui révèle un manque = élevée (une fois par comptage), avec les lignes manquantes, le camion et le chauffeur",
      },
      thresholdNames: {
        document_soon_days: "bientôt sous",
        document_horizon_days: "horizon",
        device_silent_high_h: "silence → élevée après",
        inspection_high_factor: "élevée à",
        speeding_high_count: "élevée dès",
      },
      enabled: "actif",
      severity: "gravité",
      severityRule: "gravité de la règle",
      notify: "prévenir",
      tenantValue: "réglé pour ce tenant (défaut {{def}})",
      footer:
        "La carte Conformité des écrans chauffeurs lance le même moniteur avec les mêmes seuils ; l'exécution quotidienne de 06:00 aussi.",
    },
    custom: {
      heading: "Règles personnalisées sur les événements",
      hint: "Quand cet événement survient et que ces champs ressemblent à cela, lever une alerte de cette gravité avec ce titre — et prévenir ce rôle. Le relais les applique à chaque événement né dans OneFleet.",
      new: "Nouvelle règle",
      close: "Fermer",
      name: "Nom",
      event: "Événement (un type ou un motif)",
      when: "Conditions (toutes doivent être vraies)",
      addCondition: "condition",
      value: "valeur",
      title: "Titre de l'alerte ({espaces} lus dans l'événement)",
      message: "Message (facultatif)",
      notify: "Prévenir le rôle",
      nobody: "personne",
      oncePer: "Une alerte par",
      once: {
        event: "événement",
        subject: "enregistrement (comptée à chaque répétition)",
        day: "enregistrement et jour",
      },
      sample: "Charge utile d'essai (JSON)",
      try: "Essayer",
      wouldRaise: "Lèverait",
      noMatch: "Aucune correspondance sur cet essai",
      badJson: "L'essai n'est pas du JSON valide",
      save: "Enregistrer la règle",
      enable: "Activer",
      disable: "Désactiver",
      delete: "Supprimer",
      confirmDelete: "Supprimer la règle « {{name}} » ?",
      empty: "Aucune règle personnalisée.",
    },
  },
  compliance: {
    title: "Moniteur de conformité",
    hint: "Règles seulement — documents qui expirent, temps de conduite, boîtiers silencieux ou VIN différent, entretiens dus ; l'exécution lève les alertes (une alerte connue est mise à jour, jamais dupliquée) et notifie les rôles concernés à partir de modèles ; le worker l'exécute chaque jour à 06:00",
    scan: "Analyser",
    run: "Exécuter le moniteur",
    raised: "{{new}} nouvelle(s) alerte(s), {{sent}} notification(s)",
    noNotify: "pas de notification",
    alerts: "Alertes ouvertes",
    noAlert: "Aucune alerte ouverte.",
    acknowledged: "prise en compte",
    since: "depuis {{when}}",
    ack: "Prendre en compte",
    resolve: "Résoudre",
    resolutionPrompt: "Qu'est-ce qui l'a résolue ? (facultatif)",
    notifications: "Mes notifications",
    unread: "{{count}} non lue(s)",
    noNotification: "Rien pour vous pour l'instant.",
    markRead: "marquer lue",
    open: "ouvrir",
    kind: {
      document_expired: "document expiré",
      document_expiring: "document bientôt expiré",
      hos_violation: "temps de conduite dépassé",
      hos_break_due: "pause due",
      device_no_signal: "boîtier silencieux",
      device_vin_mismatch: "VIN différent",
      device_dtc: "code défaut",
      maintenance_overdue: "entretien en retard",
      maintenance_due_soon: "entretien bientôt dû",
      inspection_overdue: "inspection due",
      no_go_entered: "zone interdite franchie",
      speeding: "excès de vitesse",
      dwell_exceeded: "stationnement dépassé",
      off_route: "hors itinéraire",
      items_missing: "articles manquants à la livraison",
    },
  },
  bell: {
    title: "Notifications",
    unread: "{{count}} non lue(s)",
    allRead: "tout est lu",
    empty: "Rien pour vous pour l'instant.",
    unavailable: "OneFleet ne répond pas.",
    all: "Toutes les notifications et alertes",
    kind: {
      document_expired: "document expiré",
      document_expiring: "document bientôt expiré",
      hos_violation: "temps de conduite dépassé",
      hos_break_due: "pause due",
      device_no_signal: "boîtier silencieux",
      device_vin_mismatch: "VIN différent",
      device_dtc: "code défaut",
      maintenance_overdue: "entretien en retard",
      maintenance_due_soon: "entretien bientôt dû",
      inspection_overdue: "inspection due",
      no_go_entered: "zone interdite franchie",
      speeding: "excès de vitesse",
      dwell_exceeded: "stationnement dépassé",
      off_route: "hors itinéraire",
      items_missing: "articles manquants à la livraison",
    },
  },
  routeAdvisor: {
    title: "Conseiller tournées",
    hint: "Le plan lu avec vos droits — la journée livrée une commande à la fois contre le plan, la charge de chaque tournée, les commandes laissées de côté, ce qu'il faut vérifier avant d'appliquer ; l'application est une proposition qu'un second dispatcheur décide",
    run: "Interroger le conseiller",
    propose: "Déposer la proposition d'application",
    before: "Un trajet par commande",
    beforeHint: "{{count}} commande(s), à vol d'oiseau (borne basse)",
    beforeHintRoad:
      "{{count}} commande(s), une course chacune, distance routière",
    after: "Le plan",
    afterHint:
      "{{routes}} tournée(s) · {{orders}} commande(s) · {{unassigned}} non planifiée(s)",
    saving: "Économie",
    utilisation: "remplissage {{pct}} %",
    applyProposal: "appliquer (W3)",
    kind: {
      plan_failed: "plan en échec",
      plan_not_computed: "non calculé",
      fallback_plan: "plan heuristique",
      unassigned_share: "beaucoup de non planifiées",
      unassigned_order: "non planifiée",
      skipped_order: "écartée",
      vehicles_without_driver: "sans chauffeur",
      route_near_capacity: "proche de la capacité",
      route_beyond_day: "au-delà de la journée",
      idle_vehicles: "véhicules inactifs",
      single_stop_routes: "tournées à un arrêt",
      past_day: "journée passée",
      apply_ready: "prêt à appliquer",
      proposal_pending: "proposition en attente",
      applied: "appliqué",
      trip_variance: "au-delà du plan",
      trip_readings_suspect: "relevés douteux",
    },
    col: {
      vehicle: "Véhicule",
      driver: "Chauffeur",
      orders: "Commandes",
      time: "Durée",
      load: "Charge",
    },
  },
  fuelAnalyst: {
    title: "Analyste carburant",
    hint: "Anomalies ouvertes par véhicule, dérive de consommation, file de rapprochement, lots fournisseurs prêts, imputations à proposer — les règles trouvent, le modèle raconte ; chaque écriture est une proposition à double contrôle",
    run: "Interroger l'analyste",
    narrative: "Expliquer cet aperçu",
    explain: "Expliquer",
    kind: {
      anomalies: "anomalies",
      consumption_drift: "dérive",
      fuel_beyond_trips: "carburant au-delà des trajets",
      trip_consumption: "consommation sur km de trajet",
      matching_stale: "file ancienne",
      unclaimed_transactions: "lignes non réclamées",
      batch_ready: "lot prêt",
      allocation_previewed: "imputation prévisualisée",
      allocation_unallocated: "non imputé",
    },
    action: {
      "fuel.propose_batch": "facture d'achat (ERP)",
      "allocation.propose_run": "journal d'imputation (ERP)",
    },
    col: { vehicle: "Véhicule", costKm: "Coût / km", reports: "Rapports" },
  },
  printed: {
    heading: "Documents imprimés",
    intro:
      "Les papiers que la flotte pose sur un bureau : la lettre de voiture que le chauffeur emporte, l'ordre de travail sur l'établi, la fiche de remise d'un camion. Chacun est livré correctement rédigé — ne changez que ce qui doit porter vos mots.",
    readOnly: "Modifier la rédaction demande le droit sur les paramètres.",
    shipped: "Texte livré",
    yours: "Vos mots",
    edit: "Modifier",
    close: "Fermer",
    title: "Titre",
    body: "Corps (Markdown)",
    save: "Enregistrer",
    reset: "Revenir au texte livré",
    copyShipped: "Partir du texte livré",
    placeholders: "Ce que vous pouvez utiliser",
    hint: "Cliquez pour insérer. Tout le reste est refusé à l'enregistrement : un document ne doit jamais échouer pendant qu'on attend une signature.",
    blockHint:
      "Un tableau construit pour vous — placez-le, et décidez de ce qui l'entoure",
    print: "Imprimer",
    printHint: "Ouvre la page que votre navigateur imprime.",
    subject: {
      order: "Imprimé depuis une commande",
      work_order: "Imprimé depuis un ordre de travail",
      vehicle: "Imprimé depuis un véhicule",
    },
    kind: {
      "order.waybill": "Lettre de voiture",
      "work_order.job_card": "Ordre de travail",
      "vehicle.handover": "Fiche de remise",
    },
  },
  carriers: {
    title: "Une autre flotte",
    hint: "Confiez cette course à un transporteur quand aucun de nos camions ne peut la prendre.",
    price: "Demander le prix",
    quoted: "Ils annoncent {{amount}}, environ {{minutes}} minutes.",
    handOver: "Confier la course",
    note: "Ce qu'ils doivent savoir (facultatif)",
    track: "Leur page de suivi",
    takeBack: "Reprendre la course",
    takeBackHint:
      "Nous leur demandons ; la commande reste où ils l'ont laissée.",
    unmapped: "un mot que nous ne connaissons pas",
    state: {
      requested: "Demandée",
      accepted: "Acceptée",
      collected: "Enlevée",
      delivered: "Livrée",
      cancelled: "Rendue",
      failed: "Échouée",
    },
  },
  comments: {
    title: "Notes",
    count: "{{count}} note",
    count_other: "{{count}} notes",
    placeholder:
      "Que doit savoir la prochaine personne qui ouvre cette fiche ?",
    hint: "Toute personne qui peut ouvrir cette fiche les lit.",
    add: "Ajouter une note",
    empty: "Aucune note sur cette fiche.",
    someone: "Quelqu'un",
    edit: "Modifier",
    save: "Enregistrer",
    cancel: "Annuler",
    delete: "Supprimer",
    pin: "Épingler",
    unpin: "Désépingler",
    pinned: "Épinglée",
    edited: "modifiée",
    sendToCustomer: "Le client lira cette note",
    toCustomerHint: "Cette note apparaît dans le portail du client.",
    toCustomer: "envoyée au client",
    fromCustomer: "du client",
  },
  dimensions: {
    saved: "EnregistrÃ© â mais lâERP nâacceptera pas ces codes",
    fix: "Corriger le code",
    continue: "Continuer quand mÃªme",
    of: { cost_center: "Centre de coÃ»t", project: "Projet", site: "Site" },
  },
  assistant: {
    title: "Ask OneFleet",
    subtitle:
      "Lit la flotte avec vos droits — outils sur les données en direct, preuves, aucune écriture sans proposition",
    prompts: {
      q1: "Commandes en retard aujourd'hui",
      q2: "Quelles commandes sont non affectées ?",
      q3: "Qui est en ligne ?",
      q4: "Indicateurs de la semaine",
    },
  },
};
